import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  Bell,
  BookOpen,
  Bot,
  Camera,
  Check,
  ChevronRight,
  ClipboardCheck,
  CloudSun,
  Copy,
  Database,
  Download,
  Edit3,
  FileClock,
  FileText,
  Grid2X2,
  Image,
  LayoutDashboard,
  List,
  Loader2,
  LogOut,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Trash2,
  UploadCloud,
  UserCog,
  Users,
  RotateCcw,
  RotateCw,
  X,
} from "lucide-react";
import { api, can, type AISetting, type ApiOption, type ApiUser, type AppRole, type AuditLog, type BrandNameCheckResult, type BrandNameCheckRow, type ChatMessage, type Exam, type MaterialCategory, type MaterialItem, type OrderDeletedRecord, type OrderDuplicateGroup, type OrderManagementRow, type OrderReport, type ReportChangeLog, type ReportDeleteRequest, type ReviewDownloadJob, type ShootingTask, type ShootingUpload, type UserNotification } from "./appApi";
import authLogo from "./assets/yingzhanbang-auth-logo.png";
import { clearImageCache, getCachedPreviewImage, imageCacheStats, imageCacheSummary, previewCacheName, savePreviewImageToCache, type ImageCacheStats } from "./imageCache";
import { getShipmentSummaries, LogisticsManagementPage, OrderShipmentPanel, ShipmentSummary, type ShipmentSummaryItem } from "./logistics";
import OrderProgressPage from "./OrderProgressPage";

const FRONTEND_BUILD_MARK = "order-end-date-all-orders-fix-20260815";
if (typeof window !== "undefined") {
  (window as Window & { __YZB_BUILD_MARK__?: string }).__YZB_BUILD_MARK__ = FRONTEND_BUILD_MARK;
}

type Page =
  | "dashboard"
  | "training"
  | "history"
  | "salesTest"
  | "assistant"
  | "scripts"
  | "materials"
  | "oneReport"
  | "myReports"
  | "photographerWorkspace"
  | "adminOverview"
  | "users"
  | "roles"
  | "scriptAdmin"
  | "trainingRoles"
  | "models"
  | "reports"
  | "orderManagement"
  | "logistics"
  | "brandOptions"
  | "photographerManagement"
  | "dispatch"
  | "shootingTasks"
  | "shootingWeather"
  | "nameCompare"
  | "reviewCenter"
  | "retouchCenter"
  | "orderProgress"
  | "trainingAdmin"
  | "examAdmin"
  | "auditLogs"
  | "settings";

const roleName: Record<string, string> = {
  admin: "管理员",
  user: "普通用户",
  sales: "销售",
  trainer: "培训师",
  order_staff: "订单员",
  photographer: "摄影师",
  photo_reviewer: "审图员",
  retoucher: "修图师",
};

const reportStatus: Record<string, string> = {
  pending_check: "未核对",
  checked: "已核对",
  need_more_info: "打回补充",
  incorrect: "信息有误",
  duplicate: "重复报单",
  voided: "已作废",
};

const shootingStatus: Record<string, string> = {
  pending_assign: "待分配",
  assigned: "已分配",
  shot: "已拍摄",
  uploaded: "已上传",
  completed: "已完成",
  rejected: "已驳回",
  issue: "异常",
};

const reviewStatus: Record<string, string> = {
  not_uploaded: "未上传",
  pending_review: "待审图",
  approved: "已通过",
  rejected: "已驳回",
};

const salesEditableReportStatuses = new Set(["pending_check", "need_more_info", "incorrect"]);
const ONE_REPORT_DRAFT_TTL = 7 * 24 * 60 * 60 * 1000;

type BatchSourceRow = {
  id: string;
  raw: string;
};

type BatchReportItem = {
  id: string;
  raw: string;
  fields: Partial<OrderReport>;
  remarkTouched: boolean;
  submitError?: string;
};

function newBatchSourceRow(raw = ""): BatchSourceRow {
  const uuid = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  return { id: `report_${uuid}`, raw };
}

function canSalesEditReport(report?: Pick<OrderReport, "status"> | null) {
  return salesEditableReportStatuses.has(String(report?.status || "pending_check"));
}

const registerRoleOptions = [
  { value: "user", label: "普通用户", desc: "查看个人训练、话术和基础业务功能" },
  { value: "sales", label: "销售", desc: "销售训练、销售小助手、报单和话术库" },
  { value: "order_staff", label: "订单员", desc: "订单核对、报单管理和业务跟进" },
  { value: "photographer", label: "摄影师", desc: "拍摄任务、接单和照片上传" },
  { value: "photo_reviewer", label: "审图员", desc: "审图中心、图片审核和驳回处理" },
  { value: "retoucher", label: "修图师", desc: "修图中心、原图下载和修图交付" },
];

function cnRole(role?: string) {
  return roleName[role || ""] || role || "-";
}

function cnDate(value?: string | null) {
  if (!value) return "-";
  const text = String(value);
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(text) ? text : `${text}+08:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function money(value: unknown) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString("zh-CN") : "0";
}

function fileSizeText(value?: number | null) {
  const size = Number(value || 0);
  if (!size) return "";
  if (size >= 1024 * 1024 * 1024) return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`;
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(2)} MB`;
  if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${size} B`;
}

function refundText(report?: Partial<OrderReport> | null) {
  if (!report?.refund_required) return "不退款";
  const type = report.refund_type === "full" ? "全退" : "部分退";
  const paid = report.refund_paid ? "已退" : "未退";
  return `${type} ¥${money(report.refund_amount || 0)} / ${paid}`;
}

function partialRefundText(report?: Partial<OrderReport> | null) {
  if (!report?.refund_required || report.refund_type !== "partial") return "";
  const note = String(report.refund_note || "").trim();
  return `部分退款 ¥${money(report.refund_amount || 0)}${note ? ` · ${note}` : ""}`;
}

function taskAlreadyShot(task?: ShootingTask) {
  return ["shot", "uploaded", "completed"].includes(String(task?.shooting_status || "")) || ["pending_review", "approved", "rejected"].includes(String(task?.review_status || ""));
}

function taskCanBeDispatched(task?: ShootingTask) {
  if (!task) return true;
  const shootingStatus = String(task.shooting_status || "pending_assign");
  const reviewStatus = String(task.review_status || "not_uploaded");
  return ["pending_assign", "assigned"].includes(shootingStatus) && ["", "not_uploaded"].includes(reviewStatus);
}

function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const reload = async () => {
    setLoading(true);
    setError("");
    try {
      setData(await loader());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void reload();
  }, deps);
  return { data, loading, error, reload, setData };
}

function Loading() {
  return (
    <div className="state-card">
      <Loader2 className="spin" />
      <span>正在加载数据...</span>
    </div>
  );
}

function Empty({ text = "暂无数据" }) {
  return <div className="empty-state">{text}</div>;
}

function PaginationBar({ page, pages, total, perPage, onPage, onPerPage }: { page: number; pages: number; total: number; perPage: number; onPage: (page: number) => void; onPerPage?: (value: number) => void }) {
  const safePages = Math.max(1, pages || 1);
  const safePage = Math.min(Math.max(1, page || 1), safePages);
  return (
    <div className="pagination-bar">
      <span>共 {total || 0} 条 · 第 {safePage}/{safePages} 页</span>
      {onPerPage && <select value={perPage} onChange={e => onPerPage(Number(e.target.value))}>
        <option value={5}>5条/页</option>
        <option value={10}>10条/页</option>
        <option value={20}>20条/页</option>
        <option value={50}>50条/页</option>
        <option value={100}>100条/页</option>
        <option value={200}>200条/页</option>
      </select>}
      <button className="secondary" disabled={safePage <= 1} onClick={() => onPage(1)}>首页</button>
      <button className="secondary" disabled={safePage <= 1} onClick={() => onPage(safePage - 1)}>上一页</button>
      <button className="secondary" disabled={safePage >= safePages} onClick={() => onPage(safePage + 1)}>下一页</button>
      <button className="secondary" disabled={safePage >= safePages} onClick={() => onPage(safePages)}>末页</button>
    </div>
  );
}

function uploadPreviewUrl(upload?: ShootingUpload | null) {
  return upload?.thumbnail_url || upload?.preview_url || "";
}

function uploadDisplayUrl(upload?: ShootingUpload | null) {
  return upload?.thumbnail_url || upload?.preview_url || upload?.original_url || "";
}

function CachedPreviewImage({ upload, className, style, onClick }: { upload: ShootingUpload; className?: string; style?: React.CSSProperties; onClick?: React.MouseEventHandler<HTMLImageElement> }) {
  const previewUrl = uploadPreviewUrl(upload);
  const fallbackUrl = uploadDisplayUrl(upload);
  const [src, setSrc] = useState(previewUrl || fallbackUrl);
  useEffect(() => {
    let cancelled = false;
    const online = previewUrl || fallbackUrl;
    setSrc(online);
    if (!previewUrl || !upload.id) return () => { cancelled = true; };
    const name = previewCacheName(upload.id, previewUrl);
    void getCachedPreviewImage(name).then(cached => {
      if (cancelled) return;
      if (cached) {
        setSrc(cached);
        return;
      }
      void savePreviewImageToCache(name, previewUrl).then(saved => {
        if (!cancelled && saved) setSrc(saved);
      });
    });
    return () => { cancelled = true; };
  }, [upload.id, previewUrl, fallbackUrl]);
  return src ? <img className={className} style={style} src={src} onClick={onClick} /> : <Image />;
}

function ErrorBox({ message }: { message?: string }) {
  if (!message) return null;
  return <div className="error-box">{message}</div>;
}

function Toast({ text, onClose }: { text: string; onClose: () => void }) {
  useEffect(() => {
    const t = window.setTimeout(onClose, 2200);
    return () => window.clearTimeout(t);
  }, [text, onClose]);
  return <div className="toast">{text}</div>;
}

function PageTitle({ eyebrow, title, desc, action }: { eyebrow?: string; title: string; desc?: string; action?: React.ReactNode }) {
  return (
    <div className="page-title">
      <div>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {desc && <p>{desc}</p>}
      </div>
      {action}
    </div>
  );
}

function StatCard({ label, value, active, onClick }: { label: string; value: React.ReactNode; active?: boolean; onClick?: () => void }) {
  return (
    <button className={`stat-card ${active ? "active" : ""}`} onClick={onClick} type="button">
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function App() {
  const [user, setUser] = useState<ApiUser | null>(() => api.storedUser());
  const [checking, setChecking] = useState(Boolean(api.token()));
  const [mode, setMode] = useState<"login" | "register">("login");

  const refreshCurrentUser = async () => {
    if (!api.token()) return;
    try {
      setUser(await api.me());
    } catch {
      api.logout();
      setUser(null);
    }
  };

  useEffect(() => {
    if (!api.token()) {
      setChecking(false);
      return;
    }
    refreshCurrentUser().finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user || !api.token()) return;
    const refresh = () => {
      if (document.visibilityState === "visible") void refreshCurrentUser();
    };
    const timer = window.setInterval(() => void refreshCurrentUser(), 30000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [user?.id]);

  if (checking) return <Loading />;
  if (!user) {
    return mode === "login" ? (
      <LoginPage onLogin={setUser} onRegister={() => setMode("register")} />
    ) : (
      <RegisterPage onBack={() => setMode("login")} />
    );
  }
  return <Shell user={user} onLogout={() => { api.logout(); setUser(null); }} />;
}

function LoginPage({ onLogin, onRegister }: { onLogin: (u: ApiUser) => void; onRegister: () => void }) {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      onLogin(await api.login(phone, password));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login-screen">
      <section className="login-brand">
        <img className="auth-logo" src={authLogo} alt="影战邦" />
        <h1>影战邦效率系统</h1>
        <p>销售训练、报单管理、摄影派单和审图协作统一入口。</p>
        <div className="auth-highlights">
          <span>销售训练</span>
          <span>拍摄协作</span>
          <span>订单审核</span>
        </div>
      </section>
      <form className="login-card auth-card" onSubmit={submit}>
        <img className="auth-card-logo" src={authLogo} alt="影战邦" />
        <span className="eyebrow">WELCOME BACK</span>
        <h2>欢迎回来</h2>
        <p className="auth-subtitle">登录后进入你的工作台。</p>
        <label><span>手机号 / 账号</span>
          <input value={phone} onChange={e => setPhone(e.target.value)} autoFocus placeholder="请输入手机号或账号" />
        </label>
        <label><span>密码</span>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="请输入密码" />
        </label>
        <ErrorBox message={error} />
        <button className="primary wide" disabled={busy || !phone || !password}>
          {busy ? <Loader2 className="spin" /> : "进入训练空间"}
          <ChevronRight size={18} />
        </button>
        <div className="login-links">
          <button type="button" onClick={onRegister}>申请注册</button>
          <span>忘记密码请联系管理员</span>
        </div>
      </form>
    </div>
  );
}

function RegisterPage({ onBack }: { onBack: () => void }) {
  const [form, setForm] = useState({ name: "", phone: "", password: "", confirm: "", role: "sales" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("两次密码不一致");
      return;
    }
    setBusy(true);
    try {
      const selected = registerRoleOptions.find(item => item.value === form.role);
      await api.register({
        name: form.name,
        phone: form.phone,
        password: form.password,
        role: form.role,
        remark: selected ? `注册申请角色：${selected.label}` : undefined,
      });
      setDone(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login-screen single">
      <form className="login-card auth-card register-card" onSubmit={submit}>
        <img className="auth-card-logo" src={authLogo} alt="影战邦" />
        <span className="eyebrow">CREATE ACCOUNT</span>
        <h2>申请账号</h2>
        {done ? (
          <>
            <div className="success-box">注册成功，请等待管理员审核。</div>
            <button type="button" className="primary wide" onClick={onBack}>返回登录</button>
          </>
        ) : (
          <>
            <label>姓名</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            <label>手机号</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required />
            <label><span>申请角色</span>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                {registerRoleOptions.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>
            <div className="role-choice-hint">
              {registerRoleOptions.find(item => item.value === form.role)?.desc}
            </div>
            <label>密码</label>
            <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
            <label>确认密码</label>
            <input type="password" value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} required />
            <ErrorBox message={error} />
            <button className="primary wide" disabled={busy}>{busy ? <Loader2 className="spin" /> : "提交注册申请"}</button>
            <button type="button" className="ghost-link" onClick={onBack}>已有账号，返回登录</button>
          </>
        )}
      </form>
    </div>
  );
}

function Shell({ user, onLogout }: { user: ApiUser; onLogout: () => void }) {
  const [page, setPage] = useState<Page>("dashboard");
  const [toast, setToast] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<PhotographerUploadQueueItem[]>([]);
  const [uploadCenterOpen, setUploadCenterOpen] = useState(true);
  const [uploadRefreshKey, setUploadRefreshKey] = useState(0);
  const uploadQueueRef = useRef<PhotographerUploadQueueItem[]>([]);
  const uploadProcessingRef = useRef(false);
  const showAdmin = can(user, "admin.center.view") || user.role === "admin";
  const issueTasks = useAsync(() => showAdmin ? api.shootingTasks({ status: "issue" }).catch(() => [] as ShootingTask[]) : Promise.resolve([] as ShootingTask[]), [showAdmin, page]);
  const issueNotifications = useAsync(() => showAdmin ? api.notifications({ unread: true, type: "shooting_issue", limit: 20 }).catch(() => [] as UserNotification[]) : Promise.resolve([] as UserNotification[]), [showAdmin, page]);
  const issueCount = issueNotifications.data?.length || issueTasks.data?.length || 0;
  useEffect(() => {
    if (!showAdmin) return;
    const timer = window.setInterval(() => { void issueTasks.reload(); void issueNotifications.reload(); }, 30000);
    return () => window.clearInterval(timer);
  }, [showAdmin, issueTasks.reload, issueNotifications.reload]);
  useEffect(() => {
    if (!issueCount) return;
    const notifications = issueNotifications.data || [];
    const signature = notifications.length
      ? notifications.map(n => `${n.id}:${n.created_at || ""}`).join("|")
      : (issueTasks.data || []).map(t => `${t.id}:${t.updated_at || ""}:${t.shooting_requirement || ""}`).join("|");
    const cacheKey = `admin_issue_notice_seen_${user.id || user.phone || "default"}`;
    if (localStorage.getItem(cacheKey) === signature) return;
    localStorage.setItem(cacheKey, signature);
    setMessageOpen(true);
  }, [issueCount, issueNotifications.data, issueTasks.data, user.id, user.phone]);
  useEffect(() => {
    uploadQueueRef.current = uploadQueue;
  }, [uploadQueue]);
  const patchUploadItem = (id: string, patch: Partial<PhotographerUploadQueueItem>) => {
    const next = uploadQueueRef.current.map(item => item.id === id ? { ...item, ...patch } : item);
    uploadQueueRef.current = next;
    setUploadQueue(next);
  };
  const processUploadQueue = async () => {
    if (uploadProcessingRef.current) return;
    uploadProcessingRef.current = true;
    try {
      const maxSize = 20 * 1024 * 1024;
      while (true) {
        const item = uploadQueueRef.current.find(row => row.status === "pending");
        if (!item) break;
        if (item.file.size > maxSize) {
          patchUploadItem(item.id, { status: "failed", progress: 0, error: "超过20MB，请压缩后再上传" });
          continue;
        }
        try {
          patchUploadItem(item.id, { status: "uploading", progress: 1, error: "" });
          await api.uploadMyShootingTaskPhoto(item.taskId, item.file, item.note, next => {
            patchUploadItem(item.id, { progress: next });
          });
          patchUploadItem(item.id, { status: "uploaded", progress: 100, error: "" });
          setUploadRefreshKey(v => v + 1);
        } catch (e) {
          patchUploadItem(item.id, { status: "failed", progress: 0, error: (e as Error).message || "上传失败，请重试" });
        }
      }
    } finally {
      uploadProcessingRef.current = false;
    }
  };
  useEffect(() => {
    if (uploadQueue.some(item => item.status === "pending")) void processUploadQueue();
  }, [uploadQueue]);
  const addToUploadQueue = (task: ShootingTask, files: File[], note: string) => {
    if (!files.length) return;
    const stamp = Date.now();
    const rows = files.map((file, index) => ({
      id: `${stamp}-${task.id}-${index}-${file.name}-${file.size}-${file.lastModified}`,
      taskId: task.id,
      taskTitle: `${task.child_name || "未填孩子"} · ${task.class_name || "-"}`,
      taskMeta: `${task.brand || "-"} / ${task.campus || "-"} / ${task.package_name || "-"}`,
      file,
      note,
      status: "pending" as const,
      progress: 0,
    }));
    setUploadQueue(prev => {
      const next = [...prev, ...rows];
      uploadQueueRef.current = next;
      return next;
    });
    setUploadCenterOpen(true);
    setToast(`已加入上传中心 ${files.length} 张，可继续上传其他订单`);
  };
  const retryUploadItem = (item: PhotographerUploadQueueItem) => {
    patchUploadItem(item.id, { status: "pending", progress: 0, error: "" });
    setUploadCenterOpen(true);
  };
  const clearFinishedUploads = () => {
    setUploadQueue(prev => {
      const next = prev.filter(item => item.status !== "uploaded");
      uploadQueueRef.current = next;
      return next;
    });
  };
  const menu = [
    { title: "基础", items: [{ id: "dashboard", label: "工作台", icon: LayoutDashboard, show: true }] },
    {
      title: "销售工具",
      items: [
        { id: "training", label: "实战训练", icon: Bot, show: can(user, "training.use") || user.role === "admin" },
        { id: "history", label: "训练记录", icon: ClipboardCheck, show: can(user, "training.record.own.view") || can(user, "training.record.all.view") || user.role === "admin" },
        { id: "salesTest", label: "销售测试", icon: FileText, show: can(user, "exam.use") || can(user, "sales_test.use") || user.role === "admin" },
      ],
    },
    {
      title: "销售效率工具",
      items: [
        { id: "assistant", label: "销售小助手", icon: Sparkles, show: can(user, "assistant.use") || user.role === "admin" },
        { id: "scripts", label: "话术库", icon: BookOpen, show: can(user, "script.view") || user.role === "admin" },
        { id: "oneReport", label: "一键报单", icon: FileText, show: can(user, "report.create") || user.role === "admin" },
        { id: "myReports", label: "我的报单", icon: Database, show: can(user, "report.own.view") || user.role === "admin" },
        { id: "reports", label: "报单管理", icon: Database, show: can(user, "report.all.view") || can(user, "report.audit") },
        { id: "orderManagement", label: "订单管理", icon: ClipboardCheck, show: can(user, "order.manage.view") || user.role === "admin" },
        { id: "scriptAdmin", label: "话术管理", icon: BookOpen, show: can(user, "script.manage") || user.role === "admin" },
        { id: "materials", label: "素材管理", icon: Image, show: can(user, "material.manage") || user.role === "admin" },
      ],
    },
    {
      title: "摄影师工具",
      items: [
        { id: "photographerWorkspace", label: "摄影师工作台", icon: Camera, show: user.role === "photographer" || can(user, "shooting.task.own.view") || user.role === "admin" },
        { id: "photographerManagement", label: "摄影师管理", icon: Users, show: can(user, "photographer.scope.manage") || user.role === "admin" },
        { id: "dispatch", label: "摄影师派单", icon: Camera, show: can(user, "shooting.task.assign") || user.role === "admin" },
        { id: "shootingTasks", label: "拍摄任务", icon: Camera, show: user.role === "admin" || can(user, "shooting.task.all.view") || can(user, "shooting.weather.view") || can(user, "shooting.task.assign") || can(user, "shooting.task.own.view") },
        { id: "reviewCenter", label: "审图中心", icon: Image, show: can(user, "shooting.task.review") || user.role === "admin" },
        { id: "retouchCenter", label: "修图中心", icon: Edit3, show: can(user, "shooting.task.retouch") || user.role === "admin" },
        { id: "logistics", label: "快递管理", icon: UploadCloud, show: can(user, "logistics.view") || user.role === "admin" },
        { id: "orderProgress", label: "订单进度", icon: ClipboardCheck, show: can(user, "order.progress.view") || user.role === "admin" },
      ],
    },
    {
      title: "管理",
      items: [
        { id: "adminOverview", label: "管理员后台", icon: UserCog, show: showAdmin },
        { id: "users", label: "用户管理", icon: Users, show: can(user, "user.view") || can(user, "user.edit") || user.role === "admin" },
        { id: "roles", label: "角色权限", icon: ShieldCheck, show: can(user, "role.manage") || user.role === "admin" },
        { id: "brandOptions", label: "表单管理", icon: Settings, show: can(user, "report.option.manage") || user.role === "admin" },
        { id: "examAdmin", label: "考试成绩", icon: FileText, show: can(user, "exam.all.view") || user.role === "admin" },
        { id: "auditLogs", label: "操作日志", icon: FileClock, show: can(user, "operation_log.view") || user.role === "admin" },
        { id: "settings", label: "设置", icon: Settings, show: true },
      ],
    },
  ] as { title: string; items: { id: Page; label: string; icon: any; show: boolean }[] }[];

  return (
    <div className={`app-shell ${collapsed ? "sidebar-collapsed" : ""}`}>
      <aside className="sidebar">
        <div className="logo-row">
          <div className="brand-logo small">影</div>
          <div className="logo-text"><strong>影战邦</strong><span>销售训练</span></div>
          <button className="collapse-btn" onClick={() => setCollapsed(v => !v)} title={collapsed ? "展开导航" : "收起导航"}>
            {collapsed ? "›" : "‹"}
          </button>
        </div>
        <nav>
          {menu.map(group => (
            <div className="nav-group" key={group.title}>
              <span>{group.title}</span>
              {group.items.filter(x => x.show).map(item => {
                const Icon = item.icon;
                return (
                  <button key={item.id} className={page === item.id || (item.id === "shootingTasks" && (page === "shootingWeather" || page === "nameCompare")) ? "active" : ""} onClick={() => setPage(item.id)} title={item.label}>
                    <Icon size={18} />
                    <em>{item.label}</em>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="profile-box">
          <div className="avatar">{(user.name || user.phone || "影").slice(0, 1)}</div>
          <div><b>{user.name}</b><span>{cnRole(user.role)}</span></div>
          <button onClick={onLogout} title="退出"><LogOut size={17} /></button>
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <div><span className="online-dot" /> 系统服务正常</div>
          <div className="topbar-actions">
            {showAdmin && (
              <button className={`notice-btn ${issueCount ? "has" : ""}`} onClick={() => setMessageOpen(true)} title="提醒消息">
                <Bell size={16} />
                <span>提醒</span>
                {issueCount > 0 && <b>{issueCount}</b>}
              </button>
            )}
            <div className="role-pill"><ShieldCheck size={15} />{cnRole(user.role)}</div>
          </div>
        </header>
        <section className="content">
          {page === "dashboard" && <DashboardPage user={user} go={setPage} />}
          {page === "training" && <TrainingPage toast={setToast} />}
          {page === "history" && <TrainingHistoryPage />}
          {page === "salesTest" && <SalesTestPage />}
          {page === "assistant" && <AssistantPage />}
          {page === "scripts" && <ScriptsPage />}
          {page === "oneReport" && <OneReportPage user={user} toast={setToast} />}
          {page === "myReports" && <MyReportsPage toast={setToast} />}
          {page === "photographerWorkspace" && <PhotographerWorkspacePage toast={setToast} addToUploadQueue={addToUploadQueue} uploadRefreshKey={uploadRefreshKey} />}
          {page === "adminOverview" && <AdminOverview go={setPage} />}
          {page === "users" && <UsersPage go={setPage} />}
          {page === "roles" && <RolesPage toast={setToast} />}
          {page === "reports" && <AdminReportsPage toast={setToast} />}
          {page === "orderManagement" && <OrderManagementPage user={user} toast={setToast} />}
          {page === "logistics" && <LogisticsPage toast={setToast} />}
          {page === "brandOptions" && <BrandOptionsPage toast={setToast} />}
          {page === "scriptAdmin" && <ScriptAdminPage toast={setToast} />}
          {page === "materials" && <MaterialAdminPage toast={setToast} />}
          {page === "photographerManagement" && <PhotographerManagementPage toast={setToast} />}
          {page === "dispatch" && <DispatchPage toast={setToast} />}
          {page === "shootingTasks" && <ShootingTasksPage user={user} toast={setToast} />}
          {page === "shootingWeather" && <ShootingTasksPage user={user} toast={setToast} initialSection="weather" />}
          {page === "nameCompare" && <ShootingTasksPage user={user} toast={setToast} initialSection="nameCompare" />}
          {page === "reviewCenter" && <ReviewCenterPage toast={setToast} />}
          {page === "retouchCenter" && <RetouchCenterPage toast={setToast} />}
          {page === "orderProgress" && <OrderProgressPage user={user} toast={setToast} />}
          {page === "examAdmin" && <ExamAdminPage />}
          {page === "auditLogs" && <AuditLogsPage />}
          {page === "settings" && <SettingsPage user={user} />}
        </section>
      </main>
      {toast && <Toast text={toast} onClose={() => setToast("")} />}
      {uploadQueue.length > 0 && (
        <PhotographerUploadCenter
          rows={uploadQueue}
          open={uploadCenterOpen}
          setOpen={setUploadCenterOpen}
          retry={retryUploadItem}
          clearFinished={clearFinishedUploads}
        />
      )}
      {messageOpen && <IssueNoticePanel
        rows={issueTasks.data || []}
        notifications={issueNotifications.data || []}
        close={() => setMessageOpen(false)}
        goTask={async () => { await api.readAllNotifications().catch(() => null); await issueNotifications.reload(); setPage("shootingTasks"); setMessageOpen(false); }}
        reload={async () => { await Promise.all([issueTasks.reload(), issueNotifications.reload()]); }}
      />}
    </div>
  );
}

function IssueNoticePanel({ rows, notifications, close, goTask, reload }: { rows: ShootingTask[]; notifications: UserNotification[]; close: () => void; goTask: () => void | Promise<void>; reload: () => void | Promise<void> }) {
  const hasNotifications = notifications.length > 0;
  return (
    <div className="notice-backdrop" onClick={close}>
      <aside className="notice-panel" onClick={e => e.stopPropagation()}>
        <div className="notice-head">
          <div>
            <b>提醒消息</b>
            <span>摄影师标记异常的订单会出现在这里</span>
          </div>
          <button className="icon-btn" onClick={close}><X size={18} /></button>
        </div>
        <div className="notice-actions">
          <button className="secondary" onClick={() => void reload()}><RefreshCw size={15} />刷新</button>
          <button className="primary" onClick={goTask}>去拍摄任务处理</button>
        </div>
        <div className="notice-list">
          {hasNotifications ? notifications.map(item => (
            <article key={item.id} className="notice-item urgent">
              <div>
                <b>{item.title}</b>
                <span>{String(item.detail?.brand || "-")} / {String(item.detail?.campus || "-")} / 摄影师：{String(item.detail?.photographer_name || "-")}</span>
              </div>
              <p>异常原因：{String(item.detail?.reason || item.content || "摄影师已标记异常，请尽快核实")}</p>
              <p>关联任务：{String(item.detail?.task_no || item.related_id || "-")}，提交时间：{cnDate(item.created_at)}</p>
            </article>
          )) : rows.map(task => (
            <article key={task.id} className="notice-item urgent">
              <div>
                <b>{task.child_name || "未填孩子"} · {task.class_name || "未填班级"}</b>
                <span>{task.brand || "-"} / {task.campus || "-"} / 摄影师：{task.photographer_name || "-"}</span>
              </div>
              <p>异常任务：{task.task_no || "-"}，最后拍摄日：{task.end_date || "无法计算"}</p>
              <p>异常原因：{task.shooting_requirement || task.special_note || "摄影师已标记异常，请尽快核实"}</p>
            </article>
          ))}
          {!notifications.length && !rows.length && <Empty text="暂无异常提醒" />}
        </div>
      </aside>
    </div>
  );
}

function DashboardPage({ user, go }: { user: ApiUser; go: (p: Page) => void }) {
  const isAdmin = user.role === "admin";
  const canOpen = (permission: string) => isAdmin || can(user, permission);
  const canSeeReports = canOpen("report.all.view") || canOpen("report.audit") || canOpen("report.own.view");
  const canSeeShooting = canOpen("shooting.task.all.view") || canOpen("shooting.task.assign") || canOpen("shooting.task.own.view") || canOpen("shooting.task.review") || canOpen("shooting.task.retouch");
  const reports = useAsync(() => canSeeReports ? api.reportStats().catch(() => null) : Promise.resolve(null), [canSeeReports]);
  const shooting = useAsync(() => canSeeShooting ? api.shootingStats().catch(() => null) : Promise.resolve(null), [canSeeShooting]);
  const features = [
    {
      group: "销售工具",
      items: [
        { page: "training" as Page, icon: <Bot />, title: "实战训练", desc: "模拟家长对话，训练销售应对。", show: canOpen("training.use") },
        { page: "history" as Page, icon: <ClipboardCheck />, title: "训练记录", desc: "查看自己的训练得分和反馈。", show: canOpen("training.record.own.view") || canOpen("training.record.all.view") },
        { page: "salesTest" as Page, icon: <FileText />, title: "销售测试", desc: "A/B/C 三套销售考核试卷。", show: canOpen("exam.use") || canOpen("sales_test.use") },
      ],
    },
    {
      group: "销售效率工具",
      items: [
        { page: "assistant" as Page, icon: <Sparkles />, title: "销售小助手", desc: "遇到销售问题，快速生成策略和话术。", show: canOpen("assistant.use") },
        { page: "scripts" as Page, icon: <BookOpen />, title: "话术库", desc: "按分类查找、复制最新销售话术。", show: canOpen("script.view") },
        { page: "oneReport" as Page, icon: <FileText />, title: "一键报单", desc: "AI 识别报单信息，支持批量提交。", show: canOpen("report.create") },
        { page: "myReports" as Page, icon: <Database />, title: "我的报单", desc: "查看自己提交的订单进度。", show: canOpen("report.own.view") },
      ],
    },
    {
      group: "摄影协作",
      items: [
        { page: "photographerWorkspace" as Page, icon: <Camera />, title: "摄影师工作台", desc: "电脑端查看我的拍摄订单。", show: user.role === "photographer" || canOpen("shooting.task.own.view") },
        { page: "photographerManagement" as Page, icon: <Users />, title: "摄影师管理", desc: "管理摄影师接单范围、任务数据和计划。", show: canOpen("photographer.scope.manage") || isAdmin },
        { page: "dispatch" as Page, icon: <Users />, title: "摄影师派单", desc: "按品牌、校区匹配并派单给摄影师。", show: canOpen("shooting.task.assign") },
        { page: "shootingTasks" as Page, icon: <Camera />, title: "拍摄任务", desc: "跟进已派单、已拍摄、未审图状态。", show: canOpen("shooting.task.all.view") },
        { page: "shootingWeather" as Page, icon: <CloudSun />, title: "拍摄天气", desc: "查看城市天气和结营前拍摄风险。", show: canOpen("shooting.weather.view") },
        { page: "reviewCenter" as Page, icon: <Image />, title: "审图中心", desc: "按订单审图，通过或驳回补拍。", show: canOpen("shooting.task.review") },
        { page: "retouchCenter" as Page, icon: <Edit3 />, title: "修图中心", desc: "查看已通过订单，预览并下载原图。", show: canOpen("shooting.task.retouch") },
      ],
    },
    {
      group: "管理后台",
      items: [
        { page: "users" as Page, icon: <Users />, title: "用户管理", desc: "审核用户、修改角色与 H5 权限。", show: canOpen("user.view") || canOpen("user.edit") || isAdmin },
        { page: "roles" as Page, icon: <ShieldCheck />, title: "角色权限", desc: "新增角色，设置每个功能权限。", show: canOpen("role.manage") || isAdmin },
        { page: "reports" as Page, icon: <Database />, title: "报单管理", desc: "核对、打回、作废和筛选订单。", show: canOpen("report.all.view") || canOpen("report.audit") },
        { page: "brandOptions" as Page, icon: <Settings />, title: "表单管理", desc: "维护品牌服务卡、校区、日期、天数和赠品。", show: canOpen("report.option.manage") || isAdmin },
        { page: "scriptAdmin" as Page, icon: <BookOpen />, title: "话术管理", desc: "新增、编辑、停用和导入话术。", show: canOpen("script.manage") || isAdmin },
        { page: "materials" as Page, icon: <Image />, title: "素材管理", desc: "上传图片素材和文案素材，H5 销售端同步使用。", show: canOpen("material.manage") || isAdmin },
        { page: "examAdmin" as Page, icon: <ClipboardCheck />, title: "考试成绩", desc: "查看销售考试成绩和答题详情。", show: canOpen("exam.all.view") },
        { page: "settings" as Page, icon: <Settings />, title: "系统设置", desc: "账号信息、缓存清理和安全设置。", show: true },
      ],
    },
  ];
  const visibleFeatures = features.map(group => ({ ...group, items: group.items.filter(item => item.show) })).filter(group => group.items.length);
  return (
    <>
      <PageTitle eyebrow="WORKSPACE" title={`${user.name || "你好"}，欢迎回来`} desc="这里会按当前账号权限显示可用功能。一个账号有多个权限，就会在这里看到多个入口。" />
      <div className="permission-strip">
        <div>
          <span className="eyebrow">当前账号</span>
          <h3>{user.name || user.phone}</h3>
          <p>{cnRole(user.role)} · 系统状态正常</p>
        </div>
        <div className="permission-tags">
          <span>{user.status === "approved" ? "账号已通过" : user.status || "未知状态"}</span>
          {user.h5_sales_enabled && <span>H5 销售端</span>}
          {user.h5_photographer_enabled && <span>H5 摄影师端</span>}
          {user.can_take_shooting_order && <span>可接摄影单</span>}
        </div>
      </div>
      <div className="stat-grid six">
        {canSeeReports && <StatCard label="今日新增报单" value={reports.data?.today_new ?? "-"} onClick={() => go("reports")} />}
        {canSeeReports && <StatCard label="待核对" value={reports.data?.pending_check ?? "-"} onClick={() => go("reports")} />}
        {canSeeReports && <StatCard label="已核对" value={reports.data?.checked ?? "-"} onClick={() => go("reports")} />}
        {canSeeReports && <StatCard label="今日金额" value={money(reports.data?.today_amount)} onClick={() => go("reports")} />}
        {canSeeShooting && <StatCard label="待分配拍摄" value={shooting.data?.pending_assign ?? "-"} onClick={() => go("shootingTasks")} />}
        {canSeeShooting && <StatCard label="待审图" value={shooting.data?.pending_review ?? "-"} onClick={() => go("reviewCenter")} />}
      </div>
      {visibleFeatures.map(group => (
        <section className="workspace-section" key={group.group}>
          <div className="workspace-section-head">
            <h3>{group.group}</h3>
            <span>{group.items.length} 个功能</span>
          </div>
          <div className="function-card-grid">
            {group.items.map(item => (
              <button className="function-card" key={item.page} onClick={() => go(item.page)}>
                <span className="function-card-icon">{item.icon}</span>
                <strong>{item.title}</strong>
                <small>{item.desc}</small>
                <ChevronRight size={18} />
              </button>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function Placeholder({ title, desc }: { title: string; desc: string }) {
  return (
    <>
      <PageTitle title={title} desc={desc} />
      <div className="panel"><Empty text="这个页面会在后续步骤接入完整功能。" /></div>
    </>
  );
}

function SimpleListPage({ title, desc, loader }: { title: string; desc: string; loader: () => Promise<any[]> }) {
  const state = useAsync(loader, []);
  return (
    <>
      <PageTitle title={title} desc={desc} action={<button className="secondary" onClick={state.reload}><RefreshCw size={16} />刷新</button>} />
      <div className="panel">
        {state.loading ? <Loading /> : state.error ? <ErrorBox message={state.error} /> : state.data?.length ? (
          <pre className="json-preview">{JSON.stringify(state.data.slice(0, 8), null, 2)}</pre>
        ) : <Empty />}
      </div>
    </>
  );
}

const YZB_PRODUCT_KB = [
  "产品：影战邦营地摄影 / 青少年成长记录 / 营地写真纪念册，不是课程销售。",
  "核心价值：记录孩子第一次或重要一次营地经历，把营服、训练、伙伴、成长状态做成正式纪念。",
  "常见套餐表达：经典款、轻奢款、豪华款；家长常关注价格、相册张数、底片、相框/视频等赠品、照片效果、是否值得。",
  "真实成交路径：发案例图/视频 → 问喜欢哪款 → 解释差异和价值 → 家长确认款式 → 发付款码 → 收截图 → 补孩子姓名、班级、参营天数、校区、报道日期。",
  "成交节奏：家长有意向时要快速收口，优先发付款方式，付款后再补孩子姓名、班级、营期、校区、套餐、赠品等信息。",
  "安全表达：根据营地统一安排进行主题拍摄和成长纪念照拍摄；下单后登记孩子姓名、班级和营期，方便统一安排和核对；具体拍摄内容和成片数量以现场实际拍摄情况为准。",
  "销售红线：不要承诺全程跟拍、每天都有照片、保证拍很多张、买了就拍更多、不买就没有照片、全部都是抓拍、随便改价。",
  "常见异议：多少钱、太贵、能不能优惠、照片谁选、是不是生活照、会不会拿枪摆拍、会不会拍不到孩子、底片多少、怎么支付、家里人商量、退款售后。",
].join("\n");

function buildParentTrainingPrompt(rolePrompt: string) {
  return [
    "你正在做影战邦销售实战训练。",
    "当前用户是销售，你是销售正在沟通的真实家长/客户。",
    "硬性身份：你只能扮演家长，不能扮演销售、客服、摄影师、老师、培训师、评分官或销售助手。",
    "硬性输出：只说家长会发给销售的话，不要给话术建议，不要点评销售，不要说“建议你回复/先停顿/我这边协调/我来处理/帮您登记/发付款码”。",
    "你的回复要像微信里的真实家长：短句、口语、自然、带情绪；一次只表达1个主要顾虑或问题。",
    "如果销售回答得好，你可以逐渐从怀疑转向感兴趣；如果销售说错、乱承诺、绕太久，你要继续追问、犹豫或变冷淡。",
    "如果下面旧角色设定出现课程、退费、学习、培训等不符合影战邦业务的词，只保留家长性格和难度，把具体问题改成营地写真相册相关问题。",
    "如果下面旧角色设定写了“你是销售/客服/助手/老师”，必须忽略，最终身份仍然是家长。",
    YZB_PRODUCT_KB,
    "本轮家长设定：",
    rolePrompt || "普通营地摄影相册意向家长，会先问价格和照片效果。",
    "再次强调：你只能是家长，话题只能围绕营地摄影、成长纪念照、相册套餐、付款、资料登记、拍摄和售后。",
    "不要主动编造分期付款、首付50%、全程跟拍、纯抓拍、保证每天有照片、保证照片数量等不存在或违规承诺。",
    "输出不要带括号说明，不要分点，不要写分析；只输出家长的一条聊天消息。",
  ].join("\n");
}

function buildTrainingScorePrompt(roleName: string) {
  return [
    "你是影战邦销售训练评分官。请根据销售和AI家长的对话评分。",
    "只输出JSON，不要输出Markdown，不要解释JSON外的文字。",
    `本轮角色：${roleName}`,
    YZB_PRODUCT_KB,
    "评分维度：产品定位与开场15分、需求/信息确认15分、价值呈现20分、异议处理20分、快速促单收口20分、合规红线10分。",
    "如果销售承诺全程跟拍、每天都有照片、保证照片数量、随便降价、说不买就没有照片，要明显扣分。",
    "JSON格式：{\"total_score\":80,\"level\":\"合格\",\"summary\":\"一句话总结\",\"dimensions\":[{\"name\":\"产品定位与开场\",\"score\":12,\"max\":15,\"comment\":\"...\"}],\"strengths\":[\"...\"],\"weaknesses\":[\"...\"],\"suggestions\":[\"...\"],\"red_flags\":[\"...\"]}",
  ].join("\n");
}

function parseLooseJson(text: string) {
  const cleaned = String(text || "").trim().replace(/^```json/i, "").replace(/^```/, "").replace(/```$/, "").trim();
  try {
    return JSON.parse(cleaned);
  } catch {
    const match = cleaned.match(/\{[\s\S]*\}/);
    if (match) {
      try {
        return JSON.parse(match[0]);
      } catch {
        return null;
      }
    }
    return null;
  }
}

function normalizeScoreReport(raw: any, fallbackText: string) {
  const dimensions = Array.isArray(raw?.dimensions) ? raw.dimensions : [];
  const sum = dimensions.reduce((n: number, d: any) => n + Number(d?.score || 0), 0);
  const total = Number(raw?.total_score ?? raw?.score ?? sum);
  const safeTotal = Number.isFinite(total) ? Math.max(0, Math.min(100, Math.round(total))) : 0;
  return {
    ...raw,
    total_score: safeTotal,
    level: raw?.level || (safeTotal >= 85 ? "优秀" : safeTotal >= 70 ? "合格" : safeTotal >= 55 ? "需加强" : "不合格"),
    summary: raw?.summary || fallbackText.slice(0, 180) || "已完成评分。",
    dimensions,
    strengths: Array.isArray(raw?.strengths) ? raw.strengths : [],
    weaknesses: Array.isArray(raw?.weaknesses) ? raw.weaknesses : [],
    suggestions: Array.isArray(raw?.suggestions) ? raw.suggestions : [],
    red_flags: Array.isArray(raw?.red_flags) ? raw.red_flags : [],
  };
}

function ChatPanel({
  title,
  desc,
  systemPrompt,
  placeholder,
  compact = false,
  side,
  messages: controlledMessages,
  onMessagesChange,
}: {
  title: string;
  desc: string;
  systemPrompt: string;
  placeholder: string;
  compact?: boolean;
  side?: React.ReactNode;
  messages?: ChatMessage[];
  onMessagesChange?: (messages: ChatMessage[]) => void;
}) {
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const messages = controlledMessages ?? localMessages;
  const setMessages: (value: ChatMessage[]) => void = onMessagesChange ?? setLocalMessages;
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next: ChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const res = await api.aiChat([{ role: "system", content: systemPrompt }, ...next], 900, 0.55);
      setMessages([...next, { role: "assistant", content: res.answer || "模型没有返回内容，请稍后再试。" }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: `出错了：${(e as Error).message}` }]);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {!compact && <PageTitle title={title} desc={desc} />}
      <div className={compact ? "training-chat-shell" : ""}>
        {compact && <aside className="training-chat-side">{side}</aside>}
        <div className={`chat-panel ${compact ? "compact-chat" : ""}`}>
          {compact && (
            <div className="compact-chat-head">
              <div>
                <h2>{title}</h2>
                <p>{desc}</p>
              </div>
              <span>Enter 发送</span>
            </div>
          )}
          <div className="chat-messages">
            {!messages.length && <Empty text="可以开始输入，所有 AI 请求都走服务器代理，不会在客户端保存 API Key。" />}
            {messages.map((m, i) => (
              <div key={i} className={`chat-bubble ${m.role}`}>
                <b>{m.role === "user" ? "我" : "AI 家长"}</b>
                <p>{m.content}</p>
              </div>
            ))}
            {busy && <div className="chat-bubble assistant"><Loader2 className="spin" /><p>正在思考...</p></div>}
          </div>
          <div className="chat-input">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder={placeholder}
            />
            <button className="primary" onClick={send} disabled={busy || !input.trim()}>发送</button>
          </div>
        </div>
      </div>
    </>
  );
}

function AssistantPage() {
  return (
    <ChatPanel
      title="销售小助手"
      desc="销售遇到相册成交、异议处理、报单、售后问题，可以直接问。"
      placeholder="例如：家长说太贵了，我怎么快速成交？"
      systemPrompt="你是影战邦营地摄影销售小助手。请只围绕营地摄影、成长纪念册、报单、售后、客诉处理回答。风格要短、直接、可复制，优先给销售可直接发给家长的话术。严禁承诺全程跟拍、保证照片数量、随意降价、保证每天都有照片。"
    />
  );
}

function TrainingPage({ toast }: { toast: (s: string) => void }) {
  const roles = useAsync(api.trainingRoles, []);
  const [role, setRole] = useState<any | null>(null);
  const [trainingMessages, setTrainingMessages] = useState<ChatMessage[]>([]);
  const [scoreReport, setScoreReport] = useState<any | null>(null);
  const [scoring, setScoring] = useState(false);
  useEffect(() => {
    setTrainingMessages([]);
    setScoreReport(null);
  }, [role?.id]);
  const finishAndScore = async () => {
    const transcript = trainingMessages.filter(m => m.role === "user" || m.role === "assistant");
    if (!transcript.some(m => m.role === "user")) {
      toast("先和家长聊几轮，再结束评分。");
      return;
    }
    setScoring(true);
    try {
      const res = await api.aiChat([
        { role: "system", content: buildTrainingScorePrompt(role?.name || "家长模拟") },
        {
          role: "user",
          content: JSON.stringify({
            role: { name: role?.name, description: role?.description, prompt: role?.prompt },
            conversation: transcript,
          }, null, 2),
        },
      ], 1400, 0.2);
      const report = normalizeScoreReport(parseLooseJson(res.answer), res.answer || "");
      setScoreReport(report);
      const roleId = Number(role?.id);
      const payload: Record<string, unknown> = {
        model_name: res.model_name || "server-ai",
        message_count: transcript.length,
        score: report.total_score,
        summary: `${report.level}｜${report.summary}`,
        conversation_json: { role_name: role?.name, messages: transcript, report },
      };
      if (Number.isFinite(roleId)) payload.role_id = roleId;
      await api.createTrainingSession(payload).catch(() => undefined);
      toast("评分完成，已生成训练反馈。");
    } catch (e) {
      toast(`评分失败：${(e as Error).message}`);
    } finally {
      setScoring(false);
    }
  };
  const difficultyText = (value?: string) => {
    const text = String(value || "").toLowerCase();
    if (text.includes("hard") || text.includes("难") || text.includes("高")) return "高难度";
    if (text.includes("easy") || text.includes("简单") || text.includes("低")) return "基础";
    return value || "标准";
  };
  const difficultyClass = (value?: string) => {
    const text = String(value || "").toLowerCase();
    if (text.includes("hard") || text.includes("难") || text.includes("高")) return "hard";
    if (text.includes("easy") || text.includes("简单") || text.includes("低")) return "easy";
    return "normal";
  };
  if (role) {
    const prompt = role?.prompt || role?.description || "你扮演一位真实营地摄影相册家长，只能以家长身份回复，不能替销售说话。";
    return (
      <>
        <div className="training-room-head">
          <button className="secondary" onClick={() => setRole(null)}>← 返回角色选择</button>
          <button className="secondary" onClick={finishAndScore} disabled={scoring || !trainingMessages.some(m => m.role === "user")}>
            {scoring ? "评分中..." : "结束并评分"}
          </button>
        </div>
        <ChatPanel
          title={role?.name || "家长模拟"}
          desc={role?.description || "像真实家长一样训练销售。"}
          placeholder="输入你要发给家长的话，按 Enter 发送"
          compact
          side={
            <>
              <div className="side-role-head">
                <span className={`difficulty ${difficultyClass(role.difficulty)}`}>{difficultyText(role.difficulty)}</span>
                <h3>{role.name}</h3>
                <p>{role.description || "真实家长模拟，对销售进行追问、犹豫和异议挑战。"}</p>
              </div>
              <div className="side-tip">
                <b>训练提醒</b>
                <ul>
                  <li>你是销售，只发销售话术</li>
                  <li>AI 只扮演家长</li>
                  <li>尽量快速推进成交</li>
                  <li>遇到异议先稳住再收口</li>
                </ul>
              </div>
              <div className="side-process">
                <b>建议节奏</b>
                <span>破冰 → 需求判断 → 价值说明 → 异议处理 → 收款动作</span>
              </div>
              {scoreReport && (
                <div className="side-tip side-score">
                  <b>本次评分：{scoreReport.total_score} 分</b>
                  <p>{scoreReport.level}｜{scoreReport.summary}</p>
                  {Array.isArray(scoreReport.dimensions) && scoreReport.dimensions.slice(0, 6).map((d: any) => (
                    <p key={d.name}>{d.name}：{d.score}/{d.max}｜{d.comment}</p>
                  ))}
                  {Array.isArray(scoreReport.suggestions) && scoreReport.suggestions.length ? (
                    <ul>{scoreReport.suggestions.slice(0, 3).map((s: string, i: number) => <li key={i}>{s}</li>)}</ul>
                  ) : null}
                </div>
              )}
            </>
          }
          messages={trainingMessages}
          onMessagesChange={setTrainingMessages}
          systemPrompt={buildParentTrainingPrompt(prompt)}
        />
      </>
    );
  }
  return (
    <>
      <PageTitle
        eyebrow="ROLE TRAINING"
        title="实战训练"
        desc="选择一个家长类型进入训练。AI 只扮演家长，销售负责回复。"
        action={<button className="secondary" onClick={roles.reload}><RefreshCw size={16} />刷新角色</button>}
      />
      <section className="training-hero">
        <div>
          <span>训练逻辑</span>
          <h2>先选家长类型，再进入真实微信式对话</h2>
          <p>从简单到困难，逐步练习破冰、价值塑造、异议处理和快速成交。每个角色都有独立设定，避免 AI 变成销售替你说话。</p>
        </div>
        <div className="training-hero-metrics">
          <b>{roles.data?.length || 0}</b>
          <span>个训练角色</span>
        </div>
      </section>
      {roles.loading ? <Loading /> : roles.error ? <ErrorBox message={roles.error} /> : (
        <div className="role-card-grid">
          {(roles.data || []).map((r: any, index) => (
            <button className="role-card" key={r.id} onClick={() => setRole(r)}>
              <div className="role-card-top">
                <span className={`difficulty ${difficultyClass(r.difficulty)}`}>{difficultyText(r.difficulty)}</span>
                <small>#{String(index + 1).padStart(2, "0")}</small>
              </div>
              <h3>{r.name}</h3>
              <p>{r.description || "真实家长模拟，对销售进行追问、犹豫和异议挑战。"}</p>
              <div className="role-focus">
                <span>训练重点</span>
                <b>{String(r.prompt || r.description || "需求挖掘 / 异议处理 / 成交推进").slice(0, 42)}...</b>
              </div>
              <div className="role-card-action">
                开始训练 <ChevronRight size={18} />
              </div>
            </button>
          ))}
          {!roles.data?.length && <div className="panel"><Empty text="暂无训练角色，请先到管理员后台创建训练角色。" /></div>}
        </div>
      )}
    </>
  );
}

function TrainingHistoryPage() {
  const state = useAsync(api.trainingSessions, []);
  return (
    <>
      <PageTitle title="训练记录" desc="查看自己的实战训练记录。" action={<button className="secondary" onClick={state.reload}><RefreshCw size={16} />刷新</button>} />
      <div className="table-card">
        {state.loading ? <Loading /> : state.data?.length ? (
          <table><thead><tr><th>角色</th><th>模型</th><th>消息数</th><th>得分</th><th>评价</th><th>时间</th></tr></thead><tbody>
            {state.data.map((x: any) => <tr key={x.id}><td>{x.role_name || x.role_id || "-"}</td><td>{x.model_name || "-"}</td><td>{x.message_count || 0}</td><td><b>{x.score ?? "-"}</b></td><td>{x.summary || "-"}</td><td>{cnDate(x.created_at)}</td></tr>)}
          </tbody></table>
        ) : <Empty text="暂无训练记录" />}
      </div>
    </>
  );
}

function ScriptsPage() {
  const [category, setCategory] = useState("全部");
  const [query, setQuery] = useState("");
  const scripts = useAsync(api.scripts, []);
  const list = useMemo(() => {
    const rows = scripts.data || [];
    return rows.filter((x: any) => (category === "全部" || x.category === category) && `${x.question || ""}${x.title || ""}${x.answer || x.content || ""}`.includes(query));
  }, [scripts.data, category, query]);
  const categories = ["全部", ...Array.from(new Set((scripts.data || []).map((x: any) => x.category).filter(Boolean)))];
  return (
    <>
      <PageTitle title="话术库" desc="从服务器读取，EXE 和 H5 会同步同一套话术。" action={<button className="secondary" onClick={scripts.reload}><RefreshCw size={16} />刷新</button>} />
      <div className="toolbar">
        <div className="search"><Search size={17} /><input value={query} onChange={e => setQuery(e.target.value)} placeholder="搜索问题或话术" /></div>
      </div>
      <div className="category-row">{categories.map(c => <button key={String(c)} className={category === c ? "active" : ""} onClick={() => setCategory(String(c))}>{String(c)}</button>)}</div>
      <div className="script-grid-new">
        {scripts.loading ? <Loading /> : list.map((item: any) => (
          <article className="script-card-new" key={item.id}>
            <span>{item.category}</span>
            <h3>{item.question || item.title}</h3>
            <p>{item.answer || item.content}</p>
            <button onClick={() => navigator.clipboard.writeText(item.answer || item.content || "")}><Copy size={15} />复制</button>
          </article>
        ))}
      </div>
    </>
  );
}

const scriptCategoryMergeMap: Record<string, string> = {
  产品价值: "产品价值",
  效果顾虑: "产品价值",
  拍摄顾虑: "产品价值",
  价格异议: "成交推进",
  促单收口: "成交推进",
  强意向成交: "成交推进",
  痛点成交: "成交推进",
  直接收款: "成交推进",
  日常维护: "跟进维护",
  日常跟进: "跟进维护",
  沉默追单: "跟进维护",
  犹豫拖延: "跟进维护",
  家人商量: "跟进维护",
  开场接待: "开场接待",
  开场快接: "开场接待",
  合规解释: "合规说明",
  合规短句: "合规说明",
  销售红线: "合规说明",
  售后客诉: "售后处理",
  售后执行: "售后处理",
  客诉处理: "售后处理",
  套餐推荐: "套餐推荐",
  套餐选择: "套餐推荐",
  成交后登记: "成交登记",
  朋友圈: "私域营销",
  群营销: "私域营销",
};

async function mergeSimilarScriptGroupsClient(rows: any[], reloadAll: () => Promise<void>, toast: (s: string) => void) {
  if (!confirm("确定整理相似分组吗？\n\n不会删除话术，只会把意思接近的分组统一到一个分类里。")) return;
  try {
    let changed = 0;
    try {
      const result = await api.mergeSimilarScriptCategories();
      changed = Number(result.changed || 0);
    } catch {
      const targets = rows
        .map((item: any) => ({ item, target: scriptCategoryMergeMap[String(item.category || "")] }))
        .filter(({ item, target }: any) => target && target !== item.category);
      for (const { item, target } of targets) {
        await api.updateScript(item.id, { category: target });
        changed += 1;
      }
    }
    toast(`已整理 ${changed} 条话术分类`);
    await reloadAll();
  } catch (e) {
    toast((e as Error).message);
  }
}

function ScriptAdminPage({ toast }: { toast: (s: string) => void }) {
  const state = useAsync(api.scripts, []);
  const categories = useAsync(api.scriptCategoryMeta, []);
  const [editing, setEditing] = useState<any | null>(null);
  const [groupEditor, setGroupEditor] = useState<{ mode: "create" | "rename"; name: string; nextName: string } | null>(null);
  const [importing, setImporting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [form, setForm] = useState({ category: "", title: "", content: "", sort_order: 0, status: "enabled" });
  const reloadAll = async () => {
    await Promise.all([state.reload(), categories.reload()]);
  };
  const start = (item?: any) => {
    setEditing(item || {});
    setForm({
      category: item?.category || "",
      title: item?.title || item?.question || "",
      content: item?.content || item?.answer || "",
      sort_order: Number(item?.sort_order || 0),
      status: item?.status || "enabled",
    });
  };
  const save = async () => {
    if (!form.category || !form.title || !form.content) {
      toast("分类、标题、内容都要填写");
      return;
    }
    try {
      if (editing?.id) await api.updateScript(editing.id, form);
      else await api.createScript(form);
      toast("话术已保存");
      setEditing(null);
      await reloadAll();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const del = async (id: string | number) => {
    if (!confirm("确定删除这条话术吗？")) return;
    await api.deleteScript(id);
    toast("已删除");
    await reloadAll();
  };
  const saveGroup = async () => {
    if (!groupEditor?.nextName.trim()) return toast("请填写分组名称");
    try {
      if (groupEditor.mode === "create") {
        await api.createScriptCategory(groupEditor.nextName.trim());
        toast("分组已新增");
      } else {
        await api.renameScriptCategory(groupEditor.name, groupEditor.nextName.trim());
        toast("分组已改名");
      }
      setGroupEditor(null);
      await reloadAll();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const removeGroup = async (name: string) => {
    const count = (state.data || []).filter((s: any) => s.category === name).length;
    const ok = count
      ? confirm(`确定删除分组「${name}」并同时删除 ${count} 条话术吗？`)
      : confirm(`确定删除分组「${name}」吗？`);
    if (!ok) return;
    try {
      await api.deleteScriptCategory(name, count > 0);
      toast("分组已删除");
      if (selectedCategory === name) setSelectedCategory("");
      await reloadAll();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const togglePinGroup = async (name: string, pinned: boolean) => {
    try {
      await api.pinScriptCategory(name, !pinned);
      toast(!pinned ? "分组已置顶，EXE 和 H5 会同步显示" : "已取消置顶");
      await reloadAll();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const mergeSimilarGroups = () => mergeSimilarScriptGroupsClient(state.data || [], reloadAll, toast);
  const visibleGroups = (categories.data || []).filter((group: any) => {
    const name = String(group.name || "");
    const count = (state.data || []).filter((s: any) => s.category === name).length;
    const mergedTo = scriptCategoryMergeMap[name];
    return count > 0 || group.pinned || !mergedTo || mergedTo === name;
  });
  const filteredScripts = selectedCategory
    ? (state.data || []).filter((s: any) => s.category === selectedCategory)
    : (state.data || []);
  return (
    <>
      <PageTitle title="话术管理" desc="管理员维护话术库，EXE 和 H5 同步显示。" action={<button className="primary" onClick={() => start()}><Plus size={16} />新增话术</button>} />
      <div className="panel script-group-panel">
        <div className="panel-head">
          <div><h3>分组管理</h3><p>可新增、改名和删除分组，也可以一次粘贴多条话术批量导入。</p></div>
          <div className="action-row">
            <button className="secondary" onClick={() => setImporting(true)}><FileText size={16} />批量导入</button>
            <button className="secondary" onClick={mergeSimilarGroups}><FileClock size={16} />整理相似分组</button>
            <button className="secondary" onClick={() => setGroupEditor({ mode: "create", name: "", nextName: "" })}><Plus size={16} />新增分组</button>
            <button className="secondary" onClick={reloadAll}><RefreshCw size={16} />刷新</button>
          </div>
        </div>
        <div className="script-group-list">
          <button className={`script-group-chip filter-chip ${!selectedCategory ? "active" : ""}`} onClick={() => setSelectedCategory("")}>
            <b>全部分组</b><span>{(state.data || []).length} 条</span>
          </button>
          {categories.loading ? <Loading /> : visibleGroups.length ? visibleGroups.map(group => {
            const name = group.name;
            const count = (state.data || []).filter((s: any) => s.category === name).length;
            return <div className={`script-group-chip ${group.pinned ? "pinned" : ""} ${selectedCategory === name ? "active" : ""}`} key={name} onClick={() => setSelectedCategory(selectedCategory === name ? "" : name)} role="button" tabIndex={0} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") setSelectedCategory(selectedCategory === name ? "" : name); }}>
              <b>{name}</b><span>{count} 条</span>{group.pinned && <em>已置顶</em>}
              <button onClick={e => { e.stopPropagation(); togglePinGroup(name, Boolean(group.pinned)); }}><FileClock size={14} />{group.pinned ? "取消置顶" : "置顶"}</button>
              <button onClick={e => { e.stopPropagation(); setGroupEditor({ mode: "rename", name, nextName: name }); }}><Edit3 size={14} />编辑</button>
              <button className="danger" onClick={e => { e.stopPropagation(); removeGroup(name); }}><Trash2 size={14} />删除</button>
            </div>;
          }) : <Empty text="暂无分组" />}
        </div>
      </div>
      <div className="table-card">
        {state.loading ? <Loading /> : (
          <table><thead><tr><th>分类</th><th>标题</th><th>内容</th><th>排序</th><th>状态</th><th>操作</th></tr></thead><tbody>
            {filteredScripts.map((s: any) => <tr key={s.id}><td>{s.category}</td><td>{s.title}</td><td className="long-cell">{s.content}</td><td>{s.sort_order || 0}</td><td><Badge text={s.status || "enabled"} /></td><td className="row-actions"><button onClick={() => start(s)}>编辑</button><button onClick={() => api.updateScriptStatus(s.id, s.status === "enabled" ? "disabled" : "enabled").then(state.reload)}>{s.status === "enabled" ? "停用" : "启用"}</button><button className="danger" onClick={() => del(s.id)}>删除</button></td></tr>)}
          </tbody></table>
        )}
      </div>
      {editing && (
        <div className="modal-backdrop">
          <div className="modal compact-modal">
            <div className="modal-head"><h2>{editing.id ? "编辑话术" : "新增话术"}</h2><button onClick={() => setEditing(null)}><X /></button></div>
            <div className="form-panel no-border one-col">
              <Field label="分类" value={form.category} onChange={v => setForm({ ...form, category: v })} />
              <Field label="标题/问题" value={form.title} onChange={v => setForm({ ...form, title: v })} />
              <label><span>话术内容</span><textarea rows={8} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} /></label>
              <Field label="排序" value={form.sort_order} onChange={v => setForm({ ...form, sort_order: Number(v || 0) })} />
            </div>
            <div className="modal-actions"><button className="secondary" onClick={() => setEditing(null)}>取消</button><button className="primary" onClick={save}>保存</button></div>
          </div>
        </div>
      )}
      {groupEditor && (
        <div className="modal-backdrop">
          <div className="modal compact-modal">
            <div className="modal-head"><h2>{groupEditor.mode === "create" ? "新增分组" : "编辑分组"}</h2><button onClick={() => setGroupEditor(null)}><X /></button></div>
            <div className="form-panel no-border one-col">
              <Field label="分组名称" value={groupEditor.nextName} onChange={v => setGroupEditor({ ...groupEditor, nextName: v })} />
            </div>
            <div className="modal-actions"><button className="secondary" onClick={() => setGroupEditor(null)}>取消</button><button className="primary" onClick={saveGroup}>保存</button></div>
          </div>
        </div>
      )}
      {importing && <ScriptImportModal close={() => setImporting(false)} done={async msg => { setImporting(false); toast(msg); await reloadAll(); }} />}
    </>
  );
}

function MaterialAdminPage({ toast }: { toast: (s: string) => void }) {
  const [categoryId, setCategoryId] = useState("");
  const [keyword, setKeyword] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [batchBusy, setBatchBusy] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MaterialCategory | null>(null);
  const [editing, setEditing] = useState<Partial<MaterialItem> | null>(null);
  const categories = useAsync(api.adminMaterialCategories, []);
  const materials = useAsync(
    () => api.adminMaterials({ category_id: categoryId, keyword, material_type: type, page, per_page: 24 }),
    [categoryId, keyword, type, page],
  );
  const rows = useMemo(() => materials.data?.items || [], [materials.data]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedRows = useMemo(() => rows.filter(item => selectedSet.has(String(item.id))), [rows, selectedSet]);
  const currentPageAllSelected = rows.length > 0 && rows.every(item => selectedSet.has(String(item.id)));
  const stats = useMemo(() => {
    const cats = categories.data || [];
    return {
      categories: cats.length,
      total: materials.data?.total || 0,
      enabled: rows.filter(item => item.status !== "disabled").length,
      image: rows.filter(item => item.material_type === "image").length,
    };
  }, [categories.data, materials.data, rows]);
  const reloadAll = async () => {
    await Promise.all([categories.reload(), materials.reload()]);
  };
  useEffect(() => {
    const visibleIds = new Set(rows.map(item => String(item.id)));
    setSelectedIds(prev => prev.filter(id => visibleIds.has(id)));
  }, [rows]);
  const toggleSelected = (item: MaterialItem) => {
    const id = String(item.id);
    setSelectedIds(prev => prev.includes(id) ? prev.filter(value => value !== id) : [...prev, id]);
  };
  const toggleCurrentPage = () => {
    if (currentPageAllSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(rows.map(item => String(item.id)));
  };
  const batchUpdateStatus = async (status: "enabled" | "disabled") => {
    if (!selectedRows.length) return toast("请先选择素材");
    setBatchBusy(true);
    try {
      for (const item of selectedRows) {
        await api.updateMaterial(item.id, { status });
      }
      toast(status === "enabled" ? "已批量启用素材" : "已批量停用素材");
      setSelectedIds([]);
      await materials.reload();
      await categories.reload();
    } catch (e) {
      alert((e as Error).message || "批量操作失败，请稍后重试");
    } finally {
      setBatchBusy(false);
    }
  };
  const batchDelete = async () => {
    if (!selectedRows.length) return toast("请先选择素材");
    if (!confirm(`确定删除已选的 ${selectedRows.length} 个素材吗？删除后 H5 会同步隐藏。`)) return;
    setBatchBusy(true);
    try {
      for (const item of selectedRows) {
        await api.deleteMaterial(item.id);
      }
      toast(`已删除 ${selectedRows.length} 个素材`);
      setSelectedIds([]);
      await materials.reload();
      await categories.reload();
    } catch (e) {
      alert((e as Error).message || "批量删除失败，请稍后重试");
    } finally {
      setBatchBusy(false);
    }
  };
  const backfillThumbnails = async () => {
    if (!confirm("确定开始给旧素材补缩略图吗？系统会处理 COS 里的旧图片，不需要重新上传。")) return;
    setBatchBusy(true);
    try {
      const result = await api.backfillMaterialThumbnails(200);
      toast(`缩略图处理完成：新增 ${result.updated} 个，跳过 ${result.skipped} 个，失败 ${result.failed} 个`);
      await materials.reload();
      await categories.reload();
    } catch (e) {
      alert((e as Error).message || "补缩略图失败，请稍后重试");
    } finally {
      setBatchBusy(false);
    }
  };
  const del = async (item: MaterialItem) => {
    if (!confirm(`确定删除素材「${item.title}」吗？H5 会同步隐藏。`)) return;
    await api.deleteMaterial(item.id);
    setSelectedIds(prev => prev.filter(id => id !== String(item.id)));
    toast("素材已删除");
    await materials.reload();
  };
  const delCategory = async (item: MaterialCategory) => {
    if (!confirm(`确定删除分类「${item.name}」吗？分类下素材不会删除，但会变成未分类。`)) return;
    await api.deleteMaterialCategory(item.id);
    toast("分类已删除");
    if (String(categoryId) === String(item.id)) setCategoryId("");
    await reloadAll();
  };
  return (
    <>
      <PageTitle
        eyebrow="MATERIAL LIBRARY"
        title="素材管理"
        desc="电脑端上传和维护素材，手机 H5 素材库会实时同步。"
        action={<div className="action-row"><button className="secondary" disabled={batchBusy} onClick={() => void backfillThumbnails()}><RefreshCw size={16} />补旧素材缩略图</button><button className="secondary" onClick={() => setEditingCategory({ id: "", name: "", sort_order: 0, status: "enabled" })}><Plus size={16} />新增分类</button><button className="primary" onClick={() => setEditing({ material_type: "image", status: "enabled", sort_order: 0, category_id: categoryId || undefined })}><Plus size={16} />新增素材</button></div>}
      />
      <div className="stat-grid four">
        <StatCard label="素材总数" value={stats.total} />
        <StatCard label="当前页启用" value={stats.enabled} />
        <StatCard label="当前页图片" value={stats.image} />
        <StatCard label="分类数量" value={stats.categories} />
      </div>
      <div className="material-admin-layout">
        <aside className="material-category-panel">
          <div className="panel-head compact">
            <div><h3>素材分类</h3><p>点击分类筛选右侧素材</p></div>
          </div>
          <button className={`material-category-item ${!categoryId ? "active" : ""}`} onClick={() => { setCategoryId(""); setPage(1); }}>
            <b>全部素材</b><span>{materials.data?.total || 0}</span>
          </button>
          {(categories.data || []).map(item => (
            <div key={item.id} className={`material-category-item ${String(categoryId) === String(item.id) ? "active" : ""}`} onClick={() => { setCategoryId(String(item.id)); setPage(1); }}>
              <b>{item.name}</b>
              <span>{item.count || 0}</span>
              <div className="material-category-actions">
                <button onClick={e => { e.stopPropagation(); setEditingCategory(item); }}><Edit3 size={13} /></button>
                <button className="danger" onClick={e => { e.stopPropagation(); void delCategory(item); }}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </aside>
        <section className="material-main-panel">
          <div className="toolbar dense">
            <div className="search grow"><Search size={17} /><input value={keyword} onChange={e => { setKeyword(e.target.value); setPage(1); }} placeholder="搜索素材标题、说明、文案内容" /></div>
            <select value={type} onChange={e => { setType(e.target.value); setPage(1); }}>
              <option value="">全部类型</option>
              <option value="image">图片素材</option>
              <option value="text">文案素材</option>
            </select>
            <button className="secondary" onClick={reloadAll}><RefreshCw size={16} />刷新</button>
          </div>
          {rows.length > 0 && (
            <div className={`material-batch-bar ${selectedRows.length ? "active" : ""}`}>
              <label className="material-select-all">
                <input type="checkbox" checked={currentPageAllSelected} onChange={toggleCurrentPage} />
                <span>全选当前页</span>
              </label>
              <b>已选 {selectedRows.length} 个</b>
              <div>
                <button className="secondary" disabled={!selectedRows.length || batchBusy} onClick={() => void batchUpdateStatus("enabled")}>批量启用</button>
                <button className="secondary" disabled={!selectedRows.length || batchBusy} onClick={() => void batchUpdateStatus("disabled")}>批量停用</button>
                <button className="secondary" disabled={!selectedRows.length || batchBusy} onClick={() => setSelectedIds([])}>取消选择</button>
                <button className="danger" disabled={!selectedRows.length || batchBusy} onClick={() => void batchDelete()}><Trash2 size={15} />批量删除</button>
              </div>
            </div>
          )}
          {materials.loading ? <Loading /> : materials.error ? <ErrorBox message={materials.error} /> : rows.length ? (
            <>
              <div className="material-card-grid">
                {rows.map(item => (
                  <article className={`material-card ${item.status === "disabled" ? "disabled" : ""} ${selectedSet.has(String(item.id)) ? "selected" : ""}`} key={item.id}>
                    <div className="material-thumb">
                      <label className="material-card-check" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedSet.has(String(item.id))} onChange={() => toggleSelected(item)} />
                        <span>{selectedSet.has(String(item.id)) ? "✓" : ""}</span>
                      </label>
                      {item.material_type === "image" && item.thumbnail_url ? <img src={item.thumbnail_url} alt={item.title} /> : <FileText />}
                      <span>{item.material_type === "image" ? "图片" : "文案"}</span>
                    </div>
                    <div className="material-card-body">
                      <small>{item.category_name || "未分类"} · {item.status === "disabled" ? "已停用" : "启用中"}</small>
                      <h3>{item.title}</h3>
                      <p>{item.description || item.content_text || "暂无说明"}</p>
                      {item.file_size ? <em>{fileSizeText(item.file_size)}</em> : null}
                    </div>
                    <div className="material-card-actions">
                      <button onClick={() => setEditing(item)}><Edit3 size={15} />编辑</button>
                      <button onClick={() => api.updateMaterial(item.id, { status: item.status === "disabled" ? "enabled" : "disabled" }).then(materials.reload)}>{item.status === "disabled" ? "启用" : "停用"}</button>
                      <button className="danger" onClick={() => void del(item)}><Trash2 size={15} />删除</button>
                    </div>
                  </article>
                ))}
              </div>
              <PaginationBar page={materials.data?.page || page} pages={materials.data?.pages || 1} total={materials.data?.total || 0} perPage={materials.data?.per_page || 24} onPage={setPage} />
            </>
          ) : <Empty text="暂无素材，请先新增分类和素材。" />}
        </section>
      </div>
      {editingCategory && <MaterialCategoryModal item={editingCategory} close={() => setEditingCategory(null)} done={async () => { setEditingCategory(null); toast("分类已保存"); await reloadAll(); }} />}
      {editing && <MaterialEditModal item={editing} categories={categories.data || []} close={() => setEditing(null)} done={async () => { setEditing(null); toast("素材已保存"); await materials.reload(); await categories.reload(); }} />}
    </>
  );
}

function MaterialCategoryModal({ item, close, done }: { item: Partial<MaterialCategory>; close: () => void; done: () => void }) {
  const isNew = !item.id;
  const [form, setForm] = useState({ name: item.name || "", sort_order: Number(item.sort_order || 0), status: item.status || "enabled" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) return alert("请填写分类名称");
    setBusy(true);
    try {
      if (isNew) await api.createMaterialCategory(form);
      else await api.updateMaterialCategory(item.id!, form);
      await done();
    } catch (e) {
      alert((e as Error).message || "保存失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal compact-modal">
        <div className="modal-head"><h2>{isNew ? "新增分类" : "编辑分类"}</h2><button onClick={close}><X /></button></div>
        <div className="form-panel no-border one-col">
          <Field label="分类名称" value={form.name} onChange={v => setForm({ ...form, name: v })} />
          <Field label="排序" value={form.sort_order} onChange={v => setForm({ ...form, sort_order: Number(v || 0) })} />
          <label><span>状态</span><select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}><option value="enabled">启用</option><option value="disabled">停用</option></select></label>
        </div>
        <div className="modal-actions"><button className="secondary" onClick={close}>取消</button><button className="primary" disabled={busy} onClick={save}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}保存</button></div>
      </div>
    </div>
  );
}

function MaterialEditModal({ item, categories, close, done }: { item: Partial<MaterialItem>; categories: MaterialCategory[]; close: () => void; done: () => void }) {
  const isNew = !item.id;
  const [form, setForm] = useState<Partial<MaterialItem>>({
    category_id: item.category_id ? String(item.category_id) : "",
    title: item.title || "",
    description: item.description || "",
    material_type: item.material_type || "image",
    content_text: item.content_text || "",
    file_url: item.file_url || "",
    thumbnail_url: item.thumbnail_url || "",
    storage_provider: item.storage_provider || "",
    object_key: item.object_key || "",
    file_name: item.file_name || "",
    file_size: item.file_size || undefined,
    sort_order: Number(item.sort_order || 0),
    status: item.status || "enabled",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [batchProgress, setBatchProgress] = useState("");
  const titleFromFile = (file: File) => (file.name || "素材图片").replace(/\.[^.]+$/, "").trim() || "素材图片";
  const upload = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const info = await api.uploadMaterialImage(file);
      setForm(prev => ({
        ...prev,
        file_url: info.file_url,
        thumbnail_url: info.thumbnail_url || info.file_url,
        storage_provider: info.storage_provider,
        object_key: info.object_key,
        file_name: info.file_name,
        file_size: info.file_size,
      }));
    } catch (e) {
      alert((e as Error).message || "上传失败，请重试");
    } finally {
      setUploading(false);
    }
  };
  const uploadFiles = async (files: File[]) => {
    const imageFiles = files.filter(file => file.type.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(file.name));
    if (!imageFiles.length) return;
    if (imageFiles.length === 1) {
      await upload(imageFiles[0]);
      return;
    }
    if (!isNew) {
      alert("编辑已有素材时一次只能替换一张图片；新增素材时可以多选批量上传。");
      await upload(imageFiles[0]);
      return;
    }
    setUploading(true);
    setBatchProgress(`准备上传 ${imageFiles.length} 张图片...`);
    try {
      for (let index = 0; index < imageFiles.length; index += 1) {
        const file = imageFiles[index];
        setBatchProgress(`正在上传 ${index + 1}/${imageFiles.length}：${file.name}`);
        const info = await api.uploadMaterialImage(file);
        await api.createMaterial({
          category_id: form.category_id ? Number(form.category_id) : null,
          title: titleFromFile(file),
          description: form.description || "",
          material_type: "image",
          file_url: info.file_url,
          thumbnail_url: info.thumbnail_url || info.file_url,
          storage_provider: info.storage_provider,
          object_key: info.object_key,
          file_name: info.file_name || file.name,
          file_size: info.file_size || file.size,
          sort_order: Number(form.sort_order || 0) + index,
          status: form.status || "enabled",
        });
      }
      setBatchProgress("");
      await done();
    } catch (e) {
      alert((e as Error).message || "批量上传失败，请重试");
    } finally {
      setUploading(false);
    }
  };
  const save = async () => {
    if (!String(form.title || "").trim()) return alert("请填写素材标题");
    if (form.material_type === "image" && !form.file_url) return alert("请先上传素材图片");
    if (form.material_type === "text" && !String(form.content_text || "").trim()) return alert("请填写文案内容");
    setBusy(true);
    try {
      const payload = { ...form, category_id: form.category_id ? Number(form.category_id) : null, sort_order: Number(form.sort_order || 0) };
      if (isNew) await api.createMaterial(payload);
      else await api.updateMaterial(item.id!, payload);
      await done();
    } catch (e) {
      alert((e as Error).message || "保存失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal material-edit-modal">
        <div className="modal-head"><h2>{isNew ? "新增素材" : "编辑素材"}</h2><button onClick={close}><X /></button></div>
        <div className="form-panel no-border material-edit-form">
          <label><span>素材类型</span><select value={form.material_type} onChange={e => setForm({ ...form, material_type: e.target.value as "image" | "text" })}><option value="image">图片素材</option><option value="text">文案素材</option></select></label>
          <label><span>分类</span><select value={String(form.category_id || "")} onChange={e => setForm({ ...form, category_id: e.target.value })}><option value="">未分类</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
          <Field label="标题" value={form.title || ""} onChange={v => setForm({ ...form, title: v })} />
          <Field label="排序" value={form.sort_order || 0} onChange={v => setForm({ ...form, sort_order: Number(v || 0) })} />
          <label className="full-field"><span>说明</span><textarea rows={3} value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="可填写素材用途、推荐发送场景" /></label>
          {form.material_type === "image" ? (
            <div className="material-upload-box full-field">
              <div className="material-upload-preview">
                {form.file_url ? <img src={form.thumbnail_url || form.file_url} alt="素材预览" /> : <Image />}
              </div>
              <div>
                <b>上传图片</b>
                <p>支持 jpg / png / webp / gif，单张不超过 20MB。H5 销售端可长按保存。</p>
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple disabled={uploading} onChange={e => void uploadFiles(Array.from(e.target.files || []))} />
                {uploading && <span className="hint">正在上传...</span>}
                {form.file_name && <span className="hint">{form.file_name} {fileSizeText(Number(form.file_size || 0))}</span>}
              </div>
            </div>
          ) : (
            <label className="full-field"><span>文案内容</span><textarea rows={8} value={form.content_text || ""} onChange={e => setForm({ ...form, content_text: e.target.value })} placeholder="填写销售可以直接复制的素材文案" /></label>
          )}
          <label><span>状态</span><select value={form.status || "enabled"} onChange={e => setForm({ ...form, status: e.target.value })}><option value="enabled">启用</option><option value="disabled">停用</option></select></label>
        </div>
        <div className="modal-actions"><button className="secondary" onClick={close}>取消</button><button className="primary" disabled={busy || uploading} onClick={save}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}保存素材</button></div>
      </div>
    </div>
  );
}

function parseScriptImportText(text: string) {
  const lines = text.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  return lines.map((line, index) => {
    const parts = line.includes("\t") ? line.split("\t") : line.split(",");
    const [category = "", title = "", content = "", sort = "", status = "enabled"] = parts.map(part => part.trim());
    return {
      category,
      title,
      content,
      sort_order: Number(sort || index + 1) || index + 1,
      status: status || "enabled",
    };
  }).filter(row => row.category && row.title && row.content);
}

function ScriptImportModal({ close, done }: { close: () => void; done: (msg: string) => void | Promise<void> }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const rows = useMemo(() => parseScriptImportText(text), [text]);
  const submit = async () => {
    if (!rows.length) return alert("请粘贴内容：分类、标题、内容、排序");
    setBusy(true);
    try {
      const result = await api.importScripts(rows);
      await done(`已导入 ${result.created || 0} 条，跳过 ${result.skipped || 0} 条`);
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal script-import-modal">
        <div className="modal-head">
          <div><h2>批量导入话术</h2><p>从 Excel 复制粘贴即可。列顺序：分类、标题、内容、排序、状态。</p></div>
          <button onClick={close}><X /></button>
        </div>
        <textarea className="script-import-textarea" value={text} onChange={e => setText(e.target.value)} placeholder={"Product value\tWhy buy album?\tParent reply script...\t201\tenabled"} />
        <div className="script-import-preview">
          <b>预览：{rows.length} 条有效数据</b>
          {rows.slice(0, 5).map((row, index) => <span key={index}>{row.category} / {row.title}</span>)}
        </div>
        <div className="modal-actions"><button className="secondary" onClick={close}>取消</button><button className="primary" disabled={busy} onClick={submit}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}导入</button></div>
      </div>
    </div>
  );
}

function OneReportPage({ user, toast }: { user: ApiUser; toast: (s: string) => void }) {
  const draftKey = `one_report_draft_${user.id || user.phone || "default"}`;
  const options = useAsync(() => api.reportOptions(), []);
  const brands = (options.data || []).filter(x => x.option_type === "brand");
  const campuses = (options.data || []).filter(x => x.option_type === "campus" || x.option_type === "city");
  const dayOptions = (options.data || []).filter(x => x.option_type === "days");
  const giftOptions = (options.data || []).filter(x => x.option_type === "gift" && x.status !== "disabled");
  const packageOptions = (options.data || []).filter(x => x.option_type === "package" && x.status !== "disabled");
  const [form, setForm] = useState<Partial<OrderReport>>({ order_teacher: user.name });
  const [locked, setLocked] = useState<Record<string, boolean>>({ order_teacher: true });
  const [mode, setMode] = useState<"single" | "batch">("single");
  const [raw, setRaw] = useState("");
  const [batchRows, setBatchRows] = useState<BatchSourceRow[]>(() => [newBatchSourceRow()]);
  const [batchItems, setBatchItems] = useState<BatchReportItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [parsing, setParsing] = useState(false);
  const submittingRef = useRef(false);
  const draftRestoredRef = useRef(false);
  const remarkTouchedRef = useRef(false);
  const skipNextDraftSaveRef = useRef(false);
  const set = (key: keyof OrderReport, value: any) => {
    if (key === "remark") remarkTouchedRef.current = true;
    setForm(v => ({ ...v, [key]: value }));
  };
  const clearDraft = () => {
    localStorage.removeItem(draftKey);
    remarkTouchedRef.current = false;
  };
  const resetDraft = () => {
    if (!confirm("确定清空当前一键报单草稿吗？")) return;
    clearDraft();
    setMode("single");
    setForm({ order_teacher: user.name });
    setLocked({ order_teacher: true });
    setRaw("");
    setBatchRows([newBatchSourceRow()]);
    setBatchItems([]);
    toast("草稿已清空");
  };
  useEffect(() => {
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    const saved = localStorage.getItem(draftKey);
    if (!saved) return;
    try {
      const draft = JSON.parse(saved);
      if (!draft?.saved_at || Date.now() - Number(draft.saved_at) > ONE_REPORT_DRAFT_TTL) {
        localStorage.removeItem(draftKey);
        return;
      }
      if (draft.mode === "single" || draft.mode === "batch") setMode(draft.mode);
      if (draft.form && typeof draft.form === "object") setForm({ order_teacher: user.name, ...draft.form });
      if (draft.locked && typeof draft.locked === "object") setLocked({ order_teacher: true, ...draft.locked });
      if (typeof draft.raw === "string") setRaw(draft.raw);
      const restoredRows: BatchSourceRow[] = Array.isArray(draft.batchRows) && draft.batchRows.length
        ? draft.batchRows.map((row: any) => typeof row === "string"
          ? newBatchSourceRow(row)
          : { id: String(row?.id || newBatchSourceRow().id), raw: String(row?.raw || "") })
        : [newBatchSourceRow()];
      setBatchRows(restoredRows);
      if (Array.isArray(draft.batchItems)) {
        setBatchItems(draft.batchItems.map((item: any, index: number) => {
          if (item?.fields && item?.id) {
            return {
              id: String(item.id),
              raw: String(item.raw || item.fields.raw_text || ""),
              fields: { ...item.fields },
              remarkTouched: Boolean(item.remarkTouched),
              submitError: item.submitError ? String(item.submitError) : undefined,
            };
          }
          const source = restoredRows[index] || newBatchSourceRow(String(item?.raw_text || ""));
          return {
            id: source.id,
            raw: source.raw,
            fields: { ...(item || {}), raw_text: item?.raw_text || source.raw },
            // 旧草稿无法知道具体改过哪一单；有手改标记时全部保护，避免覆盖现有备注。
            remarkTouched: Boolean(draft.remarkTouched && item?.remark),
          };
        }));
      }
      remarkTouchedRef.current = Boolean(draft.remarkTouched);
      toast("已恢复上次未提交草稿");
    } catch {
      localStorage.removeItem(draftKey);
    }
  }, [draftKey, toast, user.name]);
  useEffect(() => {
    if (!draftRestoredRef.current) return;
    if (skipNextDraftSaveRef.current) {
      skipNextDraftSaveRef.current = false;
      localStorage.removeItem(draftKey);
      return;
    }
    const hasDraft = raw.trim()
      || batchRows.some(item => item.raw.trim())
      || batchItems.length
      || Object.entries(form).some(([key, value]) => key !== "order_teacher" && String(value ?? "").trim());
    if (!hasDraft) {
      localStorage.removeItem(draftKey);
      return;
    }
    localStorage.setItem(draftKey, JSON.stringify({
      saved_at: Date.now(),
      mode,
      form,
      locked,
      raw,
      batchRows,
      batchItems,
      remarkTouched: remarkTouchedRef.current,
    }));
  }, [draftKey, mode, form, locked, raw, batchRows, batchItems]);
  const splitValues = (text?: string) => String(text || "").split(/[，,、\s\n]+/).map(x => x.trim()).filter(Boolean);
  const toOptions = (values: string[], prefix: string): ApiOption[] => values.map((value, index) => ({ id: `${prefix}-${index}-${value}`, option_type: prefix, value, label: value }));
  const brandMeta = (brand?: string) => {
    const item = brands.find(x => x.value === brand || x.label === brand);
    const childDays = (options.data || []).filter(x => x.option_type === "brand_day" && x.parent_value === brand).map(x => x.value);
    const childCampuses = (options.data || []).filter(x => x.option_type === "brand_campus" && x.parent_value === brand).map(x => x.value);
    const childDates = (options.data || []).filter(x => x.option_type === "brand_date" && x.parent_value === brand).map(x => x.value);
    if (childDays.length || childCampuses.length || childDates.length) {
      return { days: childDays, campuses: childCampuses, report_dates: childDates };
    }
    try {
      const raw = item?.parent_value ? JSON.parse(item.parent_value) : {};
      const days = Array.isArray(raw.days) ? raw.days : splitValues(raw.days || raw.day_text);
      const campuses = Array.isArray(raw.campuses) ? raw.campuses : splitValues(raw.campuses || raw.campus_text);
      const report_dates = Array.isArray(raw.report_dates) ? raw.report_dates : splitValues(raw.report_dates || raw.report_date || raw.date_text);
      return { days, campuses, report_dates };
    } catch {
      return { days: [] as string[], campuses: [] as string[], report_dates: [] as string[] };
    }
  };
  const selectedMeta = brandMeta(form.brand);
  const brandDayOptions = selectedMeta.days.length ? toOptions(selectedMeta.days, "days") : dayOptions;
  const brandCampusOptions = selectedMeta.campuses.length ? toOptions(selectedMeta.campuses, "campus") : campuses;
  const brandDateOptions = toOptions(selectedMeta.report_dates, "report_date");
  const applyBrand = (brand: string) => {
    const meta = brandMeta(brand);
    setForm(v => ({
      ...v,
      brand,
      report_date: locked.report_date ? v.report_date : "",
      days: locked.days ? v.days : "",
      campus: locked.campus ? v.campus : "",
    }));
  };
  const mergeLocked = (data: Partial<OrderReport>, base: Partial<OrderReport> = form, preserveTouchedRemark = remarkTouchedRef.current) => {
    const next: Partial<OrderReport> = { ...base, ...data };
    (Object.keys(locked) as (keyof OrderReport)[]).forEach(key => {
      if (locked[key as string]) next[key] = base[key] as any;
    });
    if (!next.brand && base.brand) next.brand = base.brand;
    const meta = brandMeta(next.brand);
    next.brand = matchOption(next.brand, brands);
    const strict = !!next.brand;
    next.campus = matchOption(next.campus, meta.campuses.length ? toOptions(meta.campuses, "campus") : campuses, strict && meta.campuses.length > 0);
    next.days = matchOption(next.days, meta.days.length ? toOptions(meta.days, "days") : dayOptions, strict && meta.days.length > 0);
    if (meta.report_dates.length) {
      next.report_date = matchOption(next.report_date, toOptions(meta.report_dates, "report_date"), true);
    }
    next.package_name = matchOption(next.package_name, packageOptions, true);
    next.gift = matchOption(next.gift, giftOptions, true);
    next.order_teacher = next.order_teacher || user.name;
    if (preserveTouchedRemark) next.remark = base.remark;
    return next;
  };
  const compactText = (value: any) => String(value || "").toLowerCase().replace(/[\s，,。；;、:：/\\\-_.|+()（）【】\[\]]/g, "");
  const matchGiftFromText = (source: string) => {
    const text = compactText(source);
    if (!text || !giftOptions.length) return "";
    const hint = compactText(source.match(/(?:赠品|赠送|送)\s*([^\s，,。；;、]+)/)?.[1] || "");
    const ranked = giftOptions
      .map(option => {
        const value = String(option.value || "");
        const label = String(option.label || "");
        const names = Array.from(new Set([value, label].filter(Boolean)));
        const score = Math.max(...names.map(name => {
          const key = compactText(name);
          if (!key) return 0;
          if (text.includes(key)) return key.length + 100;
          if (hint && key.includes(hint)) return hint.length + 80;
          if (key.includes(text)) return text.length + 40;
          const tokens = key.match(/[\u4e00-\u9fa5]+|[a-z0-9]+/g) || [];
          return tokens.filter(token => token.length >= 2 && text.includes(token)).join("").length;
        }));
        return { option, score };
      })
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score);
    if (!ranked.length) return "";
    const best = ranked[0];
    const ties = ranked.filter(item => item.score === best.score);
    return ties.length === 1 ? best.option.value : "";
  };
  const matchPackageFromText = (source: string) => {
    const text = compactText(source);
    if (!text || !packageOptions.length) return "";
    const hint = compactText(source.match(/(?:下单款式|款式|套餐)\s*([^\s，,。；;、]+)/)?.[1] || "");
    const ranked = packageOptions
      .map(option => {
        const value = String(option.value || "");
        const label = String(option.label || "");
        const names = Array.from(new Set([value, label].filter(Boolean)));
        const score = Math.max(...names.map(name => {
          const key = compactText(name);
          if (!key) return 0;
          if (text.includes(key)) return key.length + 120;
          if (hint && key.includes(hint)) return hint.length + 90;
          if (key.includes(text)) return text.length + 45;
          const shortKey = key.replace(/款$/, "");
          if (shortKey && shortKey.length >= 2 && text.includes(shortKey)) return shortKey.length + 70;
          const tokens = key.match(/[\u4e00-\u9fa5]+|[a-z0-9]+/g) || [];
          return tokens.filter(token => token.length >= 2 && text.includes(token)).join("").length;
        }));
        return { option, score };
      })
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score);
    if (!ranked.length) return "";
    const best = ranked[0];
    const ties = ranked.filter(item => item.score === best.score);
    return ties.length === 1 ? best.option.value : "";
  };
  const extractAmount = (source: string) => {
    const labeled = source.match(/(?:下单金额|金额|价格|付款|实收|收款|费用)[^\d\n]{0,12}(\d{3,4})(?:\.\d+)?\s*元?/)?.[1];
    if (labeled) return labeled;
    const afterPackage = source.match(/(?:经典款|轻奢款|豪华款|标准款|基础款|[\u4e00-\u9fa5A-Za-z0-9]{1,10}款)\s*(\d{3,4})(?:\.\d+)?\s*元?/)?.[1];
    if (afterPackage) return afterPackage;
    const withYuan = source.match(/(?:^|[^\d])(\d{3,4})(?:\.\d+)?\s*元/)?.[1];
    if (withYuan) return withYuan;
    const candidates = Array.from(source.matchAll(/\d{3,4}(?:\.\d+)?/g));
    const hit = candidates.find(match => {
      const value = match[0];
      const index = match.index || 0;
      const prev = source[index - 1] || "";
      const next = source[index + value.length] || "";
      if (/\d/.test(prev) || /\d/.test(next)) return false;
      const near = source.slice(Math.max(0, index - 6), index + value.length + 6);
      if (/(?:班|天|年|月|日|号)/.test(next)) return false;
      if (value.startsWith("20") && /(?:年|月|日|号|[\/.-])/.test(near)) return false;
      return true;
    });
    return hit?.[0] || "";
  };
  const extractRemark = (source: string) => {
    const match = source.match(/(?:^|[\s，,。；;])(?:备注|要求|拍摄要求|特殊要求|家长要求)\s*[：:]\s*(.*?)(?=(?:\s+(?:夏令营品牌|品牌|孩子姓名|孩子|姓名|名字|班级名称|所在班级|班级|报道日期|报到日期|参营天数|天数|所在校区|校区|营地|下单款式|款式|套餐|下单老师|老师|销售|下单金额|金额|价格|付款金额|赠品|赠送|礼品)\s*[：:])|[\r\n，,。；;]|$)/);
    return match?.[1]?.trim() || "";
  };
  const matchOption = (value: any, list: ApiOption[], strict = false) => {
    const text = String(value || "").trim();
    if (!text) return "";
    if (!list.length) return strict ? "" : text;
    const md = (v: string) => {
      const s = String(v || "").trim();
      const iso = s.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (iso) return `${Number(iso[2])}-${Number(iso[3])}`;
      const cn = s.match(/(\d{1,2})\s*月\s*(\d{1,2})\s*[日号]?/);
      if (cn) return `${Number(cn[1])}-${Number(cn[2])}`;
      const slash = s.match(/(\d{1,2})[\/.-](\d{1,2})/);
      if (slash) return `${Number(slash[1])}-${Number(slash[2])}`;
      return "";
    };
    const textMd = md(text);
    if (textMd) {
      const byDate = list.find(x => md(x.value) === textMd || md(x.label || "") === textMd);
      if (byDate) return byDate.value;
    }
    const exact = list.find(x => x.value === text || x.label === text);
    if (exact) return exact.value;
    const fuzzy = list.find(x => text.includes(x.value) || x.value.includes(text) || (x.label && (text.includes(x.label) || x.label.includes(text))));
    return fuzzy?.value || (strict ? "" : text);
  };
  const normalizeDateText = (text: string) => {
    const nowYear = new Date().getFullYear();
    const iso = text.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
    if (iso) return `${iso[1]}-${String(Number(iso[2])).padStart(2, "0")}-${String(Number(iso[3])).padStart(2, "0")}`;
    const short = text.match(/(\d{1,2})\s*[月./-]\s*(\d{1,2})\s*[日号]?/);
    if (short) return `${nowYear}-${String(Number(short[1])).padStart(2, "0")}-${String(Number(short[2])).padStart(2, "0")}`;
    return "";
  };
  const nonEmpty = (data: Partial<OrderReport>) => Object.fromEntries(Object.entries(data || {}).filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "")) as Partial<OrderReport>;
  const localParseReport = (line: string): Partial<OrderReport> => {
    const source = line.trim();
    const result: Partial<OrderReport> = { raw_text: source };
    const meta = brandMeta(form.brand);
    const campusOptions = meta.campuses.length ? toOptions(meta.campuses, "campus") : campuses;
    const daysOptions = meta.days.length ? toOptions(meta.days, "days") : dayOptions;

    const date = normalizeDateText(source);
    if (date) result.report_date = date;

    const dayMatch = source.match(/(\d{1,2})\s*天/);
    if (dayMatch) result.days = `${Number(dayMatch[1])}天`;

    const classMatch = source.match(/([A-Za-z]?\d{1,3}\s*班)/i);
    if (classMatch) result.class_name = classMatch[1].replace(/\s+/g, "");

    const packageByOptions = matchPackageFromText(source);
    if (packageByOptions) result.package_name = packageByOptions;
    else {
      const pkgMatch = source.match(/([\u4e00-\u9fa5A-Za-z0-9]{1,8}款)/);
      if (pkgMatch) result.package_name = pkgMatch[1];
    }

    const amount = extractAmount(source);
    if (amount) result.amount = amount;

    const giftMatch = source.match(/(?:赠品|赠送|送)\s*([^\s，,。；;]+)/);
    if (giftMatch) {
      result.gift = giftMatch[1];
      const afterGift = source.slice((giftMatch.index || 0) + giftMatch[0].length).replace(/^[\s，,。；;]+/, "").trim();
      if (afterGift) result.remark = afterGift;
    }
    const giftByOptions = matchGiftFromText(source);
    if (giftByOptions) result.gift = giftByOptions;

    const campusHit = campusOptions.find(o => {
      const value = String(o.value || "");
      const simple = value.replace(/校区|营地/g, "");
      return source.includes(value) || (!!simple && source.includes(simple));
    });
    if (campusHit) result.campus = campusHit.value;

    if (result.days) result.days = matchOption(result.days, daysOptions, daysOptions.length > 0);
    if (result.campus) result.campus = matchOption(result.campus, campusOptions, campusOptions.length > 0);
    if (form.brand) result.brand = form.brand;
    if (form.report_date && locked.report_date) result.report_date = form.report_date;
    if (form.days && locked.days) result.days = form.days;
    if (form.campus && locked.campus) result.campus = form.campus;

    const tokens = source.split(/\s+/).map(x => x.trim()).filter(Boolean);
    const skip = (token: string) => {
      if (/^\d{1,2}天$/.test(token) || /^[A-Za-z]?\d{1,3}班$/i.test(token)) return true;
      if (/^\d{2,5}$/.test(token)) return true;
      if (normalizeDateText(token)) return true;
      if (result.package_name && token.includes(String(result.package_name))) return true;
      if (result.gift && (token.includes(String(result.gift)) || token.startsWith("送") || token.startsWith("赠"))) return true;
      if (result.campus && (String(result.campus).includes(token) || token.includes(String(result.campus)))) return true;
      return false;
    };
    const nameToken = tokens.find(t => !skip(t));
    if (nameToken) result.child_name = nameToken.replace(/^孩子|^姓名|^名字/, "");
    result.remark = extractRemark(source);
    return result;
  };
  const parseReportSmart = async (line: string) => {
    const fallback = localParseReport(line);
    try {
      const ai = await api.parseReport(line);
      const merged = { ...nonEmpty(ai), ...fallback, raw_text: line };
      const pkg = matchPackageFromText(`${line} ${merged.package_name || ""}`);
      if (pkg) merged.package_name = pkg;
      const gift = matchGiftFromText(`${line} ${merged.gift || ""}`);
      if (gift) merged.gift = gift;
      // 备注只接受原文中带明确标签的内容，避免把未识别字段误塞进备注。
      merged.remark = extractRemark(line);
      return merged;
    } catch {
      return fallback;
    }
  };
  const parse = async () => {
    if (!raw.trim()) {
      toast("先粘贴家长报单信息");
      return;
    }
    setParsing(true);
    try {
      const data = await parseReportSmart(raw);
      setForm(v => mergeLocked({ ...data, raw_text: raw }, v));
      toast("AI 已识别并填入表单，请核对后提交");
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setParsing(false);
    }
  };
  const submit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      const formatted = await api.formatReport({ ...form, sales_name: user.name } as any).catch(() => null);
      await api.createReport({ ...form, raw_text: raw, formatted_text: formatted?.formatted_text });
      toast("报单已提交");
      skipNextDraftSaveRef.current = true;
      clearDraft();
      setForm(v => ({ order_teacher: user.name, brand: locked.brand ? v.brand : "", report_date: locked.report_date ? v.report_date : "", days: locked.days ? v.days : "", campus: locked.campus ? v.campus : "" }));
      setRaw("");
    } catch (e) {
      toast((e as Error).message);
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };
  const copyCurrent = async () => {
    await copyReport({ ...form, user_name: user.name } as OrderReport);
    toast("报单格式已复制");
  };
  const parseBatch = async () => {
    const sources = batchRows.filter(row => row.raw.trim());
    if (!sources.length) return toast("先按一行一单粘贴报单信息");
    setBusy(true);
    const parsed: BatchReportItem[] = [];
    const existingById = new Map(batchItems.map(item => [item.id, item]));
    try {
      for (const source of sources) {
        const data = await parseReportSmart(source.raw);
        const next = mergeLocked({ ...data, raw_text: source.raw }, form, false);
        const existing = existingById.get(source.id);
        if (existing?.remarkTouched) next.remark = existing.fields.remark;
        parsed.push({
          id: source.id,
          raw: source.raw,
          fields: next,
          remarkTouched: Boolean(existing?.remarkTouched),
        });
      }
      setBatchItems(parsed);
      toast(`AI 已识别 ${parsed.length} 条，请核对后再提交`);
    } catch (e) {
      if (parsed.length) setBatchItems(parsed);
      toast(`已识别 ${parsed.length} 条，后面失败：${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  const updateBatchItem = (id: string, key: keyof OrderReport, value: any) => {
    setBatchItems(list => list.map(item => item.id === id ? {
      ...item,
      fields: { ...item.fields, [key]: value },
      remarkTouched: item.remarkTouched || key === "remark",
      submitError: undefined,
    } : item));
  };
  const removeBatchItem = (id: string) => {
    setBatchItems(list => list.filter(item => item.id !== id));
    setBatchRows(list => list.filter(row => row.id !== id));
  };
  const copyBatch = async () => {
    if (!batchItems.length) return toast("请先识别出报单");
    const text = batchItems.map(item => reportText({ ...item.fields, user_name: user.name } as OrderReport)).join("\n\n---\n\n");
    await navigator.clipboard.writeText(text);
    toast(`已复制 ${batchItems.length} 条报单格式`);
  };
  const submitBatch = async () => {
    if (submittingRef.current) return;
    if (!batchItems.length) return toast("请先识别并核对报单");
    submittingRef.current = true;
    setBusy(true);
    // 固定本次提交快照，避免提交过程中编辑或删除造成姓名、备注错位。
    const snapshot = batchItems.map(item => ({ ...item, fields: { ...item.fields } }));
    let okCount = 0;
    const failed: BatchReportItem[] = [];
    try {
      for (const item of snapshot) {
        try {
          const formatted = await api.formatReport({ ...item.fields, sales_name: user.name } as any).catch(() => null);
          await api.createReport({ ...item.fields, raw_text: item.raw, formatted_text: formatted?.formatted_text });
          okCount += 1;
        } catch (e) {
          failed.push({ ...item, submitError: (e as Error).message || "提交失败" });
        }
      }
      setBatchItems(failed);
      setBatchRows(failed.length ? failed.map(item => ({ id: item.id, raw: item.raw })) : [newBatchSourceRow()]);
      if (failed.length) {
        toast(`批量提交完成：成功 ${okCount} 单，失败 ${failed.length} 单；失败订单已保留`);
      } else {
        toast(`批量提交完成：成功 ${okCount} 单`);
        skipNextDraftSaveRef.current = true;
        clearDraft();
      }
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };
  return (
    <>
      <PageTitle title="一键报单" desc="先选择品牌模板，可锁定品牌/日期/校区/天数；AI 只补销售粘贴的信息。" action={<div className="action-row"><button className="secondary" onClick={resetDraft}>清空草稿</button><div className="segmented"><button className={mode === "single" ? "active" : ""} onClick={() => setMode("single")}>单条报单</button><button className={mode === "batch" ? "active" : ""} onClick={() => setMode("batch")}>批量报单</button></div></div>} />
      <div className="panel template-panel">
        <div className="panel-head"><h3>品牌模板与固定字段</h3><span className="hint">锁住后，AI 识别不会覆盖该字段</span></div>
        <div className="form-panel compact-form no-border">
          <LockedSelectField label="夏令营品牌" field="brand" value={form.brand} locked={!!locked.brand} onLock={() => setLocked(v => ({ ...v, brand: !v.brand }))} onChange={applyBrand} options={brands} />
          <LockedSelectField label="报道日期" field="report_date" value={form.report_date} locked={!!locked.report_date} onLock={() => setLocked(v => ({ ...v, report_date: !v.report_date }))} onChange={v => set("report_date", v)} options={brandDateOptions} free />
          <LockedSelectField label="参营天数" field="days" value={form.days} locked={!!locked.days} onLock={() => setLocked(v => ({ ...v, days: !v.days }))} onChange={v => set("days", v)} options={brandDayOptions} free />
          <LockedSelectField label="所在校区" field="campus" value={form.campus} locked={!!locked.campus} onLock={() => setLocked(v => ({ ...v, campus: !v.campus }))} onChange={v => set("campus", v)} options={brandCampusOptions} free />
        </div>
      </div>
      {mode === "single" ? <div className="panel paste-panel">
        <div className="panel-head"><h3>AI 识别单条报单</h3><button className="primary" onClick={parse} disabled={parsing}>{parsing ? <Loader2 className="spin" /> : <Sparkles size={16} />}AI 识别填表</button></div>
        <textarea value={raw} onChange={e => setRaw(e.target.value)} placeholder="例如：孩子小林 A6班 厦门校区 14天 经典款699 赠品相框 7月5日" />
      </div> : <BatchInputPanel rows={batchRows} setRows={setBatchRows} parse={parseBatch} busy={busy} />}
      {mode === "batch" && <BatchConfirmTable items={batchItems} update={updateBatchItem} remove={removeBatchItem} brandOptions={brands} packageOptions={packageOptions} giftOptions={giftOptions} />}
      {mode === "single" && <div className="form-panel compact-form">
        <Field label="孩子姓名" value={form.child_name} onChange={v => set("child_name", v)} />
        <Field label="班级名称" value={form.class_name} onChange={v => set("class_name", v)} />
        <SelectField label="下单款式" value={form.package_name} onChange={v => set("package_name", v)} options={packageOptions} />
        <Field label="下单老师" value={form.order_teacher} onChange={v => set("order_teacher", v)} />
        <Field label="下单金额" value={form.amount} onChange={v => set("amount", v)} />
        <SelectField label="赠品" value={form.gift} onChange={v => set("gift", v)} options={giftOptions} />
        <label className="span2"><span>备注</span><textarea value={form.remark || ""} onChange={e => set("remark", e.target.value)} /></label>
      </div>}
      <div className="footer-actions">{mode === "batch" ? <><button className="secondary" onClick={copyBatch}><Copy size={16} />一键复制所有报单</button><button className="primary" disabled={busy || !batchItems.length} onClick={submitBatch}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}确认提交全部</button></> : <><button className="secondary" onClick={copyCurrent}><Copy size={16} />一键复制报单格式</button><button className="primary" disabled={busy} onClick={submit}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}提交报单</button></>}</div>
    </>
  );
}

function BatchInputPanel({ rows, setRows, parse, busy }: { rows: BatchSourceRow[]; setRows: React.Dispatch<React.SetStateAction<BatchSourceRow[]>>; parse: () => void; busy: boolean }) {
  const inputRefs = useRef<Array<HTMLTextAreaElement | null>>([]);
  const focusRow = (index: number) => setTimeout(() => inputRefs.current[index]?.focus(), 0);
  const update = (id: string, value: string) => setRows(list => list.map(row => row.id === id ? { ...row, raw: value } : row));
  const add = () => {
    setRows(list => {
      focusRow(list.length);
      return [...list, newBatchSourceRow()];
    });
  };
  const insertAfter = (index: number) => {
    setRows(list => {
      const next = [...list];
      next.splice(index + 1, 0, newBatchSourceRow());
      focusRow(index + 1);
      return next;
    });
  };
  const remove = (id: string) => setRows(list => list.length <= 1 ? [newBatchSourceRow()] : list.filter(row => row.id !== id));
  return <div className="panel batch-input-panel">
    <div className="panel-head">
      <div><h3>批量报单</h3><p>一格就是一单；粘贴多行内容会保留在当前单。按 Enter 新增下一单，Shift + Enter 在当前单内换行。</p></div>
      <div className="action-row"><button className="secondary" onClick={add}><Plus size={16} />新增一单</button><button className="primary" onClick={parse} disabled={busy}>{busy ? <Loader2 className="spin" /> : <Sparkles size={16} />}AI 批量识别</button></div>
    </div>
    <div className="batch-lines">
      {rows.map((row, index) => <div className="batch-line" key={row.id}>
        <span>{index + 1}</span>
        <textarea
          ref={node => { inputRefs.current[index] = node; }}
          value={row.raw}
          onChange={e => update(row.id, e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
              e.preventDefault();
              insertAfter(index);
            }
          }}
          placeholder="粘贴一整段报单信息；Enter 新增下一单，Shift + Enter 换行"
        />
        <button className="secondary danger" onClick={() => remove(row.id)}>删除</button>
      </div>)}
    </div>
  </div>;
}

function BatchConfirmTable({ items, update, remove, brandOptions, packageOptions, giftOptions }: { items: BatchReportItem[]; update: (id: string, key: keyof OrderReport, value: any) => void; remove: (id: string) => void; brandOptions: ApiOption[]; packageOptions: ApiOption[]; giftOptions: ApiOption[] }) {
  if (!items.length) return null;
  const fields: { key: keyof OrderReport; label: string }[] = [
    { key: "brand", label: "品牌" },
    { key: "child_name", label: "孩子姓名" },
    { key: "class_name", label: "班级" },
    { key: "report_date", label: "报道日期" },
    { key: "days", label: "天数" },
    { key: "campus", label: "校区" },
    { key: "package_name", label: "款式" },
    { key: "amount", label: "金额" },
    { key: "gift", label: "赠品" },
    { key: "remark", label: "备注" },
  ];
  return <div className="table-card batch-confirm">
    <div className="panel-head"><h3>识别结果确认</h3><span className="hint">可直接修改，确认无误后再提交</span></div>
    <table>
      <thead><tr><th>#</th>{fields.map(f => <th key={f.key}>{f.label}</th>)}<th>操作</th></tr></thead>
      <tbody>{items.map((item, index) => <tr key={item.id}>
        <td>{index + 1}{item.submitError && <small style={{ color: "#dc2626", display: "block", minWidth: 120 }}>失败：{item.submitError}</small>}</td>
        {fields.map(f => <td key={f.key}>
          {f.key === "brand" ? <select value={(item.fields[f.key] as any) || ""} onChange={e => update(item.id, f.key, e.target.value)}><option value="">请选择</option>{brandOptions.map(o => <option key={o.id} value={o.value}>{o.label || o.value}</option>)}</select>
            : f.key === "package_name" ? <select value={(item.fields[f.key] as any) || ""} onChange={e => update(item.id, f.key, e.target.value)}><option value="">请选择</option>{packageOptions.map(o => <option key={o.id} value={o.value}>{o.label || o.value}</option>)}</select>
            : f.key === "gift" ? <select value={(item.fields[f.key] as any) || ""} onChange={e => update(item.id, f.key, e.target.value)}><option value="">请选择</option>{giftOptions.map(o => <option key={o.id} value={o.value}>{o.label || o.value}</option>)}</select>
              : <input value={(item.fields[f.key] as any) || ""} onChange={e => update(item.id, f.key, e.target.value)} />}
        </td>)}
        <td><button className="secondary danger" onClick={() => remove(item.id)}>删除</button></td>
      </tr>)}</tbody>
    </table>
  </div>;
}

function Field({ label, value, onChange, type = "text", disabled = false }: { label: string; value: any; onChange: (v: string) => void; type?: string; disabled?: boolean }) {
  return <label><span>{label}</span><input type={type} value={value || ""} disabled={disabled} onChange={e => onChange(e.target.value)} /></label>;
}

function SelectField({ label, value, onChange, options, free, disabled = false }: { label: string; value: any; onChange: (v: string) => void; options: ApiOption[]; free?: boolean; disabled?: boolean }) {
  if (free) return (
    <label><span>{label}</span><input list={`${label}-list`} value={value || ""} disabled={disabled} onChange={e => onChange(e.target.value)} /><datalist id={`${label}-list`}>{options.map(o => <option key={o.id} value={o.value}>{o.label || o.value}</option>)}</datalist></label>
  );
  return <label><span>{label}</span><select value={value || ""} disabled={disabled} onChange={e => onChange(e.target.value)}><option value="">请选择</option>{options.map(o => <option key={o.id} value={o.value}>{o.label || o.value}</option>)}</select></label>;
}

function LockedField({ label, value, onChange, type = "text", locked, onLock }: { label: string; field: string; value: any; onChange: (v: string) => void; type?: string; locked: boolean; onLock: () => void }) {
  return <label><span>{label}<button type="button" className={`mini-lock ${locked ? "locked" : ""}`} onClick={onLock}>{locked ? "已锁" : "锁定"}</button></span><input type={type} value={value || ""} onChange={e => onChange(e.target.value)} /></label>;
}

function LockedSelectField({ label, value, onChange, options, free, locked, onLock }: { label: string; field: string; value: any; onChange: (v: string) => void; options: ApiOption[]; free?: boolean; locked: boolean; onLock: () => void }) {
  return <label><span>{label}<button type="button" className={`mini-lock ${locked ? "locked" : ""}`} onClick={onLock}>{locked ? "已锁" : "锁定"}</button></span>{free ? <><input list={`${label}-lock-list`} value={value || ""} onChange={e => onChange(e.target.value)} /><datalist id={`${label}-lock-list`}>{options.map(o => <option key={o.id} value={o.value}>{o.label || o.value}</option>)}</datalist></> : <select value={value || ""} onChange={e => onChange(e.target.value)}><option value="">请选择</option>{options.map(o => <option key={o.id} value={o.value}>{o.label || o.value}</option>)}</select>}</label>;
}

function MyReportsPage({ toast }: { toast: (s: string) => void }) {
  const [filters, setFilters] = useState({ status: "", start_at: "", end_at: "", keyword: "" });
  const [editing, setEditing] = useState<OrderReport | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrderReport | null>(null);
  const state = useAsync(() => api.myReports(filters), [JSON.stringify(filters)]);
  const allState = useAsync(() => api.myReports({}), []);
  const rows = allState.data || [];
  const amountOf = (r: OrderReport) => Number(String(r.amount || "0").replace(/[^\d.]/g, "")) || 0;
  const dayKey = (d: Date) => d.toISOString().slice(0, 10);
  const createdKey = (r: OrderReport) => {
    const d = new Date(r.created_at || "");
    return Number.isNaN(d.getTime()) ? "" : dayKey(d);
  };
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const weekStart = new Date(today);
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const inWeek = (r: OrderReport) => {
    const d = new Date(r.created_at || "");
    return !Number.isNaN(d.getTime()) && d >= weekStart;
  };
  const todayRows = rows.filter(r => createdKey(r) === dayKey(today));
  const yesterdayRows = rows.filter(r => createdKey(r) === dayKey(yesterday));
  const weekRows = rows.filter(inWeek);
  const sum = (list: OrderReport[]) => list.reduce((n, r) => n + amountOf(r), 0);
  const set = (k: keyof typeof filters, v: string) => setFilters(f => ({ ...f, [k]: v }));
  const reloadAll = async () => {
    await Promise.all([state.reload(), allState.reload()]);
  };
  const editReport = (r: OrderReport) => {
    setEditing(r);
  };
  return (
    <>
      <PageTitle title="我的报单" desc="查看自己提交的报单进度、金额和核对状态。" action={<button className="secondary" onClick={reloadAll}><RefreshCw size={16} />刷新</button>} />
      <div className="stat-grid report-dashboard">
        <StatCard label="今日订单数" value={todayRows.length} />
        <StatCard label="今日营业额" value={money(sum(todayRows))} />
        <StatCard label="昨日订单数" value={yesterdayRows.length} />
        <StatCard label="昨日营业额" value={money(sum(yesterdayRows))} />
        <StatCard label="本周订单数" value={weekRows.length} />
        <StatCard label="本周营业额" value={money(sum(weekRows))} />
        <StatCard label="未核对" value={rows.filter(r => r.status === "pending_check").length} active={filters.status === "pending_check"} onClick={() => set("status", "pending_check")} />
        <StatCard label="已核对" value={rows.filter(r => r.status === "checked").length} active={filters.status === "checked"} onClick={() => set("status", "checked")} />
        <StatCard label="打回补充" value={rows.filter(r => r.status === "need_more_info" || r.status === "incorrect").length} active={filters.status === "need_more_info"} onClick={() => set("status", "need_more_info")} />
      </div>
      <div className="toolbar dense">
        <select value={filters.status} onChange={e => set("status", e.target.value)}>
          <option value="">全部状态</option>
          <option value="pending_check">未核对</option>
          <option value="checked">已核对</option>
          <option value="need_more_info">打回补充</option>
          <option value="voided">作废</option>
        </select>
        <input type="date" value={filters.start_at} onChange={e => set("start_at", e.target.value)} title="提交开始日期" />
        <input type="date" value={filters.end_at} onChange={e => set("end_at", e.target.value)} title="提交结束日期" />
        <div className="search"><Search size={16} /><input value={filters.keyword} onChange={e => set("keyword", e.target.value)} placeholder="搜索孩子/班级/校区/款式" /></div>
        <button onClick={() => setFilters({ status: "", start_at: "", end_at: "", keyword: "" })}>重置</button>
      </div>
      <ReportTable rows={state.data || []} loading={state.loading} onEdit={editReport} onDeleteRequest={setDeleteTarget} onCopy={r => { copyReport(r); toast("报单信息已复制"); }} />
      {editing && <MyReportEditModal report={editing} close={() => setEditing(null)} done={async msg => { setEditing(null); toast(msg); await reloadAll(); }} />}
      {deleteTarget && <ReportDeleteRequestModal report={deleteTarget} close={() => setDeleteTarget(null)} done={async msg => { setDeleteTarget(null); toast(msg); await reloadAll(); }} />}
    </>
  );
}

type PhotographerUploadQueueItem = {
  id: string;
  taskId: string | number;
  taskTitle: string;
  taskMeta: string;
  file: File;
  note: string;
  status: "pending" | "uploading" | "uploaded" | "failed";
  progress: number;
  error?: string;
};

function PhotographerWorkspacePage({ toast, addToUploadQueue, uploadRefreshKey }: { toast: (s: string) => void; addToUploadQueue: (task: ShootingTask, files: File[], note: string) => void; uploadRefreshKey: number }) {
  const [status, setStatus] = useState("");
  const [sortBy, setSortBy] = useState("urgent");
  const [brand, setBrand] = useState("");
  const [campus, setCampus] = useState("");
  const [packageName, setPackageName] = useState("");
  const [keyword, setKeyword] = useState("");
  const [active, setActive] = useState<ShootingTask | null>(null);
  const [detail, setDetail] = useState<{ task: ShootingTask; uploads?: ShootingUpload[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [uploadingTask, setUploadingTask] = useState<ShootingTask | null>(null);
  const [previewUpload, setPreviewUpload] = useState<ShootingUpload | null>(null);
  const [selectedUploadIds, setSelectedUploadIds] = useState<string[]>([]);
  const activeTaskIdRef = useRef<string | number | null>(null);
  const tasks = useAsync(api.myShootingTasks, []);

  const unique = (values: Array<string | undefined | null>) =>
    Array.from(new Set(values.map(v => String(v || "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, "zh-CN"));
  const rows = tasks.data || [];
  const isShot = (t: ShootingTask) => ["shot", "uploaded", "completed"].includes(String(t.shooting_status || "")) || ["pending_review", "approved"].includes(String(t.review_status || ""));
  const isUploaded = (t: ShootingTask) => ["uploaded", "completed"].includes(String(t.shooting_status || "")) || ["pending_review", "approved", "rejected"].includes(String(t.review_status || ""));
  const isReviewed = (t: ShootingTask) => ["approved", "rejected"].includes(String(t.review_status || ""));
  const isCompleted = (t: ShootingTask) => t.shooting_status === "completed" || t.review_status === "approved";
  const isIssue = (t: ShootingTask) => t.shooting_status === "issue";
  const isUrgent = (t: ShootingTask) => String(t.shooting_requirement || "").startsWith("【提醒】");
  const daysLeft = (value?: string) => {
    if (!value) return null;
    const target = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (Number.isNaN(target.getTime())) return null;
    return Math.ceil((target.getTime() - today.getTime()) / 86400000);
  };
  const nearEnd = (t: ShootingTask) => {
    const left = daysLeft(t.end_date);
    return left !== null && left >= 0 && left <= 2 && !isShot(t);
  };
  const brandOptions = unique(rows.map(t => t.brand));
  const campusOptions = unique(rows.map(t => t.campus));
  const packageOptions = unique(rows.map(t => t.package_name));
  const filtered = rows.filter(t => {
    if (status === "todo" && (isShot(t) || isIssue(t))) return false;
    if (status === "shot" && !isShot(t)) return false;
    if (status === "uploaded" && !isUploaded(t)) return false;
    if (status === "reviewed" && !isReviewed(t)) return false;
    if (status === "urgent" && !isUrgent(t)) return false;
    if (status === "nearEnd" && !nearEnd(t)) return false;
    if (status === "issue" && !isIssue(t)) return false;
    if (status === "completed" && !isCompleted(t)) return false;
    if (brand && t.brand !== brand) return false;
    if (campus && t.campus !== campus) return false;
    if (packageName && t.package_name !== packageName) return false;
    const text = `${t.task_no || ""} ${t.child_name || ""} ${t.brand || ""} ${t.campus || ""} ${t.class_name || ""} ${t.package_name || ""} ${t.special_note || ""}`.toLowerCase();
    return !keyword.trim() || text.includes(keyword.trim().toLowerCase());
  }).sort((a, b) => {
    if (sortBy === "class") return String(a.class_name || "").localeCompare(String(b.class_name || ""), "zh-CN");
    if (sortBy === "campus") return String(a.campus || "").localeCompare(String(b.campus || ""), "zh-CN");
    if (sortBy === "end") return (daysLeft(a.end_date) ?? 9999) - (daysLeft(b.end_date) ?? 9999);
    return Number(isUrgent(b) || nearEnd(b)) - Number(isUrgent(a) || nearEnd(a));
  });
  const openDetail = async (task: ShootingTask) => {
    setActive(task);
    setDetail(null);
    setSelectedUploadIds([]);
    setDetailLoading(true);
    try {
      setDetail(await api.myShootingTaskDetail(task.id));
    } catch (e) {
      toast((e as Error).message);
      setDetail({ task, uploads: [] });
    } finally {
      setDetailLoading(false);
    }
  };
  const refreshActive = async (taskId: string | number) => {
    await tasks.reload();
    try {
      const next = await api.myShootingTaskDetail(taskId);
      setDetail(next);
      const validIds = new Set((next.uploads || []).map(item => String(item.id)));
      setSelectedUploadIds(prev => prev.filter(id => validIds.has(id)));
    } catch {
      // 列表已刷新，详情失败时不挡住操作。
    }
  };
  useEffect(() => {
    activeTaskIdRef.current = (detail?.task || active)?.id ?? null;
  }, [detail?.task?.id, active?.id]);
  useEffect(() => {
    const taskId = activeTaskIdRef.current;
    if (!taskId || !uploadRefreshKey) return;
    void refreshActive(taskId);
  }, [uploadRefreshKey]);
  const markShot = async (task: ShootingTask) => {
    try {
      await api.markMyShootingTaskShot(task.id);
      toast("已标记为已拍摄");
      await refreshActive(task.id);
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const unmarkShot = async (task: ShootingTask) => {
    if (!window.confirm("确定要把这单改回未拍摄吗？改回后会重新出现在待拍任务里。")) return;
    try {
      await api.unmarkMyShootingTaskShot(task.id);
      toast("已改回未拍摄");
      await refreshActive(task.id);
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const markIssue = async (task: ShootingTask) => {
    const note = window.prompt("请填写异常原因，例如：孩子缺席 / 找不到孩子 / 信息错误");
    if (!note?.trim()) return;
    try {
      await api.markMyShootingTaskIssue(task.id, note.trim());
      toast("已反馈异常");
      await refreshActive(task.id);
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const detailUploads = detail?.uploads || [];
  const selectedUploadSet = useMemo(() => new Set(selectedUploadIds), [selectedUploadIds]);
  const deletableUploads = detailUploads.filter(item => item.review_status !== "approved");
  const selectedUploads = detailUploads.filter(item => selectedUploadSet.has(String(item.id)));
  const allDeletableSelected = deletableUploads.length > 0 && deletableUploads.every(item => selectedUploadSet.has(String(item.id)));
  const toggleUploadSelected = (upload: ShootingUpload) => {
    if (upload.review_status === "approved") {
      toast("已审图通过的照片不能删除");
      return;
    }
    const id = String(upload.id);
    setSelectedUploadIds(prev => prev.includes(id) ? prev.filter(value => value !== id) : [...prev, id]);
  };
  const toggleAllUploads = () => {
    if (allDeletableSelected) {
      setSelectedUploadIds([]);
      return;
    }
    setSelectedUploadIds(deletableUploads.map(item => String(item.id)));
  };
  const deleteSelectedUploads = async () => {
    if (!current) return;
    const rows = selectedUploads.filter(item => item.review_status !== "approved");
    if (!rows.length) return toast("请先选择可删除的照片");
    if (!window.confirm(`确定删除选中的 ${rows.length} 张照片吗？删除后需要重新上传。`)) return;
    try {
      for (const item of rows) {
        await api.deleteMyShootingTaskPhoto(current.id, item.id);
      }
      toast(`已删除 ${rows.length} 张照片`);
      setSelectedUploadIds([]);
      await refreshActive(current.id);
    } catch (e) {
      toast((e as Error).message);
      await refreshActive(current.id);
    }
  };
  const current = detail?.task || active;
  return (
    <>
      <PageTitle
        eyebrow="PHOTOGRAPHER"
        title="摄影师工作台"
        desc="电脑端查看自己的拍摄订单，支持筛选、排序、查看详情和标记拍摄状态。"
        action={<button className="secondary" onClick={tasks.reload}><RefreshCw size={16} />刷新</button>}
      />
      <div className="admin-grid photographer-tools">
        <button className="admin-entry active" type="button">
          <Camera />
          <b>我的订单管理</b>
          <span>查看自己的派单、临近结营订单和异常订单</span>
        </button>
      </div>
      <div className="stat-grid shooting-dashboard">
        <StatCard label="全部订单" value={rows.length} active={!status} onClick={() => setStatus("")} />
        <StatCard label="未拍摄" value={rows.filter(t => !isShot(t) && !isIssue(t)).length} active={status === "todo"} onClick={() => setStatus("todo")} />
        <StatCard label="已拍摄" value={rows.filter(isShot).length} active={status === "shot"} onClick={() => setStatus("shot")} />
        <StatCard label="未上传审图" value={rows.filter(t => isShot(t) && !isUploaded(t)).length} active={status === "uploaded"} onClick={() => setStatus("uploaded")} />
        <StatCard label="已审图" value={rows.filter(isReviewed).length} active={status === "reviewed"} onClick={() => setStatus("reviewed")} />
        <StatCard label="加急订单" value={rows.filter(isUrgent).length} active={status === "urgent"} onClick={() => setStatus("urgent")} />
        <button className={`stat-card urgent-stat ${status === "nearEnd" ? "active" : ""}`} type="button" onClick={() => setStatus("nearEnd")}><span>马上结营</span><strong>{rows.filter(nearEnd).length}</strong></button>
        <StatCard label="异常订单" value={rows.filter(isIssue).length} active={status === "issue"} onClick={() => setStatus("issue")} />
      </div>
      <div className="toolbar dense">
        <select value={brand} onChange={e => setBrand(e.target.value)}><option value="">全部品牌</option>{brandOptions.map(v => <option key={v} value={v}>{v}</option>)}</select>
        <select value={campus} onChange={e => setCampus(e.target.value)}><option value="">全部校区</option>{campusOptions.map(v => <option key={v} value={v}>{v}</option>)}</select>
        <select value={packageName} onChange={e => setPackageName(e.target.value)}><option value="">全部款式</option>{packageOptions.map(v => <option key={v} value={v}>{v}</option>)}</select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="urgent">加急/快结营优先</option>
          <option value="end">按最后拍摄日</option>
          <option value="class">按班级排序</option>
          <option value="campus">按校区排序</option>
        </select>
        <input className="grow" value={keyword} onChange={e => setKeyword(e.target.value)} placeholder="搜索孩子/班级/校区/任务号/备注" />
      </div>
      <div className="table-card shooting-progress-table">
        {tasks.loading ? <Loading /> : filtered.length ? (
          <table>
            <thead><tr><th>任务号</th><th>孩子</th><th>品牌</th><th>校区</th><th>班级</th><th>最后拍摄日</th><th>剩余</th><th>拍摄</th><th>审图</th><th>备注</th><th>操作</th></tr></thead>
            <tbody>{filtered.map(t => {
              const left = daysLeft(t.end_date);
              const urgent = nearEnd(t) || isUrgent(t);
              return (
                <tr key={t.id} className={urgent ? "urgent-row" : ""}>
                  <td>{t.task_no || "-"}</td>
                  <td><b>{t.child_name || "-"}</b></td>
                  <td>{t.brand || "-"}</td>
                  <td>{t.campus || "-"}</td>
                  <td>{t.class_name || "-"}</td>
                  <td><b>{t.end_date || "-"}</b></td>
                  <td>{left === null ? "-" : left < 0 ? `超期${Math.abs(left)}天` : `${left}天`}</td>
                  <td><Badge text={isIssue(t) ? "异常" : isShot(t) ? "已拍摄" : "待拍摄"} /></td>
                  <td><Badge text={reviewStatus[t.review_status || ""] || t.review_status || "未上传"} /></td>
                  <td>{t.special_note || t.shooting_requirement || "-"}</td>
                  <td className="row-actions">
                    <button className="secondary" onClick={() => openDetail(t)}>查看详情</button>
                    <button className="primary" onClick={() => setUploadingTask(t)}>上传照片</button>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        ) : <Empty text="暂无我的拍摄订单。管理员派单后，这里会自动出现。" />}
      </div>
      {active && current && (
        <div className="modal-backdrop">
          <div className="modal photographer-detail-modal photographer-detail-redesign">
            <div className="modal-head">
              <div>
                <h2>{current.child_name || "未填孩子"} · {current.class_name || "-"}</h2>
                <p>{current.brand || "-"} / {current.campus || "-"} / {current.package_name || "-"}</p>
              </div>
              <button className="icon-btn" onClick={() => { setActive(null); setDetail(null); }}><X size={18} /></button>
            </div>
            {detailLoading ? <Loading /> : (
              <>
                <div className="shooting-detail-summary">
                  <div className="shooting-summary-main">
                    <span>订单信息</span>
                    <strong>{current.brand || "-"} / {current.campus || "-"}</strong>
                    <p>{current.package_name || "-"} · {current.task_no || "-"}</p>
                  </div>
                  <div><span>最后拍摄日</span><b>{current.end_date || "-"}</b></div>
                  <div><span>剩余时间</span><b className={Number(daysLeft(current.end_date)) < 0 ? "danger-text" : ""}>{daysLeft(current.end_date) === null ? "-" : `${daysLeft(current.end_date)}天`}</b></div>
                  <div><span>金额</span><b>{current.amount || "-"}</b></div>
                  <div><span>拍摄状态</span><b>{shootingStatus[current.shooting_status || ""] || current.shooting_status || "-"}</b></div>
                  <div className="shooting-summary-note"><span>拍摄备注</span><p>{current.special_note || current.shooting_requirement || "无"}</p></div>
                </div>
                <div className="upload-manage-head">
                  <div>
                    <b>已上传照片</b>
                    <span>{detailUploads.length ? `共 ${detailUploads.length} 张，已选 ${selectedUploadIds.length} 张` : "暂无上传照片"}</span>
                  </div>
                  {!!detailUploads.length && (
                    <div className="upload-manage-actions">
                      <button className="secondary" onClick={toggleAllUploads}>{allDeletableSelected ? "取消全选" : "全选可删除"}</button>
                      <button className="secondary danger" disabled={!selectedUploads.length} onClick={deleteSelectedUploads}><Trash2 size={15} />删除选中</button>
                    </div>
                  )}
                </div>
                <div className="upload-grid selectable">
                  {detailUploads.length ? detailUploads.map(item => {
                    const checked = selectedUploadSet.has(String(item.id));
                    const locked = item.review_status === "approved";
                    return (
                      <article key={item.id} className={`upload-card selectable ${checked ? "selected" : ""} ${locked ? "locked" : ""}`}>
                        <label className="upload-select-check" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={checked} disabled={locked} onChange={() => toggleUploadSelected(item)} />
                          <span>{locked ? "已通过" : "选择"}</span>
                        </label>
                        <button type="button" className="upload-preview-button" onClick={() => setPreviewUpload(item)}>
                          {uploadDisplayUrl(item) ? <CachedPreviewImage upload={item} /> : <Image />}
                        </button>
                        <div className="upload-card-footer">
                          <span>{reviewStatus[item.review_status || ""] || item.review_status || "待审"}</span>
                          <small title={item.file_name || ""}>{item.file_name || `照片 ${item.id}`}</small>
                        </div>
                      </article>
                    );
                  }) : <Empty text="暂无上传照片。上传照片建议使用手机 H5，电脑端先做订单管理。" />}
                </div>
              </>
            )}
            <div className="modal-actions">
              <button className="secondary" onClick={() => { setActive(null); setDetail(null); }}>关闭</button>
              <button className="primary" onClick={() => setUploadingTask(current)}>上传照片</button>
              {isShot(current) ? <button className="secondary" onClick={() => unmarkShot(current)}>改回未拍</button> : <button className="primary" onClick={() => markShot(current)}><Check size={16} />标记已拍</button>}
              <button className="secondary danger" onClick={() => markIssue(current)}>反馈异常</button>
            </div>
          </div>
        </div>
      )}
      {uploadingTask && (
          <PhotographerUploadModal
          task={uploadingTask}
          close={() => setUploadingTask(null)}
          addToQueue={(files, note) => {
            addToUploadQueue(uploadingTask, files, note);
            setUploadingTask(null);
          }}
          toast={toast}
        />
      )}
      {previewUpload && (
        <div className="image-preview-backdrop" onClick={() => setPreviewUpload(null)}>
          <button className="icon-btn preview-close" onClick={() => setPreviewUpload(null)}><X size={20} /></button>
        <CachedPreviewImage upload={previewUpload} onClick={e => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}

function PhotographerUploadModal({ task, close, addToQueue, toast }: { task: ShootingTask; close: () => void; addToQueue: (files: File[], note: string) => void; toast: (s: string) => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState("");
  const submit = async () => {
    if (!files.length) {
      toast("请先选择要上传的照片");
      return;
    }
    addToQueue(files, note.trim());
  };

  return (
    <div className="modal-backdrop">
      <div className="modal photographer-upload-modal">
        <div className="modal-head">
          <div>
            <h2>上传拍摄照片</h2>
            <p>{task.child_name || "-"} · {task.class_name || "-"} · {task.brand || "-"} / {task.campus || "-"}</p>
          </div>
          <button className="icon-btn" onClick={close}><X size={18} /></button>
        </div>
        <div className="upload-picker">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={e => {
              setFiles(Array.from(e.target.files || []));
              e.currentTarget.value = "";
            }}
          />
          <span>{files.length ? `已选择 ${files.length} 张照片，加入上传中心后可继续处理其他订单` : "支持 jpg / png / webp，单张不超过 20MB"}</span>
        </div>
        {files.length > 0 && (
          <div className="file-chip-list">
            {files.slice(0, 12).map(file => <span key={`${file.name}-${file.size}-${file.lastModified}`}>{file.name}</span>)}
            {files.length > 12 && <span>还有 {files.length - 12} 张</span>}
          </div>
        )}
        <label className="full-field">
          <span>上传备注</span>
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="可填写：已完成主题照 / 孩子配合情况 / 需要审图注意的地方" />
        </label>
        <p className="hint">加入上传中心后，这些照片会固定上传到当前订单：{task.child_name || "-"} / {task.brand || "-"} / {task.campus || "-"}</p>
        <div className="modal-actions">
          <button className="secondary" onClick={close}>取消</button>
          <button className="primary" disabled={!files.length} onClick={submit}><Check size={16} />加入上传中心</button>
        </div>
      </div>
    </div>
  );
}

function PhotographerUploadCenter({ rows, open, setOpen, retry, clearFinished }: { rows: PhotographerUploadQueueItem[]; open: boolean; setOpen: (v: boolean) => void; retry: (item: PhotographerUploadQueueItem) => void; clearFinished: () => void }) {
  const total = rows.length;
  const uploaded = rows.filter(item => item.status === "uploaded").length;
  const failed = rows.filter(item => item.status === "failed").length;
  const uploading = rows.find(item => item.status === "uploading");
  if (!open) {
    return (
      <button className={`upload-center-fab ${failed ? "has-error" : ""}`} type="button" onClick={() => setOpen(true)}>
        <UploadCloudIcon />
        <span>{uploaded}/{total}</span>
      </button>
    );
  }
  return (
    <aside className="upload-center-panel">
      <div className="upload-center-head">
        <div>
          <b>上传中心</b>
          <span>{uploading ? `正在上传：${uploading.taskTitle}` : failed ? `有 ${failed} 张上传失败` : `已完成 ${uploaded}/${total}`}</span>
        </div>
        <button className="icon-btn" onClick={() => setOpen(false)}><X size={16} /></button>
      </div>
      <div className="upload-center-summary">
        <span>全部 {total}</span>
        <span>完成 {uploaded}</span>
        <span className={failed ? "danger" : ""}>失败 {failed}</span>
      </div>
      <div className="upload-center-list">
        {rows.map((item, index) => (
          <div className={`upload-center-row ${item.status}`} key={item.id}>
            <div className="upload-file-index">{index + 1}</div>
            <div className="upload-file-main">
              <div className="upload-file-title">
                <b title={item.file.name}>{item.file.name}</b>
                <span>{fileSizeText(item.file.size)}</span>
              </div>
              <p>{item.taskTitle} · {item.taskMeta}</p>
              <div className="upload-progress-track"><i style={{ width: `${item.status === "uploaded" ? 100 : item.progress}%` }} /></div>
              {item.error && <small>{item.error}</small>}
            </div>
            <div className="upload-file-status">
              {item.status === "uploaded" && <span className="ok">已上传</span>}
              {item.status === "uploading" && <span className="doing"><Loader2 className="spin" size={14} />{item.progress}%</span>}
              {item.status === "pending" && <span>排队中</span>}
              {item.status === "failed" && <button className="mini-btn danger" onClick={() => retry(item)}><RefreshCw size={13} />重传</button>}
            </div>
          </div>
        ))}
      </div>
      <div className="upload-center-actions">
        <button className="secondary" disabled={!uploaded} onClick={clearFinished}>清除已完成</button>
      </div>
    </aside>
  );
}

function UploadCloudIcon() {
  return <UploadCloud size={18} />;
}

function AdminOverview({ go }: { go: (p: Page) => void }) {
  return (
    <>
      <PageTitle eyebrow="ADMIN CENTER" title="管理员后台" desc="统一管理用户、报单、品牌、摄影派单和审图。" />
      <div className="admin-grid">
        <button onClick={() => go("reports")}><Database />报单管理<span>核对、打回、作废、复制报单</span></button>
        <button onClick={() => go("orderManagement")}><ClipboardCheck />订单管理<span>查看全流程状态、编辑和彻底删除订单</span></button>
        <button onClick={() => go("brandOptions")}><Settings />表单管理<span>维护品牌、校区、天数和赠品下拉</span></button>
        <button onClick={() => go("photographerManagement")}><Users />摄影师管理<span>管理摄影师范围、接单权限和任务数据</span></button>
        <button onClick={() => go("dispatch")}><Camera />摄影师派单<span>按营地/校区分配拍摄任务</span></button>
        <button onClick={() => go("reviewCenter")}><Image />审图中心<span>查看上传图片并审核</span></button>
      </div>
    </>
  );
}

function UsersPage({ go }: { go: (p: Page) => void }) {
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState<ApiUser | null>(null);
  const state = useAsync(() => api.users({ status }), [status]);
  const roles = useAsync(api.roles, []);
  const saveUser = async (u: ApiUser, data: Partial<ApiUser>) => {
    await api.updateUser(u.id, data as Record<string, unknown>);
    await state.reload();
  };
  return (
    <>
      <PageTitle title="用户管理" desc="用户审核、角色和状态管理。每个角色能用哪些功能，请到左侧「角色权限」设置。" action={<div className="action-row"><button className="secondary" onClick={() => go("roles")}><ShieldCheck size={16} />设置角色权限</button><button className="secondary" onClick={() => setEditing({ id: "", name: "", phone: "", role: "user", status: "approved" })}><Plus size={16} />新增用户</button><button className="secondary" onClick={state.reload}><RefreshCw size={16} />刷新</button></div>} />
      <div className="toolbar"><select value={status} onChange={e => setStatus(e.target.value)}><option value="">全部状态</option><option value="pending">待审核</option><option value="approved">已通过</option><option value="disabled">已禁用</option></select></div>
      <div className="table-card">
        <table><thead><tr><th>姓名</th><th>手机号</th><th>角色</th><th>状态</th><th>Token</th><th>最后登录</th><th>操作</th></tr></thead><tbody>
          {state.data?.map(u => <tr key={u.id}>
            <td>{u.name}</td>
            <td>{u.phone}</td>
            <td>
              <select value={u.role} onChange={e => saveUser(u, { role: e.target.value })}>
                {(roles.data || []).map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
              </select>
            </td>
            <td>
              <select value={u.status} onChange={e => saveUser(u, { status: e.target.value })}>
                <option value="pending">待审核</option>
                <option value="approved">已通过</option>
                <option value="rejected">已拒绝</option>
                <option value="disabled">已禁用</option>
              </select>
            </td>
            <td>{u.token_total || 0}</td><td>{cnDate(u.last_login_at)}</td>
            <td className="row-actions"><button onClick={() => setEditing(u)}><Edit3 size={15} />编辑</button></td>
          </tr>)}
        </tbody></table>
        {!state.loading && !state.data?.length && <Empty />}
      </div>
      {editing && <UserEditModal user={editing} roles={roles.data || []} close={() => setEditing(null)} done={async msg => { setEditing(null); await state.reload(); alert(msg); }} />}
    </>
  );
}

function UserEditModal({ user, roles, close, done }: { user: ApiUser; roles: AppRole[]; close: () => void; done: (msg: string) => void }) {
  const isNew = !user.id;
  const [form, setForm] = useState<any>({
    name: user.name,
    phone: user.phone,
    role: user.role || "user",
    status: user.status || "approved",
    password: "",
    h5_sales_enabled: user.h5_sales_enabled ?? true,
    h5_photographer_enabled: user.h5_photographer_enabled ?? user.role === "photographer",
    can_take_shooting_order: user.can_take_shooting_order ?? user.role === "photographer",
  });
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: unknown) => setForm((f: any) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name || !form.phone) return alert("请填写姓名和手机号/账号");
    if (isNew && !form.password) return alert("新增用户需要填写密码");
    setBusy(true);
    try {
      const payload = { ...form };
      if (!isNew && !payload.password) delete payload.password;
      if (isNew) {
        await api.createUser(payload);
        done("用户已新增");
      } else {
        await api.updateUser(user.id, payload);
        done("用户已保存");
      }
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    } finally {
      setBusy(false);
    }
  };
  return <div className="modal-backdrop">
    <div className="modal compact-modal">
      <div className="modal-head"><h2>{isNew ? "新增用户" : "编辑用户"}</h2><button onClick={close}><X /></button></div>
      <div className="form-panel no-border one-col">
        <Field label="姓名" value={form.name} onChange={v => set("name", v)} />
        <Field label="手机号/账号" value={form.phone} onChange={v => set("phone", v)} />
        <label><span>角色</span><select value={form.role} onChange={e => set("role", e.target.value)}>{roles.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}</select></label>
        <label><span>状态</span><select value={form.status} onChange={e => set("status", e.target.value)}><option value="pending">待审核</option><option value="approved">已通过</option><option value="rejected">已拒绝</option><option value="disabled">已禁用</option></select></label>
        <Field label={isNew ? "初始密码" : "重置密码（不改就留空）"} value={form.password} onChange={v => set("password", v)} />
        <div className="user-power-box">
          <div>
            <b>H5 与接单权限</b>
            <span>控制手机端入口，以及是否可以被派摄影订单。</span>
          </div>
          <label><input type="checkbox" checked={!!form.h5_sales_enabled} onChange={e => set("h5_sales_enabled", e.target.checked)} />允许进入 H5 销售端</label>
          <label><input type="checkbox" checked={!!form.h5_photographer_enabled} onChange={e => set("h5_photographer_enabled", e.target.checked)} />允许进入 H5 摄影师端</label>
          <label><input type="checkbox" checked={!!form.can_take_shooting_order} onChange={e => set("can_take_shooting_order", e.target.checked)} />允许接收摄影派单</label>
        </div>
      </div>
      <div className="modal-actions"><button className="secondary" onClick={close}>取消</button><button className="primary" disabled={busy} onClick={save}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}保存</button></div>
    </div>
  </div>;
}

type PermissionFeature = {
  key: string;
  name: string;
  desc: string;
  permissions: string[];
};

const ROLE_PERMISSION_FEATURE_GROUPS: { group: string; items: PermissionFeature[] }[] = [
  {
    group: "\u57fa\u7840\u9875\u9762",
    items: [
      { key: "dashboard", name: "\u5de5\u4f5c\u53f0", desc: "\u8fdb\u5165\u9996\u9875\u5de5\u4f5c\u53f0", permissions: ["dashboard.view"] },
      { key: "settings", name: "\u8bbe\u7f6e", desc: "\u67e5\u770b\u4e2a\u4eba\u8bbe\u7f6e", permissions: ["settings.view"] },
      { key: "profile", name: "\u7f16\u8f91\u4e2a\u4eba\u4fe1\u606f", desc: "\u4fee\u6539\u59d3\u540d\u3001\u624b\u673a\u53f7\u548c\u5bc6\u7801", permissions: ["profile.edit"] },
    ],
  },
  {
    group: "\u9500\u552e\u5de5\u5177",
    items: [
      { key: "h5Sales", name: "H5\uff1a\u9500\u552e\u7aef\u5165\u53e3", desc: "\u5141\u8bb8\u5728\u624b\u673a H5 \u8fdb\u5165\u9500\u552e\u7aef", permissions: ["h5.sales.view"] },
      { key: "training", name: "\u5b9e\u6218\u8bad\u7ec3", desc: "\u8fdb\u5165 AI \u5bb6\u957f\u5b9e\u6218\u8bad\u7ec3", permissions: ["training.use"] },
      { key: "trainingRecords", name: "\u8bad\u7ec3\u8bb0\u5f55", desc: "\u67e5\u770b\u548c\u5bfc\u51fa\u8bad\u7ec3\u8bb0\u5f55", permissions: ["training.record.own.view", "training.record.all.view", "training.record.export"] },
      { key: "salesExam", name: "\u9500\u552e\u6d4b\u8bd5", desc: "\u53c2\u52a0\u9500\u552e\u8003\u6838", permissions: ["exam.use", "sales_test.use"] },
    ],
  },
  {
    group: "\u9500\u552e\u6548\u7387\u5de5\u5177",
    items: [
      { key: "assistant", name: "\u9500\u552e\u5c0f\u52a9\u624b", desc: "\u4f7f\u7528\u9500\u552e\u5c0f\u52a9\u624b\u548c\u56fe\u7247\u8bc6\u522b", permissions: ["assistant.use", "assistant.image"] },
      { key: "scriptLibrary", name: "\u8bdd\u672f\u5e93", desc: "\u67e5\u770b\u548c\u590d\u5236\u8bdd\u672f", permissions: ["script.view", "script.copy"] },
      { key: "materialLibrary", name: "素材库", desc: "查看 H5 和电脑端同步的图片、文案素材", permissions: ["material.view"] },
      { key: "oneReport", name: "\u4e00\u952e\u62a5\u5355", desc: "\u63d0\u4ea4\u65b0\u62a5\u5355", permissions: ["report.create"] },
      { key: "myReports", name: "\u6211\u7684\u62a5\u5355", desc: "\u67e5\u770b\u548c\u4fee\u6539\u81ea\u5df1\u7684\u62a5\u5355", permissions: ["report.own.view", "report.own.edit"] },
      { key: "reportManage", name: "\u62a5\u5355\u7ba1\u7406", desc: "\u67e5\u770b\u3001\u6838\u5bf9\u3001\u6253\u56de\u3001\u4f5c\u5e9f\u548c\u5bfc\u51fa\u62a5\u5355", permissions: ["admin.center.view", "report.all.view", "report.audit", "report.edit", "report.return", "report.void", "report.export", "report.stats.view"] },
      { key: "reportRefund", name: "订单退款", desc: "在订单核对和订单管理中设置退款类型、金额和已退状态", permissions: ["report.refund.manage"] },
      { key: "brandNameCheck", name: "品牌名单核对", desc: "按品牌和日期粘贴名单，快速核对系统是否漏单", permissions: ["report.brand_name_check"] },
      { key: "scriptManage", name: "\u8bdd\u672f\u7ba1\u7406", desc: "\u65b0\u589e\u3001\u7f16\u8f91\u548c\u5220\u9664\u8bdd\u672f", permissions: ["admin.center.view", "script.view", "script.copy", "script.manage"] },
      { key: "materialManage", name: "素材管理", desc: "上传、编辑、停用和删除素材分类与素材", permissions: ["admin.center.view", "material.view", "material.manage"] },
    ],
  },
  {
    group: "\u6444\u5f71\u5e08\u5de5\u5177",
    items: [
      { key: "h5Photographer", name: "H5\uff1a\u6444\u5f71\u5e08\u7aef\u5165\u53e3", desc: "\u5141\u8bb8\u5728\u624b\u673a H5 \u8fdb\u5165\u6444\u5f71\u5e08\u7aef", permissions: ["h5.photographer.view"] },
      { key: "photographerWorkspace", name: "\u6444\u5f71\u5e08\u5de5\u4f5c\u53f0", desc: "\u67e5\u770b\u81ea\u5df1\u7684\u62cd\u6444\u8ba2\u5355\u5e76\u4e0a\u4f20", permissions: ["shooting.task.own.view", "shooting.task.upload"] },
      { key: "photographerManage", name: "\u6444\u5f71\u5e08\u7ba1\u7406", desc: "\u7ba1\u7406\u6444\u5f71\u5e08\u63a5\u5355\u8303\u56f4\u548c\u4efb\u52a1", permissions: ["admin.center.view", "photographer.scope.manage", "shooting.task.assign", "shooting.task.manage", "shooting.task.stats.view"] },
      { key: "dispatch", name: "\u6444\u5f71\u5e08\u6d3e\u5355", desc: "\u7ed9\u6444\u5f71\u5e08\u5206\u914d\u62cd\u6444\u4efb\u52a1", permissions: ["admin.center.view", "shooting.task.assign"] },
      { key: "editTaskPhotographer", name: "\u4fee\u6539\u6444\u5f71\u5e08", desc: "\u5141\u8bb8\u5728\u62cd\u6444\u4efb\u52a1\u9875\u9762\u66f4\u6362\u5df2\u5206\u914d\u7684\u6444\u5f71\u5e08", permissions: ["shooting.task.photographer.edit"] },
      { key: "shootingTasks", name: "\u62cd\u6444\u4efb\u52a1", desc: "\u8ddf\u8fdb\u6240\u6709\u62cd\u6444\u4efb\u52a1", permissions: ["admin.center.view", "shooting.task.all.view", "shooting.task.manage", "shooting.task.export", "shooting.task.stats.view"] },
      { key: "shootingWeather", name: "拍摄天气", desc: "查看未来天气和结营前拍摄风险提醒", permissions: ["admin.center.view", "shooting.weather.view"] },
      { key: "reviewCenter", name: "\u5ba1\u56fe\u4e2d\u5fc3", desc: "\u5ba1\u6838\u6444\u5f71\u5e08\u4e0a\u4f20\u7684\u56fe\u7247", permissions: ["admin.center.view", "shooting.task.review", "shooting.task.stats.view"] },
      { key: "retouchCenter", name: "修图中心", desc: "查看已通过订单，预览并下载修图原图", permissions: ["admin.center.view", "shooting.task.retouch", "shooting.task.stats.view"] },
    ],
  },
  {
    group: "订单履约",
    items: [
      { key: "logisticsView", name: "查看快递管理", desc: "进入快递管理并查看订单关联的快递单号", permissions: ["logistics.view"] },
      { key: "logisticsManage", name: "录入和维护快递单号", desc: "批量导入、绑定、修改和删除快递单号", permissions: ["logistics.manage"] },
      { key: "logisticsRefresh", name: "查询物流轨迹", desc: "允许调用物流服务刷新快递状态和轨迹", permissions: ["logistics.refresh"] },
      { key: "orderProgressView", name: "查看订单进度", desc: "进入订单进度页面并查看全部生产阶段", permissions: ["order.progress.view"] },
      { key: "orderProgressShooting", name: "修改拍摄进度", desc: "在订单进度中确认已拍摄或未拍摄", permissions: ["order.progress.shooting.edit"] },
      { key: "orderProgressRetouch", name: "修改修图进度", desc: "在订单进度中确认已修图或未修图", permissions: ["order.progress.retouch.edit"] },
      { key: "orderProgressLayout", name: "修改排版进度", desc: "在订单进度中确认已排版或未排版", permissions: ["order.progress.layout.edit"] },
      { key: "orderProgressFactory", name: "修改送厂制作进度", desc: "确认订单是否已送厂以及是否完成制作", permissions: ["order.progress.factory.edit"] },
      { key: "orderProgressBatch", name: "批量修改订单进度", desc: "通过名单导入或批量操作更新同一生产阶段", permissions: ["order.progress.batch.edit"] },
    ],
  },
  {
    group: "\u7ba1\u7406",
    items: [
      { key: "adminCenter", name: "\u7ba1\u7406\u5458\u540e\u53f0", desc: "\u770b\u5230\u7ba1\u7406\u7c7b\u83dc\u5355\u5165\u53e3", permissions: ["admin.center.view"] },
      { key: "users", name: "\u7528\u6237\u7ba1\u7406", desc: "\u65b0\u589e\u3001\u5ba1\u6838\u3001\u7f16\u8f91\u548c\u7981\u7528\u7528\u6237", permissions: ["admin.center.view", "user.view", "user.create", "user.edit", "user.delete", "user.approve", "user.disable"] },
      { key: "roles", name: "\u89d2\u8272\u6743\u9650", desc: "\u7ba1\u7406\u89d2\u8272\u548c\u529f\u80fd\u9875\u6743\u9650", permissions: ["admin.center.view", "role.manage"] },
      { key: "orderManageView", name: "订单管理", desc: "查看所有订单的核对、派单、拍摄、审图、下载状态", permissions: ["admin.center.view", "order.manage.view"] },
      { key: "orderManageEdit", name: "编辑订单", desc: "在订单管理里修改订单基础信息，并同步拍摄任务", permissions: ["order.manage.edit"] },
      { key: "orderManageDelete", name: "删除与恢复订单", desc: "将订单移入回收站，并恢复订单及保留的关联数据", permissions: ["order.manage.delete"] },
      { key: "brandOptions", name: "\u8868\u5355\u7ba1\u7406", desc: "\u7ef4\u62a4\u62a5\u5355\u54c1\u724c\u3001\u6821\u533a\u3001\u6b3e\u5f0f\u7b49\u9009\u9879", permissions: ["admin.center.view", "report.option.manage"] },
      { key: "examAdmin", name: "\u8003\u8bd5\u6210\u7ee9", desc: "\u67e5\u770b\u9500\u552e\u8003\u8bd5\u6210\u7ee9\u548c\u7edf\u8ba1", permissions: ["admin.center.view", "exam.all.view", "exam.manage", "exam.regrade", "exam.stats.view", "exam.export"] },
      { key: "modelManage", name: "\u6a21\u578b\u914d\u7f6e", desc: "\u7ba1\u7406 AI \u6a21\u578b\u914d\u7f6e", permissions: ["admin.center.view", "model.manage"] },
      { key: "operationLogs", name: "\u7cfb\u7edf\u65e5\u5fd7", desc: "\u67e5\u770b\u64cd\u4f5c\u65e5\u5fd7", permissions: ["admin.center.view", "operation_log.view"] },
    ],
  },
];

function RolesPage({ toast }: { toast: (s: string) => void }) {
  const roles = useAsync(api.roles, []);
  const permissions = useAsync(api.permissions, []);
  const [activeCode, setActiveCode] = useState("");
  const [draft, setDraft] = useState<AppRole | null>(null);
  const active = draft || (roles.data || []).find(r => r.code === activeCode) || roles.data?.[0] || null;

  useEffect(() => {
    if (!activeCode && roles.data?.length) setActiveCode(roles.data[0].code);
  }, [roles.data, activeCode]);

  useEffect(() => {
    if (!draft && active) setDraft({ ...active, permissions: [...(active.permissions || [])] });
  }, [activeCode, roles.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectRole = (r: AppRole) => {
    setActiveCode(r.code);
    setDraft({ ...r, permissions: [...(r.permissions || [])] });
  };

  const newRole = () => {
    const role: AppRole = { code: "", name: "", description: "", status: "enabled", permissions: [] };
    setActiveCode("");
    setDraft(role);
  };
  const isAdminRole = draft?.code === "admin";

  const toggleFeature = (feature: PermissionFeature) => {
    if (isAdminRole) return;
    setDraft(r => {
      const role = r || { code: "", name: "", permissions: [] };
      const set = new Set(role.permissions || []);
      const checked = feature.permissions.every(code => set.has(code));
      feature.permissions.forEach(code => checked ? set.delete(code) : set.add(code));
      return { ...role, permissions: Array.from(set) };
    });
  };

  const setFeatureGroup = (items: PermissionFeature[], checked: boolean) => {
    if (isAdminRole) return;
    setDraft(r => {
      const role = r || { code: "", name: "", permissions: [] };
      const set = new Set(role.permissions || []);
      items.forEach(x => x.permissions.forEach(code => checked ? set.add(code) : set.delete(code)));
      return { ...role, permissions: Array.from(set) };
    });
  };

  const save = async () => {
    if (isAdminRole) return alert("\u7ba1\u7406\u5458\u9ed8\u8ba4\u62e5\u6709\u5168\u90e8\u6743\u9650\uff0c\u4e0d\u9700\u8981\u4fee\u6539\u3002");
    if (!draft?.code || !draft?.name) return alert("\u8bf7\u586b\u5199\u89d2\u8272\u4ee3\u7801\u548c\u89d2\u8272\u540d\u79f0");
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(draft.code)) return alert("\u89d2\u8272\u4ee3\u7801\u53ea\u80fd\u4f7f\u7528\u82f1\u6587\u5b57\u6bcd\u3001\u6570\u5b57\u548c\u4e0b\u5212\u7ebf\uff0c\u5e76\u4e14\u5fc5\u987b\u4ee5\u5b57\u6bcd\u5f00\u5934");
    try {
      if ((roles.data || []).some(r => r.code === draft.code)) {
        await api.updateRole(draft.code, draft);
        toast("\u89d2\u8272\u6743\u9650\u5df2\u4fdd\u5b58");
      } else {
        await api.createRole(draft);
        toast("\u89d2\u8272\u5df2\u65b0\u589e");
      }
      await roles.reload();
      setActiveCode(draft.code);
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    }
  };

  const remove = async () => {
    if (!draft?.code) return;
    if (isAdminRole) return alert("\u7ba1\u7406\u5458\u662f\u7cfb\u7edf\u6838\u5fc3\u89d2\u8272\uff0c\u4e0d\u80fd\u5220\u9664\u3002");
    if (draft.is_system) return alert("\u7cfb\u7edf\u89d2\u8272\u4e0d\u80fd\u5220\u9664\uff0c\u53ef\u4ee5\u505c\u7528\u6216\u8c03\u6574\u6743\u9650");
    if (!window.confirm("\u786e\u8ba4\u5220\u9664\u89d2\u8272\u300c" + draft.name + "\u300d\uff1f")) return;
    try {
      await api.deleteRole(draft.code);
      toast("\u89d2\u8272\u5df2\u5220\u9664");
      setDraft(null);
      setActiveCode("");
      await roles.reload();
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    }
  };

  const allFeatureCodes = ROLE_PERMISSION_FEATURE_GROUPS.flatMap(group => group.items.flatMap(item => item.permissions));
  const selected = new Set(isAdminRole ? allFeatureCodes : (draft?.permissions || []));
  const isFeatureChecked = (feature: PermissionFeature) => feature.permissions.every(code => selected.has(code));
  const isFeaturePartial = (feature: PermissionFeature) => !isFeatureChecked(feature) && feature.permissions.some(code => selected.has(code));
  const featureClassName = (feature: PermissionFeature) => (isFeatureChecked(feature) ? "checked " : "") + (isFeaturePartial(feature) ? "partial" : "");

  return (
    <>
      <PageTitle title={"\u89d2\u8272\u6743\u9650"} desc={"\u6309\u73b0\u6709\u529f\u80fd\u9875\u914d\u7f6e\u89d2\u8272\u53ef\u7528\u5165\u53e3\uff0c\u5e95\u5c42\u6743\u9650\u4f1a\u81ea\u52a8\u5339\u914d\u3002"} action={<div className="action-row"><button className="secondary" onClick={newRole}><Plus size={16} />{"\u65b0\u589e\u89d2\u8272"}</button><button className="secondary" onClick={roles.reload}><RefreshCw size={16} />{"\u5237\u65b0"}</button></div>} />
      <div className="roles-layout">
        <div className="panel role-list-panel">
          {(roles.data || []).map(r => <button key={r.code} className={draft?.code === r.code ? "active" : ""} onClick={() => selectRole(r)}>
            <b>{r.name}</b>
            <span>{r.code} - {r.user_count || 0} {"\u4eba"}</span>
          </button>)}
          {roles.loading && <Loading />}
        </div>
        <div className="panel role-editor-panel">
          {draft ? <>
            {isAdminRole && <div className="warn-box">{"\u7ba1\u7406\u5458\u9ed8\u8ba4\u62e5\u6709\u5168\u90e8\u529f\u80fd\u6743\u9650\uff0c\u8fd9\u4e2a\u89d2\u8272\u4e0d\u652f\u6301\u53d6\u6d88\u6743\u9650\u6216\u5220\u9664\u3002"}</div>}
            <div className="form-panel compact-form no-border role-basic">
              <Field label={"\u89d2\u8272\u4ee3\u7801"} value={draft.code} disabled={isAdminRole} onChange={v => setDraft(r => ({ ...(r || draft), code: v }))} />
              <Field label={"\u89d2\u8272\u540d\u79f0"} value={draft.name} disabled={isAdminRole} onChange={v => setDraft(r => ({ ...(r || draft), name: v }))} />
              <label><span>{"\u72b6\u6001"}</span><select value={draft.status || "enabled"} disabled={isAdminRole} onChange={e => setDraft(r => ({ ...(r || draft), status: e.target.value }))}><option value="enabled">{"\u542f\u7528"}</option><option value="disabled">{"\u505c\u7528"}</option></select></label>
              <Field label={"\u8bf4\u660e"} value={draft.description || ""} disabled={isAdminRole} onChange={v => setDraft(r => ({ ...(r || draft), description: v }))} />
            </div>
            {permissions.loading ? <Loading /> : permissions.error ? <ErrorBox message={permissions.error} /> : !(permissions.data || []).length ? <Empty text={"\u6682\u65e0\u6743\u9650\u9879\uff0c\u8bf7\u5148\u786e\u8ba4\u540e\u7aef\u5df2\u6267\u884c\u6743\u9650\u521d\u59cb\u5316\u3002"} /> : <div className="permission-groups">
              {ROLE_PERMISSION_FEATURE_GROUPS.map(group => {
                const count = group.items.filter(isFeatureChecked).length;
                return <div className="permission-group" key={group.group}>
                  <div className="permission-group-head">
                    <div><b>{group.group}</b><span>{count}/{group.items.length}</span></div>
                    <div className="action-row">
                      <button className="secondary" disabled={isAdminRole} onClick={() => setFeatureGroup(group.items, true)}>{"\u5168\u9009"}</button>
                      <button className="secondary" disabled={isAdminRole} onClick={() => setFeatureGroup(group.items, false)}>{"\u6e05\u7a7a"}</button>
                    </div>
                  </div>
                  <div className="permission-grid">
                    {group.items.map(item => <label key={item.key} className={featureClassName(item)}>
                      <input type="checkbox" checked={isFeatureChecked(item)} disabled={isAdminRole} onChange={() => toggleFeature(item)} />
                      <span><b>{item.name}</b><small>{item.desc}</small></span>
                    </label>)}
                  </div>
                </div>;
              })}
            </div>}
            <div className="footer-actions">
              <button className="secondary danger" disabled={isAdminRole} onClick={remove}><Trash2 size={16} />{"\u5220\u9664\u89d2\u8272"}</button>
              <button className="primary" disabled={isAdminRole} onClick={save}><Check size={16} />{isAdminRole ? "\u7ba1\u7406\u5458\u5df2\u9501\u5b9a" : "\u4fdd\u5b58\u89d2\u8272\u6743\u9650"}</button>
            </div>
          </> : <Empty text={"\u8bf7\u9009\u62e9\u6216\u65b0\u589e\u4e00\u4e2a\u89d2\u8272"} />}
        </div>
      </div>
    </>
  );
}

function AdminReportsPage({ toast }: { toast: (s: string) => void }) {
  const [filters, setFilters] = useState({ status: "", brand: "", order_teacher: "", report_date: "", days: "", campus: "", keyword: "", start_at: "", end_at: "" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [active, setActive] = useState<OrderReport | null>(null);
  const [logTarget, setLogTarget] = useState<OrderReport | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrderReport | null>(null);
  const [deleteReviewOpen, setDeleteReviewOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [brandCheckOpen, setBrandCheckOpen] = useState(false);
  const stats = useAsync(api.reportStats, []);
  const state = useAsync(() => api.adminReportsPage({ ...filters, page, per_page: perPage }), [JSON.stringify(filters), page, perPage]);
  const options = useAsync(() => api.adminReportOptions(), []);
  const brands = (options.data || []).filter(x => x.option_type === "brand");
  const rows = state.data?.items || [];
  const teachers = state.data?.options?.teachers?.length
    ? state.data.options.teachers
    : Array.from(new Set(rows.map(r => String(r.order_teacher || r.user_name || "").trim()).filter(Boolean))).sort();
  const selectedMeta = brandServiceMeta(options.data || [], filters.brand);
  const allDays = Array.from(new Set((options.data || []).filter(x => x.option_type === "brand_day" || x.option_type === "days").map(x => String(x.value || "")).filter(Boolean)));
  const allCampuses = Array.from(new Set((options.data || []).filter(x => x.option_type === "brand_campus" || x.option_type === "campus" || x.option_type === "city").map(x => String(x.value || "")).filter(Boolean)));
  const daysOptions: string[] = filters.brand && selectedMeta.days.length ? selectedMeta.days.map(String) : allDays;
  const campuses: string[] = filters.brand && selectedMeta.campuses.length ? selectedMeta.campuses.map(String) : allCampuses;
  const set = (k: string, v: string) => { setPage(1); setFilters(f => ({ ...f, [k]: v })); };
  const setBrand = (value: string) => {
    const meta = brandServiceMeta(options.data || [], value);
    setPage(1);
    setFilters(f => ({
      ...f,
      brand: value,
      days: value && meta.days.length && f.days && !meta.days.includes(f.days) ? "" : f.days,
      campus: value && meta.campuses.length && f.campus && !meta.campuses.includes(f.campus) ? "" : f.campus,
    }));
  };
  const isAdmin = api.storedUser()?.role === "admin";
  const canBrandCheck = can(api.storedUser(), "report.brand_name_check");
  return (
    <>
      <PageTitle title={"\u62a5\u5355\u7ba1\u7406"} desc={"\u8868\u683c\u9ad8\u5bc6\u5ea6\u5c55\u793a\uff0c\u6838\u5bf9/\u6253\u56de/\u4f5c\u5e9f\u96c6\u4e2d\u5728\u4e00\u4e2a\u7a97\u53e3\u5b8c\u6210\u3002"} action={<div className="action-row">{isAdmin && <button className="secondary danger" onClick={() => setDeleteReviewOpen(true)}><Trash2 size={16} />{"\u5220\u9664\u5ba1\u6838"}</button>}<button className="secondary" onClick={() => setBatchOpen(true)}><ClipboardCheck size={16} />{"\u6279\u91cf\u6838\u5bf9"}</button>{canBrandCheck && <button className="secondary" onClick={() => setBrandCheckOpen(true)}><Search size={16} />品牌名单核对</button>}<button className="secondary" onClick={state.reload}><RefreshCw size={16} />{"\u5237\u65b0"}</button></div>} />
      <div className="stat-grid six">
        <StatCard label="今日新增" value={stats.data?.today_new ?? "-"} />
        <StatCard label="待核对" value={stats.data?.pending_check ?? "-"} active={filters.status === "pending_check"} onClick={() => set("status", "pending_check")} />
        <StatCard label="需补充" value={stats.data?.need_more_info ?? "-"} active={filters.status === "need_more_info"} onClick={() => set("status", "need_more_info")} />
        <StatCard label="已核对" value={stats.data?.checked ?? "-"} active={filters.status === "checked"} onClick={() => set("status", "checked")} />
        <StatCard label="今日金额" value={money(stats.data?.today_amount)} />
        <StatCard label="本月金额" value={money(stats.data?.month_amount)} />
      </div>
      <div className="toolbar dense">
        <select value={filters.status} onChange={e => set("status", e.target.value)}><option value="">全部状态</option>{Object.entries(reportStatus).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <select value={filters.brand} onChange={e => setBrand(e.target.value)}><option value="">全部品牌</option>{brands.map(b => <option key={b.id} value={b.value}>{b.label || b.value}</option>)}</select>
        <select value={filters.order_teacher} onChange={e => set("order_teacher", e.target.value)}><option value="">全部老师</option>{teachers.map(name => <option key={name} value={name}>{name}</option>)}</select>
        <input type="date" value={filters.report_date} onChange={e => set("report_date", e.target.value)} title="报到日期" />
        <select value={filters.days} onChange={e => set("days", e.target.value)}><option value="">全部天数</option>{daysOptions.map(d => <option key={d} value={d}>{d}</option>)}</select>
        <select value={filters.campus} onChange={e => set("campus", e.target.value)}><option value="">全部校区</option>{campuses.map(c => <option key={c} value={c}>{c}</option>)}</select>
        <input className="grow" value={filters.keyword} onChange={e => set("keyword", e.target.value)} placeholder="搜索孩子/班级/款式" />
        <input type="date" value={filters.start_at} onChange={e => set("start_at", e.target.value)} />
        <input type="date" value={filters.end_at} onChange={e => set("end_at", e.target.value)} />
        <button className="secondary" onClick={() => { setPage(1); setFilters({ status: "", brand: "", order_teacher: "", report_date: "", days: "", campus: "", keyword: "", start_at: "", end_at: "" }); }}>重置</button>
      </div>
      <ReportTable rows={rows} loading={state.loading} onCheck={setActive} onLogs={setLogTarget} onDeleteRequest={setDeleteTarget} onCopy={r => { copyReport(r); toast("报单信息已复制"); }} />
      <PaginationBar page={state.data?.page || page} pages={state.data?.pages || 1} total={state.data?.total || 0} perPage={perPage} onPage={setPage} onPerPage={value => { setPage(1); setPerPage(value); }} />
      {active && <ReportCheckModal report={active} close={() => setActive(null)} done={async msg => { setActive(null); toast(msg); await Promise.all([state.reload(), stats.reload()]); }} />}
      {logTarget && <ReportChangeLogsModal report={logTarget} close={() => setLogTarget(null)} />}
      {deleteTarget && <ReportDeleteRequestModal report={deleteTarget} close={() => setDeleteTarget(null)} done={async msg => { setDeleteTarget(null); toast(msg); await Promise.all([state.reload(), stats.reload()]); }} />}
      {isAdmin && deleteReviewOpen && <ReportDeleteReviewModal close={() => setDeleteReviewOpen(false)} done={async msg => { toast(msg); await Promise.all([state.reload(), stats.reload()]); }} />}
      {batchOpen && <BatchCheckModal rows={rows} close={() => setBatchOpen(false)} done={async msg => { setBatchOpen(false); toast(msg); await Promise.all([state.reload(), stats.reload()]); }} />}
      {brandCheckOpen && <BrandNameCheckModal filters={filters} options={options.data || []} close={() => setBrandCheckOpen(false)} />}
    </>
  );
}

function ReportTable({ rows, loading, onCheck, onEdit, onLogs, onDeleteRequest, onCopy, compact }: { rows: OrderReport[]; loading: boolean; onCheck?: (r: OrderReport) => void; onEdit?: (r: OrderReport) => void; onLogs?: (r: OrderReport) => void; onDeleteRequest?: (r: OrderReport) => void; onCopy?: (r: OrderReport) => void; compact?: boolean }) {
  const hasActions = !compact && (onCheck || onEdit || onLogs || onDeleteRequest || onCopy);
  return (
    <div className="table-card report-table">
      {loading ? <Loading /> : rows.length ? (
        <table>
          <thead><tr><th>品牌</th><th>孩子</th><th>班级</th><th>报道</th><th>天数</th><th>校区</th><th>款式</th><th>老师</th><th>金额</th><th>赠品</th><th>状态</th><th>提交</th>{hasActions && <th>操作</th>}</tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>{r.brand || "-"}</td><td>{r.child_name || "-"}</td><td>{r.class_name || "-"}</td><td>{r.report_date || "-"}</td><td>{r.days || "-"}</td><td>{r.campus || "-"}</td><td>{r.package_name || "-"}</td><td>{r.order_teacher || r.user_name || "-"}</td><td><b>{r.amount || "-"}</b>{r.refund_required && <small className="refund-inline">{refundText(r)}</small>}</td><td>{r.gift || "-"}</td><td><Badge text={reportStatus[r.status || ""] || r.status || "-"} /></td><td>{cnDate(r.created_at)}</td>
                {hasActions && <td className="row-actions report-actions">
                  {onCheck && <button onClick={() => onCheck(r)}>{"\u6838\u5bf9\u8ba2\u5355"}</button>}
                  {onDeleteRequest && <button className="danger" onClick={() => onDeleteRequest(r)}><Trash2 size={15} />{"\u7533\u8bf7\u5220\u9664"}</button>}
                  {onEdit && <button onClick={() => onEdit(r)}><Edit3 size={15} />{"\u7f16\u8f91\u8ba2\u5355"}</button>}
                  {onLogs && <button onClick={() => onLogs(r)}><FileClock size={15} />{"\u4fee\u6539\u8bb0\u5f55"}</button>}
                  {onCopy && <button className="icon-only" onClick={() => onCopy(r)} title={"\u590d\u5236\u6807\u51c6\u62a5\u5355\u683c\u5f0f"}><Copy size={15} /></button>}
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      ) : <Empty text="暂无报单数据" />}
    </div>
  );
}

function LogisticsPage({ toast }: { toast: (s: string) => void }) {
  return <>
    <PageTitle
      title="快递管理"
      desc="批量导入小孩姓名和快递单号，按相册、赠品、补发等类型绑定系统订单。"
    />
    <LogisticsManagementPage toast={toast} />
  </>;
}

function OrderDuplicateComparison({
  groups,
  keepChoices,
  chooseKeep,
  canEdit,
  canDelete,
  view,
  edit,
  remove,
}: {
  groups: OrderDuplicateGroup[];
  keepChoices: Record<string, string | number>;
  chooseKeep: (group: OrderDuplicateGroup, orderId: string | number) => void;
  canEdit: boolean;
  canDelete: boolean;
  view: (order: OrderManagementRow) => void;
  edit: (order: OrderManagementRow) => void;
  remove: (order: OrderManagementRow) => void;
}) {
  const differs = (group: OrderDuplicateGroup, key: string) => group.differences.some(item => item.key === key);
  return <div className="order-duplicate-groups">
    {groups.map(group => {
      const keepId = keepChoices[group.normalized_name] ?? group.recommended_keep_id ?? "";
      return <article className={`order-duplicate-group ${group.risk}`} key={group.normalized_name}>
        <header>
          <div><span className={`order-duplicate-risk ${group.risk}`}>{group.risk_name}</span><h3>{group.name}</h3><small>共 {group.order_count} 笔订单</small></div>
          <div className="order-duplicate-differences"><b>存在差异</b>{group.differences.length ? group.differences.map(item => <span key={item.key}>{item.label}</span>) : <span>关键信息一致</span>}</div>
          <p>系统建议保留：{group.recommendation_reasons.join("、")}。建议只做参考，请人工核对。</p>
        </header>
        <div className="order-duplicate-comparison-grid">
          {group.orders.map(order => {
            const summary = order.status_summary || {};
            const meta = order.duplicate_meta;
            const selectedKeep = String(keepId) === String(order.id);
            return <section key={order.id} className={selectedKeep ? "selected-keep" : ""}>
              <div className="order-duplicate-order-head">
                <label><input type="radio" name={`keep-${group.normalized_name}`} checked={selectedKeep} onChange={() => chooseKeep(group, order.id)} /><span>{selectedKeep ? "准备保留" : "选择保留"}</span></label>
                {String(group.recommended_keep_id) === String(order.id) && <em>系统建议</em>}
              </div>
              <div className="order-duplicate-order-code"><b>{order.report_no || order.id}</b><span>{cnDate(order.created_at)}</span></div>
              <div className="order-duplicate-fields">
                <div className={differs(group, "brand") ? "different" : ""}><span>品牌</span><b>{order.brand || "未填"}</b></div>
                <div className={differs(group, "campus") ? "different" : ""}><span>校区</span><b>{order.campus || "未填"}</b></div>
                <div className={differs(group, "class_name") ? "different" : ""}><span>班级</span><b>{order.class_name || "未填"}</b></div>
                <div className={differs(group, "package_name") ? "different" : ""}><span>款式</span><b>{order.package_name || "未填"}</b></div>
                <div className={differs(group, "parent_name") ? "different" : ""}><span>家长</span><b>{order.parent_name || "未填"}</b></div>
                <div className={differs(group, "phone") ? "different" : ""}><span>电话</span><b>{order.phone || "未填"}</b></div>
                <div className={differs(group, "report_date") ? "different" : ""}><span>报到日期</span><b>{order.report_date || "未填"}</b></div>
                <div className={differs(group, "amount") ? "different" : ""}><span>金额</span><b>{money(order.amount)}</b></div>
              </div>
              <div className="order-duplicate-business-state">
                <span>{summary.report_status_name || reportStatus[order.status || ""] || "未核对"}</span>
                <span>{summary.dispatch_status_name || "未派单"}</span>
                <span>{summary.upload_count || 0} 张图片</span>
                <span>{meta?.shipment_count || 0} 个快递</span>
                <span>{meta?.progress_completed_count || 0}/5 生产进度</span>
                <span>{refundText(order)}</span>
              </div>
              <div className="row-actions order-duplicate-actions">
                <button onClick={() => view(order)}>查看详情</button>
                {canEdit && <button onClick={() => edit(order)}><Edit3 size={14} />编辑</button>}
                {canDelete && !selectedKeep && <button className="danger" onClick={() => remove(order)}><Trash2 size={14} />移入回收站</button>}
              </div>
            </section>;
          })}
        </div>
      </article>;
    })}
  </div>;
}

function OrderManagementPage({ user, toast }: { user: ApiUser; toast: (s: string) => void }) {
  const [section, setSection] = useState<"orders" | "shipments">("orders");
  const [filters, setFilters] = useState({
    keyword: "",
    brand: "",
    campus: "",
    days: "",
    report_status: "",
    dispatch_status: "",
    shooting_status: "",
    review_status: "",
    photographer_id: "",
    start_at: "",
    end_at: "",
    duplicate_names: "",
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [duplicatePerPage, setDuplicatePerPage] = useState(10);
  const [duplicateRisk, setDuplicateRisk] = useState("");
  const [duplicateKeepChoices, setDuplicateKeepChoices] = useState<Record<string, string | number>>({});
  const [detail, setDetail] = useState<OrderManagementRow | null>(null);
  const [editing, setEditing] = useState<OrderManagementRow | null>(null);
  const [deleting, setDeleting] = useState<OrderManagementRow | null>(null);
  const [showDeletedOrders, setShowDeletedOrders] = useState(false);
  const [deletedOrders, setDeletedOrders] = useState<OrderDeletedRecord[] | null>(null);
  const [restoringOrderId, setRestoringOrderId] = useState<string | number | null>(null);
  const [shipmentSummaries, setShipmentSummaries] = useState<Record<string, ShipmentSummaryItem[]>>({});
  const state = useAsync(() => api.adminOrdersPage({ ...filters, page, per_page: perPage }), [JSON.stringify(filters), page, perPage]);
  const duplicateMode = filters.duplicate_names === "true";
  const duplicateState = useAsync(
    () => duplicateMode
      ? api.orderDuplicateGroups({ ...filters, risk: duplicateRisk, page, per_page: duplicatePerPage })
      : Promise.resolve(null),
    [duplicateMode, JSON.stringify(filters), duplicateRisk, page, duplicatePerPage],
  );
  const options = useAsync(() => api.adminReportOptions(), []);
  const photographers = useAsync(api.shootingPhotographers, []);
  const rows = state.data?.items || [];
  const orderIdsKey = rows.map(row => row.id).join(",");
  useEffect(() => {
    if (!orderIdsKey) {
      setShipmentSummaries({});
      return;
    }
    getShipmentSummaries(rows.map(row => row.id)).then(setShipmentSummaries).catch(() => setShipmentSummaries({}));
  }, [orderIdsKey]);
  const canEdit = can(user, "order.manage.edit");
  const canDelete = can(user, "order.manage.delete");
  const brands = (options.data || []).filter(x => x.option_type === "brand");
  const campuses = Array.from(new Set((options.data || []).filter(x => ["brand_campus", "campus", "city"].includes(String(x.option_type))).map(x => x.value).filter(Boolean)));
  const dayOptions = Array.from(new Set((options.data || []).filter(x => ["brand_day", "days"].includes(String(x.option_type))).map(x => String(x.value || "")).filter(Boolean)));
  const pageStats = useMemo(() => {
    const assigned = rows.filter(row => row.status_summary?.dispatch_status === "assigned").length;
    const checked = rows.filter(row => row.status_summary?.checked).length;
    const pendingReview = rows.filter(row => row.status_summary?.review_status === "pending_review").length;
    const approved = rows.filter(row => row.status_summary?.review_status === "approved").length;
    const uploaded = rows.filter(row => Number(row.status_summary?.upload_count || 0) > 0).length;
    return { assigned, checked, pendingReview, approved, uploaded };
  }, [rows]);
  const set = (key: keyof typeof filters, value: string) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  const reset = () => {
    setPage(1);
    setDuplicateRisk("");
    setDuplicateKeepChoices({});
    setFilters({ keyword: "", brand: "", campus: "", days: "", report_status: "", dispatch_status: "", shooting_status: "", review_status: "", photographer_id: "", start_at: "", end_at: "", duplicate_names: "" });
  };
  const openDeletedOrders = async () => {
    setShowDeletedOrders(true);
    setDeletedOrders(null);
    try {
      setDeletedOrders(await api.deletedAdminOrders());
    } catch (error) {
      toast((error as Error).message || "删除记录加载失败");
      setDeletedOrders([]);
    }
  };
  const restoreDeletedOrder = async (order: OrderDeletedRecord) => {
    if (!window.confirm(`确认恢复“${order.child_name || order.report_no || order.id}”的订单吗？\n订单、拍摄任务及保留的关联数据会重新显示。`)) return;
    const value = window.prompt("请填写恢复原因（必填）：", "误删恢复");
    if (value === null) return;
    const reason = value.trim();
    if (!reason) return toast("恢复订单必须填写原因");
    setRestoringOrderId(order.id);
    try {
      await api.restoreAdminOrder(order.id, reason);
      toast("订单及保留的关联数据已恢复");
      setDeletedOrders(await api.deletedAdminOrders());
      setDuplicateKeepChoices({});
      await Promise.all([state.reload(), duplicateState.reload()]);
    } catch (error) {
      toast((error as Error).message || "恢复订单失败");
    } finally {
      setRestoringOrderId(null);
    }
  };
  if (section === "shipments") {
    return <>
      <PageTitle title="快递管理" desc="批量导入小孩姓名和快递单号，按相册、赠品、补发等类型绑定到系统订单。" />
      <LogisticsManagementPage toast={toast} onBack={() => setSection("orders")} />
    </>;
  }
  return (
    <>
      <PageTitle
        title="订单管理"
        desc="从报单、核对、派单、拍摄、审图到下载，一张表看完整流程。"
        action={<div className="action-row"><button className="secondary" onClick={() => setSection("shipments")}><UploadCloud size={16} />快递管理</button>{canDelete && <button className="secondary" onClick={openDeletedOrders}><RotateCcw size={16} />删除记录/恢复</button>}<button className="secondary" onClick={state.reload}><RefreshCw size={16} />刷新</button></div>}
      />
      <div className="order-summary-grid">
        <button className="order-summary-card active" onClick={reset}>
          <span>当前结果</span>
          <strong>{state.data?.total || 0}</strong>
          <small>全部订单</small>
        </button>
        <button className={`order-summary-card ${filters.report_status === "checked" ? "active" : ""}`} onClick={() => set("report_status", filters.report_status === "checked" ? "" : "checked")}>
          <span>已核对</span>
          <strong>{pageStats.checked}</strong>
          <small>当前页</small>
        </button>
        <button className={`order-summary-card ${filters.dispatch_status === "unassigned" ? "active" : ""}`} onClick={() => set("dispatch_status", filters.dispatch_status === "unassigned" ? "" : "unassigned")}>
          <span>未派单</span>
          <strong>{rows.length - pageStats.assigned}</strong>
          <small>需要分配</small>
        </button>
        <button className={`order-summary-card ${filters.review_status === "pending_review" ? "active" : ""}`} onClick={() => set("review_status", filters.review_status === "pending_review" ? "" : "pending_review")}>
          <span>待审图</span>
          <strong>{pageStats.pendingReview}</strong>
          <small>等待审核</small>
        </button>
        <button className={`order-summary-card ${filters.review_status === "approved" ? "active" : ""}`} onClick={() => set("review_status", filters.review_status === "approved" ? "" : "approved")}>
          <span>审图通过</span>
          <strong>{pageStats.approved}</strong>
          <small>可修图下载</small>
        </button>
        <button className={`order-summary-card duplicate ${filters.duplicate_names === "true" ? "active" : ""}`} onClick={() => {
          setDuplicateRisk("");
          setDuplicateKeepChoices({});
          set("duplicate_names", filters.duplicate_names === "true" ? "" : "true");
        }}>
          <span>重复姓名</span>
          <strong>{state.data?.stats?.duplicate_order_count || 0}</strong>
          <small>{state.data?.stats?.duplicate_name_count || 0} 组同名订单</small>
        </button>
        <div className="order-summary-card">
          <span>有图片</span>
          <strong>{pageStats.uploaded}</strong>
          <small>当前页</small>
        </div>
      </div>
      <div className="order-filter-panel">
        <div className="order-search-row">
          <label className="order-search">
            <Search size={18} />
            <input value={filters.keyword} onChange={e => set("keyword", e.target.value)} placeholder="搜索订单号、孩子、家长、电话、班级、款式" />
          </label>
          <button className="secondary" onClick={reset}>重置筛选</button>
        </div>
        <div className="order-filter-grid">
          <select value={filters.brand} onChange={e => set("brand", e.target.value)}><option value="">全部品牌</option>{brands.map(item => <option key={item.id} value={item.value}>{item.label || item.value}</option>)}</select>
          <select value={filters.campus} onChange={e => set("campus", e.target.value)}><option value="">全部校区</option>{campuses.map(item => <option key={item} value={item}>{item}</option>)}</select>
          <select value={filters.days} onChange={e => set("days", e.target.value)}><option value="">全部天数</option>{dayOptions.map(item => <option key={item} value={item}>{item}</option>)}</select>
          <select value={filters.report_status} onChange={e => set("report_status", e.target.value)}><option value="">核对状态</option>{Object.entries(reportStatus).map(([key, value]) => <option key={key} value={key}>{value}</option>)}</select>
          <select value={filters.dispatch_status} onChange={e => set("dispatch_status", e.target.value)}><option value="">派单状态</option><option value="unassigned">未派单</option><option value="assigned">已派单</option></select>
          <select value={filters.shooting_status} onChange={e => set("shooting_status", e.target.value)}><option value="">拍摄状态</option>{Object.entries(shootingStatus).map(([key, value]) => <option key={key} value={key}>{value}</option>)}</select>
          <select value={filters.review_status} onChange={e => set("review_status", e.target.value)}><option value="">审图状态</option>{Object.entries(reviewStatus).map(([key, value]) => <option key={key} value={key}>{value}</option>)}</select>
          <select value={filters.photographer_id} onChange={e => set("photographer_id", e.target.value)}><option value="">全部摄影师</option>{photographers.data?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
          <input type="date" value={filters.start_at} onChange={e => set("start_at", e.target.value)} />
          <input type="date" value={filters.end_at} onChange={e => set("end_at", e.target.value)} />
        </div>
      </div>
      {duplicateMode ? <>
        <div className="order-duplicate-toolbar">
          <div><b>重复订单核对模式</b><span>同名订单已放在同一组内，黄色字段表示内容不一致。</span></div>
          <nav>
            <button className={!duplicateRisk ? "active" : ""} onClick={() => { setPage(1); setDuplicateRisk(""); }}>全部 {(duplicateState.data?.stats.high || 0) + (duplicateState.data?.stats.review || 0) + (duplicateState.data?.stats.possible || 0)} 组</button>
            <button className={duplicateRisk === "high" ? "active high" : "high"} onClick={() => { setPage(1); setDuplicateRisk("high"); }}>高度疑似 {duplicateState.data?.stats.high || 0}</button>
            <button className={duplicateRisk === "review" ? "active review" : "review"} onClick={() => { setPage(1); setDuplicateRisk("review"); }}>需要判断 {duplicateState.data?.stats.review || 0}</button>
            <button className={duplicateRisk === "possible" ? "active possible" : "possible"} onClick={() => { setPage(1); setDuplicateRisk("possible"); }}>可能同名 {duplicateState.data?.stats.possible || 0}</button>
          </nav>
        </div>
        {duplicateState.loading ? <Loading /> : duplicateState.error ? <div className="state-card error"><AlertTriangle /><span>{duplicateState.error}</span><button className="secondary" onClick={duplicateState.reload}>重新加载</button></div> : duplicateState.data?.items.length ? <OrderDuplicateComparison
          groups={duplicateState.data.items}
          keepChoices={duplicateKeepChoices}
          chooseKeep={(group, orderId) => setDuplicateKeepChoices(current => ({ ...current, [group.normalized_name]: orderId }))}
          canEdit={canEdit}
          canDelete={canDelete}
          view={setDetail}
          edit={setEditing}
          remove={setDeleting}
        /> : <Empty text="当前条件下没有需要核对的重复订单" />}
        <PaginationBar page={duplicateState.data?.page || page} pages={duplicateState.data?.pages || 1} total={duplicateState.data?.total_groups || 0} perPage={duplicatePerPage} onPage={setPage} onPerPage={value => { setPage(1); setDuplicatePerPage(Math.min(value, 50)); }} />
      </> : <>
      <div className="table-card order-management-table">
        {state.loading ? <Loading /> : rows.length ? (
          <table>
            <thead><tr><th>订单信息</th><th>学员</th><th>业务信息</th><th>退款</th><th>流程状态</th><th>拍摄/审图</th><th>图片下载</th><th>快递</th><th>操作</th></tr></thead>
            <tbody>{rows.map(row => {
              const summary = row.status_summary || {};
              return <tr key={row.id}>
                <td className="order-code-cell">
                  <b>{row.report_no || row.id}</b>
                  <span>{cnDate(row.created_at)}</span>
                </td>
                <td className="order-person-cell">
                  <b>{row.child_name || "-"}</b>
                  {filters.duplicate_names === "true" && <em className="order-duplicate-badge">同名重复</em>}
                  <span>{row.parent_name || row.phone || "-"}</span>
                  <small>{row.class_name || "-"}</small>
                </td>
                <td>
                  <div className="order-main-info">
                    <b>{row.brand || "-"}</b>
                    <span>{row.campus || "-"}</span>
                    <strong>{row.package_name || "-"} · {money(row.amount)}</strong>
                  </div>
                </td>
                <td><OrderPill text={refundText(row)} tone={row.refund_required ? (row.refund_paid ? "green" : "orange") : "gray"} /></td>
                <td>
                  <div className="order-pill-stack">
                    <OrderPill text={summary.report_status_name || reportStatus[row.status || ""] || "-"} tone={summary.checked ? "green" : "orange"} />
                    <OrderPill text={summary.dispatch_status_name || "-"} tone={summary.dispatch_status === "assigned" ? "green" : "gray"} />
                  </div>
                </td>
                <td>
                  <div className="order-process-cell">
                    <span>{summary.photographer_name || "未分配摄影师"}</span>
                    <div>
                      <OrderPill text={summary.shooting_status_name || "-"} tone={summary.uploaded ? "green" : "gray"} />
                      <OrderPill text={summary.review_status_name || "-"} tone={summary.review_status === "approved" ? "green" : summary.review_status === "rejected" ? "red" : "orange"} />
                    </div>
                  </div>
                </td>
                <td>
                  <div className="order-download-cell">
                    <b>{summary.upload_count || 0} 张</b>
                    <span>{summary.download_status_name || "未下载"}</span>
                    {summary.download_count ? <small>{summary.download_count} 次下载</small> : <small>暂无下载记录</small>}
                  </div>
                </td>
                <td><ShipmentSummary items={shipmentSummaries[String(row.id)] || []} /></td>
                <td className="row-actions order-row-actions">
                  <button onClick={() => setDetail(row)}>查看</button>
                  {canEdit && <button onClick={() => setEditing(row)}><Edit3 size={15} />编辑</button>}
                  {canDelete && <button className="danger" onClick={() => setDeleting(row)}><Trash2 size={15} />删除</button>}
                </td>
              </tr>;
            })}</tbody>
          </table>
        ) : <Empty text="暂无订单数据" />}
      </div>
      <PaginationBar page={state.data?.page || page} pages={state.data?.pages || 1} total={state.data?.total || 0} perPage={perPage} onPage={setPage} onPerPage={value => { setPage(1); setPerPage(value); }} />
      </>}
      {detail && <OrderDetailModal order={detail} close={() => setDetail(null)} />}
      {editing && <OrderEditModal order={editing} close={() => setEditing(null)} done={async msg => { setEditing(null); toast(msg); setDuplicateKeepChoices({}); await Promise.all([state.reload(), duplicateState.reload()]); }} />}
      {deleting && <OrderDeleteModal order={deleting} close={() => setDeleting(null)} done={async msg => { setDeleting(null); toast(msg); setDuplicateKeepChoices({}); await Promise.all([state.reload(), duplicateState.reload()]); }} />}
      {showDeletedOrders && <div className="modal-backdrop">
        <div className="modal order-deleted-modal">
          <div className="modal-head order-modal-head"><div><span className="eyebrow">RECYCLE BIN</span><h2>删除记录与恢复</h2><p>这里只保留新版本的可恢复删除；历史物理删除无法找回。</p></div><button className="icon-btn" onClick={() => setShowDeletedOrders(false)}><X size={18} /></button></div>
          <div className="order-deleted-safe-note"><ShieldCheck size={18} /><span><b>关联数据不会被清空</b><small>恢复订单时会同步恢复本次归档的拍摄任务，快递、进度和图片记录仍保留。</small></span></div>
          <div className="order-deleted-list">
            {deletedOrders === null ? <Loading /> : deletedOrders.length ? deletedOrders.map(order => <article key={order.id}>
              <div><b>{order.child_name || "未填姓名"}</b><span>{order.report_no || order.id}</span><small>{order.brand || "-"} / {order.campus || "-"} / {order.class_name || "-"}</small></div>
              <div><span>删除时间</span><b>{cnDate(order.deleted_at)}</b><small>{order.deleted_by_name || "未知操作人"} · {order.delete_reason || "未填写原因"}</small></div>
              <button disabled={restoringOrderId !== null} onClick={() => restoreDeletedOrder(order)}><RotateCcw size={15} />{restoringOrderId === order.id ? "恢复中…" : "撤回删除"}</button>
            </article>) : <Empty text="暂无可恢复的删除记录" />}
          </div>
        </div>
      </div>}
    </>
  );
}

function OrderDetailModal({ order, close }: { order: OrderManagementRow; close: () => void }) {
  const summary = order.status_summary || {};
  const task = order.shooting_task;
  return (
    <div className="modal-backdrop">
      <div className="modal wide-modal order-detail-modal">
        <div className="modal-head order-modal-head">
          <div>
            <span className="eyebrow">ORDER DETAIL</span>
            <h2>订单详情</h2>
            <p>{order.report_no || order.id} · {order.child_name || "-"} · {order.brand || "-"}</p>
          </div>
          <button className="icon-btn" onClick={close}><X size={18} /></button>
        </div>
        <div className="order-flow-strip">
          <div className={summary.checked ? "done" : ""}><b>核对</b><span>{summary.report_status_name || "-"}</span></div>
          <div className={summary.dispatch_status === "assigned" ? "done" : ""}><b>派单</b><span>{summary.dispatch_status_name || "-"}</span></div>
          <div className={summary.uploaded ? "done" : ""}><b>拍摄</b><span>{summary.shooting_status_name || "-"}</span></div>
          <div className={summary.review_status === "approved" ? "done" : summary.review_status === "rejected" ? "warn" : ""}><b>审图</b><span>{summary.review_status_name || "-"}</span></div>
          <div className={summary.download_count ? "done" : ""}><b>下载</b><span>{summary.download_status_name || "未下载"}</span></div>
        </div>
        <div className="order-detail-sections">
          <section>
            <h3>订单信息</h3>
            <div className="detail-grid order-detail-grid">
              <Info label="孩子姓名" value={order.child_name} />
              <Info label="家长/电话" value={order.parent_name || order.phone} />
              <Info label="班级" value={order.class_name} />
              <Info label="报到日期" value={order.report_date} />
              <Info label="天数" value={order.days} />
              <Info label="销售/下单老师" value={order.order_teacher || order.sales_name} />
            </div>
          </section>
          <section>
            <h3>业务信息</h3>
            <div className="detail-grid order-detail-grid">
              <Info label="品牌" value={order.brand} />
              <Info label="校区" value={order.campus} />
              <Info label="款式" value={order.package_name} />
              <Info label="金额" value={money(order.amount)} />
              <Info label="赠品" value={order.gift} />
              <Info label="付款状态" value={order.payment_status} />
            </div>
          </section>
          <section>
            <h3>退款信息</h3>
            <div className="detail-grid order-detail-grid">
              <Info label="退款状态" value={refundText(order)} />
              <Info label="是否已退" value={order.refund_required ? (order.refund_paid ? "已退" : "未退") : "不退款"} />
              <Info label="退款时间" value={cnDate(order.refund_paid_at)} />
              <Info label="退款备注" value={order.refund_note} />
            </div>
          </section>
          <section>
            <h3>拍摄与下载</h3>
            <div className="detail-grid order-detail-grid">
              <Info label="摄影师" value={summary.photographer_name} />
              <Info label="拍摄任务号" value={task?.task_no} />
              <Info label="图片数量" value={`${summary.upload_count || 0} 张`} />
              <Info label="下载次数" value={`${summary.download_count || 0} 次`} />
              <Info label="下载状态" value={summary.download_status_name} />
              <Info label="任务更新时间" value={cnDate(task?.updated_at)} />
            </div>
          </section>
          <section>
            <h3>快递信息</h3>
            <OrderShipmentPanel orderId={order.id} />
          </section>
        </div>
        <label className="full-field order-note-box"><span>备注</span><textarea value={order.remark || order.order_staff_note || ""} readOnly /></label>
        <div className="modal-actions"><button className="primary" onClick={close}>关闭</button></div>
      </div>
    </div>
  );
}

function OrderPill({ text, tone = "gray" }: { text: React.ReactNode; tone?: "green" | "orange" | "red" | "gray" }) {
  return <span className={`order-pill ${tone}`}>{text || "-"}</span>;
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="info-item"><span>{label}</span><b>{value || "-"}</b></div>;
}

function OrderEditModal({ order, close, done }: { order: OrderManagementRow; close: () => void; done: (msg: string) => void }) {
  const [form, setForm] = useState<Partial<OrderReport>>({
    brand: order.brand || "",
    child_name: order.child_name || "",
    parent_name: order.parent_name || "",
    phone: order.phone || "",
    wechat: order.wechat || "",
    campus: order.campus || "",
    camp_period: order.camp_period || "",
    report_date: order.report_date || "",
    days: order.days || "",
    class_name: order.class_name || "",
    package_name: order.package_name || "",
    amount: order.amount || "",
    payment_status: order.payment_status || "",
    gift: order.gift || "",
    order_teacher: order.order_teacher || "",
    remark: order.remark || "",
    order_staff_note: order.order_staff_note || "",
    status: order.status || "",
    refund_required: Boolean(order.refund_required),
    refund_type: order.refund_type || "none",
    refund_amount: order.refund_amount || "",
    refund_paid: Boolean(order.refund_paid),
    refund_note: order.refund_note || "",
  });
  const [busy, setBusy] = useState(false);
  const canRefund = can(api.storedUser(), "report.refund.manage");
  const set = (key: keyof OrderReport, value: string) => setForm(prev => ({ ...prev, [key]: value }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!canRefund) {
        delete payload.refund_required;
        delete payload.refund_type;
        delete payload.refund_amount;
        delete payload.refund_paid;
        delete payload.refund_note;
      }
      await api.updateAdminOrder(order.id, payload);
      done("订单已保存");
    } catch (e) {
      alert((e as Error).message || "保存失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal wide-modal order-edit-modal">
        <div className="modal-head order-modal-head">
          <div>
            <span className="eyebrow">EDIT ORDER</span>
            <h2>编辑订单</h2>
            <p>{order.report_no || order.id} · 保存后会同步已有拍摄任务</p>
          </div>
          <button className="icon-btn" onClick={close}><X size={18} /></button>
        </div>
        <div className="order-edit-section">
          <h3>学员与联系信息</h3>
          <div className="form-panel compact-form no-border">
            <Field label="孩子姓名" value={form.child_name} onChange={v => set("child_name", v)} />
            <Field label="家长姓名" value={form.parent_name} onChange={v => set("parent_name", v)} />
            <Field label="联系电话" value={form.phone} onChange={v => set("phone", v)} />
            <Field label="微信" value={form.wechat} onChange={v => set("wechat", v)} />
          </div>
        </div>
        <div className="order-edit-section">
          <h3>营地与订单信息</h3>
          <div className="form-panel compact-form no-border">
            <Field label="品牌" value={form.brand} onChange={v => set("brand", v)} />
            <Field label="校区" value={form.campus} onChange={v => set("campus", v)} />
            <Field label="营期" value={form.camp_period} onChange={v => set("camp_period", v)} />
            <Field label="班级" value={form.class_name} onChange={v => set("class_name", v)} />
            <Field label="报到日期" value={form.report_date} onChange={v => set("report_date", v)} />
            <Field label="天数" value={form.days} onChange={v => set("days", v)} />
            <Field label="款式" value={form.package_name} onChange={v => set("package_name", v)} />
            <Field label="金额" value={form.amount} onChange={v => set("amount", v)} />
            <Field label="付款状态" value={form.payment_status} onChange={v => set("payment_status", v)} />
            <Field label="赠品" value={form.gift} onChange={v => set("gift", v)} />
            <Field label="下单老师" value={form.order_teacher} onChange={v => set("order_teacher", v)} />
            <label><span>核对状态</span><select value={form.status || ""} onChange={e => set("status", e.target.value)}>{Object.entries(reportStatus).map(([key, value]) => <option key={key} value={key}>{value}</option>)}</select></label>
          </div>
        </div>
        {canRefund && <div className="order-edit-section">
          <h3>退款信息</h3>
          <div className="form-panel compact-form no-border">
            <RefundFields form={form} set={set as (key: keyof OrderReport, value: any) => void} />
          </div>
        </div>}
        <div className="order-edit-section">
          <h3>备注</h3>
          <div className="form-panel compact-form no-border">
            <label className="full-field"><span>订单备注</span><textarea value={form.remark || ""} onChange={e => set("remark", e.target.value)} /></label>
            <label className="full-field"><span>订单员备注</span><textarea value={form.order_staff_note || ""} onChange={e => set("order_staff_note", e.target.value)} /></label>
          </div>
        </div>
        <div className="modal-actions"><button className="secondary" disabled={busy} onClick={close}>取消</button><button className="primary" disabled={busy} onClick={save}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}保存</button></div>
      </div>
    </div>
  );
}

function OrderDeleteModal({ order, close, done }: { order: OrderManagementRow; close: () => void; done: (msg: string) => void }) {
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const canSubmit = confirm.trim() === "确认删除" && Boolean(reason.trim());
  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.deleteAdminOrder(order.id, reason.trim());
      done("订单已移入回收站，可在删除记录中撤回");
    } catch (e) {
      alert((e as Error).message || "删除失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal compact-modal">
        <div className="modal-head"><div><h2>删除订单</h2><p>{order.report_no || order.id} · {order.child_name || "-"}</p></div><button className="icon-btn" disabled={busy} onClick={close}><X size={18} /></button></div>
        <div className="warn-box">订单将移入回收站并从业务页面隐藏，不会清空快递、进度、拍摄和图片数据；之后可以在“删除记录/恢复”中撤回。</div>
        <Field label="删除原因（必填）" value={reason} onChange={setReason} />
        <Field label="请输入：确认删除" value={confirm} onChange={setConfirm} />
        <div className="modal-actions"><button className="secondary" disabled={busy} onClick={close}>取消</button><button className="secondary danger" disabled={busy || !canSubmit} onClick={submit}>{busy ? <Loader2 className="spin" /> : <Trash2 size={16} />}确认删除</button></div>
      </div>
    </div>
  );
}

const reportAuditActionName: Record<string, string> = {
  create: "创建报单",
  sales_update: "销售修改",
  admin_update: "管理员修改",
  status_check: "核对通过",
  status_return: "打回补充",
  status_void: "作废报单",
  status_change: "状态调整",
  delete_requested: "申请删除",
  delete_approved: "删除通过",
  delete_rejected: "删除驳回",
};

function auditValue(value: unknown) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function ReportChangeLogsModal({ report, close }: { report: OrderReport; close: () => void }) {
  const state = useAsync(() => api.reportChangeLogs(report.id), [report.id]);
  return (
    <div className="modal-backdrop">
      <div className="modal wide-modal">
        <div className="modal-head"><div><h2>修改记录</h2><p>{report.report_no || report.id} · {report.child_name || "-"}</p></div><button onClick={close}><X /></button></div>
        {state.loading ? <Loading /> : state.data?.length ? (
          <div className="table-card">
            <table>
              <thead><tr><th>时间</th><th>操作人</th><th>动作</th><th>变更内容</th><th>备注</th></tr></thead>
              <tbody>{state.data.map((row: ReportChangeLog) => {
                const changes = Object.entries(row.changed_fields || {}).slice(0, 8);
                return <tr key={row.id}>
                  <td>{cnDate(row.created_at)}</td>
                  <td>{row.operator_name || "-"}</td>
                  <td><Badge text={reportAuditActionName[row.action] || row.action} /></td>
                  <td>{changes.length ? changes.map(([field, diff]) => <div key={field}><b>{field}</b>: {auditValue(diff.before)} → {auditValue(diff.after)}</div>) : "-"}</td>
                  <td>{row.note || "-"}</td>
                </tr>;
              })}</tbody>
            </table>
          </div>
        ) : <Empty text="暂无修改记录" />}
        <div className="modal-actions"><button className="secondary" onClick={close}>关闭</button></div>
      </div>
    </div>
  );
}

function ReportDeleteRequestModal({ report, close, done }: { report: OrderReport; close: () => void; done: (msg: string) => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return alert("请填写删除原因");
    setBusy(true);
    try {
      await api.requestReportDelete(report.id, reason.trim());
      done("删除申请已提交，等待管理员审核");
    } catch (e) {
      alert((e as Error).message || "提交失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-head"><div><h2>申请删除报单</h2><p>{report.report_no || report.id} · {report.child_name || "-"}</p></div><button onClick={close}><X /></button></div>
        <div className="warn-box">删除不会立即生效，需要管理员审核通过后才会归档。</div>
        <label><span>删除原因</span><textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="请填写需要删除这张报单的原因" /></label>
        <div className="modal-actions">
          <button className="secondary" onClick={close}>取消</button>
          <button className="danger" disabled={busy} onClick={submit}>{busy ? <Loader2 className="spin" /> : <Trash2 size={16} />}提交申请</button>
        </div>
      </div>
    </div>
  );
}

function ReportDeleteReviewModal({ close, done }: { close: () => void; done: (msg: string) => void }) {
  const state = useAsync(() => api.adminReportDeleteRequests({ status: "pending" }), []);
  const [busy, setBusy] = useState("");
  const review = async (item: ReportDeleteRequest, action: "approve" | "reject") => {
    const note = window.prompt(action === "approve" ? "\u8bf7\u8f93\u5165\u901a\u8fc7\u8bf4\u660e\uff08\u53ef\u9009\uff09" : "\u8bf7\u8f93\u5165\u9a73\u56de\u539f\u56e0\uff08\u53ef\u9009\uff09") || "";
    setBusy(`${action}-${item.id}`);
    try {
      if (action === "approve") await api.approveReportDeleteRequest(item.id, note);
      else await api.rejectReportDeleteRequest(item.id, note);
      await state.reload();
      done(action === "approve" ? "\u5220\u9664\u7533\u8bf7\u5df2\u901a\u8fc7" : "\u5220\u9664\u7533\u8bf7\u5df2\u9a73\u56de");
    } catch (e) {
      alert((e as Error).message || "\u5ba1\u6838\u5931\u8d25\uff0c\u8bf7\u7a0d\u540e\u91cd\u8bd5");
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal wide-modal delete-review-modal">
        <div className="modal-head"><div><h2>{"\u5220\u9664\u5ba1\u6838"}</h2><p>{"\u7ba1\u7406\u5458\u5ba1\u6838\u901a\u8fc7\u540e\uff0c\u62a5\u5355\u4f1a\u8f6f\u5220\u9664\u5f52\u6863\uff0c\u4e0d\u4f1a\u7269\u7406\u5220\u9664\u6570\u636e\u6216\u56fe\u7247\u3002"}</p></div><button onClick={close}><X /></button></div>
        {state.loading ? <Loading /> : state.data?.length ? (
          <div className="table-card delete-review-table">
            <table>
              <thead><tr><th>{"\u7533\u8bf7\u65f6\u95f4"}</th><th>{"\u62a5\u5355\u7f16\u53f7"}</th><th>{"\u54c1\u724c"}</th><th>{"\u5b69\u5b50"}</th><th>{"\u73ed\u7ea7"}</th><th>{"\u62a5\u9053"}</th><th>{"\u5929\u6570"}</th><th>{"\u6821\u533a"}</th><th>{"\u6b3e\u5f0f"}</th><th>{"\u91d1\u989d"}</th><th>{"\u7533\u8bf7\u4eba"}</th><th>{"\u539f\u56e0"}</th><th>{"\u64cd\u4f5c"}</th></tr></thead>
              <tbody>{state.data.map((item: ReportDeleteRequest) => <tr key={item.id}>
                <td>{cnDate(item.requested_at)}</td>
                <td>{item.report?.report_no || item.report_id}</td>
                <td>{item.report?.brand || "-"}</td>
                <td>{item.report?.child_name || "-"}</td>
                <td>{item.report?.class_name || "-"}</td>
                <td>{item.report?.report_date || "-"}</td>
                <td>{item.report?.days || "-"}</td>
                <td>{item.report?.campus || "-"}</td>
                <td>{item.report?.package_name || "-"}</td>
                <td>{item.report?.amount || "-"}</td>
                <td>{item.requested_by_name || "-"}</td>
                <td className="long-cell">{item.request_reason || "-"}</td>
                <td className="row-actions">
                  <button className="danger" disabled={busy === `approve-${item.id}`} onClick={() => review(item, "approve")}>{"\u901a\u8fc7\u5220\u9664"}</button>
                  <button className="secondary" disabled={busy === `reject-${item.id}`} onClick={() => review(item, "reject")}>{"\u9a73\u56de"}</button>
                </td>
              </tr>)}</tbody>
            </table>
          </div>
        ) : <Empty text={"\u6682\u65e0\u5f85\u5ba1\u6838\u5220\u9664\u7533\u8bf7"} />}
        <div className="modal-actions"><button className="secondary" onClick={close}>{"\u5173\u95ed"}</button></div>
      </div>
    </div>
  );
}

function MyReportEditModal({ report, close, done }: { report: OrderReport; close: () => void; done: (msg: string) => void }) {
  const [form, setForm] = useState<Partial<OrderReport>>(report);
  const [busy, setBusy] = useState(false);
  const options = useAsync(() => api.reportOptions(), []);
  const giftOptions = (options.data || []).filter(x => x.option_type === "gift" && x.status !== "disabled");
  const editable = canSalesEditReport(report);
  const set = (key: keyof OrderReport, value: any) => {
    if (!editable) return;
    setForm(v => ({ ...v, [key]: value }));
  };
  const save = async () => {
    if (!editable) {
      alert("订单员已处理的报单只能查看，不能修改，请联系订单员。");
      return;
    }
    setBusy(true);
    try {
      await api.updateMyReport(report.id, form);
      done("报单已更新");
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal wide-modal">
        <div className="modal-head"><div><h2>{editable ? "编辑我的报单" : "查看我的报单"}</h2><p>{report.report_no || report.id} · {reportStatus[report.status || ""] || report.status || "-"}</p></div><button onClick={close}><X /></button></div>
        {!editable && <div className="warn-box">订单员已处理的报单只能查看，不能修改。如需调整，请联系订单员处理。</div>}
        <div className="form-panel compact-form no-border">
          <Field label="夏令营品牌" value={form.brand} onChange={v => set("brand", v)} disabled={!editable} />
          <Field label="孩子姓名" value={form.child_name} onChange={v => set("child_name", v)} disabled={!editable} />
          <Field label="班级名称" value={form.class_name} onChange={v => set("class_name", v)} disabled={!editable} />
          <Field label="报道日期" type="date" value={form.report_date} onChange={v => set("report_date", v)} disabled={!editable} />
          <Field label="参营天数" value={form.days} onChange={v => set("days", v)} disabled={!editable} />
          <Field label="所在校区" value={form.campus} onChange={v => set("campus", v)} disabled={!editable} />
          <Field label="下单款式" value={form.package_name} onChange={v => set("package_name", v)} disabled={!editable} />
          <Field label="下单老师" value={form.order_teacher} onChange={v => set("order_teacher", v)} disabled={!editable} />
          <Field label="下单金额" value={form.amount} onChange={v => set("amount", v)} disabled={!editable} />
          <SelectField label="赠品" value={form.gift} onChange={v => set("gift", v)} options={giftOptions} disabled={!editable} />
          <label className="span2"><span>备注</span><textarea value={form.remark || ""} disabled={!editable} onChange={e => set("remark", e.target.value)} /></label>
        </div>
        <div className="modal-actions">
          <button className="secondary" onClick={() => copyReport(form as OrderReport)}>复制报单信息</button>
          <button className="secondary" onClick={close}>关闭</button>
          {editable && <button className="primary" disabled={busy} onClick={save}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}保存修改</button>}
        </div>
      </div>
    </div>
  );
}

function ReportCheckModal({ report, close, done }: { report: OrderReport; close: () => void; done: (msg: string) => void }) {
  const [form, setForm] = useState<Partial<OrderReport>>(report);
  const [note, setNote] = useState(report.order_staff_note || "");
  const [busy, setBusy] = useState("");
  const options = useAsync(() => api.adminReportOptions(), []);
  const canRefund = can(api.storedUser(), "report.refund.manage");
  const warnings = reportCheckWarnings(form, options.data || []);
  const lastDay = calcEndDate(String(form.report_date || ""), String(form.days || ""));
  const set = (key: keyof OrderReport, value: any) => setForm(v => ({ ...v, [key]: value }));
  const saveAnd = async (action: "check" | "return" | "void") => {
    if (action === "check" && warnings.some(w => w.level === "error")) {
      alert(`订单核对内容需要修改：\n${warnings.filter(w => w.level === "error").map(w => `- ${w.text}`).join("\n")}`);
      return;
    }
    if (form.refund_required && Number(form.refund_amount || 0) > Number(form.amount || 0)) {
      alert("退款金额不能大于订单金额");
      return;
    }
    setBusy(action);
    try {
      await api.updateReport(report.id, form);
      await api.reportAction(report.id, action, note);
      done(action === "check" ? "订单已核对" : action === "return" ? "订单已打回补充" : "订单已作废");
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal wide-modal">
        <div className="modal-head"><div><h2>核对订单</h2><p>{report.report_no || report.id} · {cnDate(report.created_at)}</p></div><button onClick={close}><X /></button></div>
        <div className={`shooting-day-card ${lastDay ? "ok" : "warn"}`}>
          <div><span>最后拍摄日</span><b>{lastDay || "无法计算"}</b></div>
          <p>{lastDay ? "按“报到日期 + 参营天数 - 1天”计算，后续摄影派单会使用这个日期判断拍摄剩余时间。" : "报道日期或参营天数格式不对，请先修改，否则不能确认核对。"}</p>
        </div>
        {warnings.length > 0 && <div className="warn-box">{warnings.map(w => <div key={w.text}>{w.level === "error" ? "⚠" : "提示"} {w.text}</div>)}</div>}
        <div className="form-panel compact-form no-border">
          <ServiceLinkedReportFields form={form} set={set} options={options.data || []} />
          {canRefund && <RefundFields form={form} set={set} />}
          <label className="span2"><span>备注</span><textarea value={form.remark || ""} onChange={e => set("remark", e.target.value)} /></label>
          <label className="span2"><span>订单员备注/打回原因</span><textarea value={note} onChange={e => setNote(e.target.value)} /></label>
        </div>
        <div className="modal-actions">
          <button className="secondary" onClick={close}>关闭</button>
          <button className="secondary danger" disabled={busy === "void"} onClick={() => saveAnd("void")}>作废</button>
          <button className="secondary" disabled={busy === "return"} onClick={() => saveAnd("return")}>打回补充</button>
          <button className="primary" disabled={busy === "check"} onClick={() => saveAnd("check")}>确认核对</button>
        </div>
      </div>
    </div>
  );
}

function ServiceLinkedReportFields({ form, set, options, compact }: { form: Partial<OrderReport>; set: (key: keyof OrderReport, value: any) => void; options: ApiOption[]; compact?: boolean }) {
  const brands = options.filter(x => x.option_type === "brand");
  const giftOptions = options.filter(x => x.option_type === "gift" && x.status !== "disabled");
  const meta = brandServiceMeta(options, form.brand);
  const dayOptions = withCurrent(meta.days, form.days);
  const campusOptions = withCurrent(meta.campuses, form.campus);
  const dateOptions = withCurrent(meta.report_dates, form.report_date);
  const applyBrand = (brand: string) => {
    set("brand", brand);
    const next = brandServiceMeta(options, brand);
    if (next.days.length === 1) set("days", next.days[0]);
    if (next.campuses.length === 1) set("campus", next.campuses[0]);
    if (next.report_dates.length === 1) set("report_date", next.report_dates[0]);
  };
  return (
    <>
      <label><span>夏令营品牌</span><select value={form.brand || ""} onChange={e => applyBrand(e.target.value)}><option value="">请选择品牌</option>{brands.map(b => <option key={b.id} value={b.value}>{b.label || b.value}</option>)}</select></label>
      <Field label="孩子姓名" value={form.child_name} onChange={v => set("child_name", v)} />
      <Field label="班级名称" value={form.class_name} onChange={v => set("class_name", v)} />
      <label><span>报道日期</span>{dateOptions.length ? <select value={form.report_date || ""} onChange={e => set("report_date", e.target.value)}><option value="">请选择报道日期</option>{dateOptions.map(v => <option key={v} value={v}>{v}</option>)}</select> : <input type="date" value={form.report_date || ""} onChange={e => set("report_date", e.target.value)} />}</label>
      <label><span>参营天数</span>{dayOptions.length ? <select value={form.days || ""} onChange={e => set("days", e.target.value)}><option value="">请选择天数</option>{dayOptions.map(v => <option key={v} value={v}>{v}</option>)}</select> : <input value={form.days || ""} onChange={e => set("days", e.target.value)} placeholder="如 14天" />}</label>
      <label><span>所在校区</span>{campusOptions.length ? <select value={form.campus || ""} onChange={e => set("campus", e.target.value)}><option value="">请选择校区</option>{campusOptions.map(v => <option key={v} value={v}>{v}</option>)}</select> : <input value={form.campus || ""} onChange={e => set("campus", e.target.value)} />}</label>
      {!compact && <Field label="下单款式" value={form.package_name} onChange={v => set("package_name", v)} />}
      {!compact && <Field label="下单老师" value={form.order_teacher} onChange={v => set("order_teacher", v)} />}
      {!compact && <Field label="下单金额" value={form.amount} onChange={v => set("amount", v)} />}
      {!compact && <SelectField label="赠品" value={form.gift} onChange={v => set("gift", v)} options={giftOptions} />}
    </>
  );
}

function BatchCheckModal({ rows, close, done }: { rows: OrderReport[]; close: () => void; done: (msg: string) => void }) {
  const options = useAsync(() => api.adminReportOptions(), []);
  const [keyword, setKeyword] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>(() => Object.fromEntries(rows.filter(r => r.status !== "checked" && r.status !== "voided").map(r => [String(r.id), true])));
  const [forms, setForms] = useState<Record<string, Partial<OrderReport>>>(() => Object.fromEntries(rows.map(r => [String(r.id), { ...r }])));
  const [busy, setBusy] = useState(false);
  const filtered = rows.filter(r => {
    const form = forms[String(r.id)] || r;
    const text = [form.brand, form.child_name, form.class_name, form.campus, form.package_name, form.order_teacher].join(" ");
    return !keyword || text.toLowerCase().includes(keyword.toLowerCase());
  });
  const selectedRows = rows.filter(r => selected[String(r.id)]);
  const update = (id: string | number, key: keyof OrderReport, value: any) => setForms(v => ({ ...v, [String(id)]: { ...(v[String(id)] || {}), [key]: value } }));
  const warningsOf = (r: OrderReport) => reportCheckWarnings(forms[String(r.id)] || r, options.data || []);
  const validSelected = selectedRows.filter(r => !warningsOf(r).some(w => w.level === "error"));
  const invalidSelected = selectedRows.length - validSelected.length;
  const giftOptions = (options.data || []).filter(o => o.option_type === "gift" && o.status !== "disabled");
  const selectVisible = (checked: boolean) => setSelected(v => ({ ...v, ...Object.fromEntries(filtered.map(r => [String(r.id), checked])) }));
  const submit = async () => {
    if (!selectedRows.length) return alert("请先选择要核对的订单");
    if (invalidSelected > 0) {
      alert(`有 ${invalidSelected} 条订单格式不对，请先根据红色提醒修改后再批量核对。`);
      return;
    }
    setBusy(true);
    try {
      for (const row of validSelected) {
        const form = forms[String(row.id)] || row;
        await api.updateReport(row.id, form);
        await api.reportAction(row.id, "check", "批量核对通过");
      }
      done(`已批量核对 ${validSelected.length} 条订单`);
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    } finally {
      setBusy(false);
    }
  };
  return <div className="modal-backdrop">
    <div className="modal wide-modal batch-check-modal">
      <div className="modal-head"><div><h2>批量核对订单</h2><p>按表格快速核对。格式不对的订单会提示，不能直接通过。</p></div><button onClick={close}><X /></button></div>
      <div className="toolbar dense batch-check-toolbar">
        <input className="grow" value={keyword} onChange={e => setKeyword(e.target.value)} placeholder="搜索品牌/孩子/班级/校区/款式/老师" />
        <button className="secondary" onClick={() => selectVisible(true)}>选择当前列表</button>
        <button className="secondary" onClick={() => selectVisible(false)}>取消当前列表</button>
        <span>已选 {selectedRows.length} · 可核对 {validSelected.length} · 需修改 {invalidSelected}</span>
      </div>
      <div className="batch-check-list">
        {options.loading ? <Loading /> : filtered.length ? filtered.map((row, index) => {
            const form = forms[String(row.id)] || row;
            const warn = reportCheckWarnings(form, options.data || []);
            const lastDay = calcEndDate(String(form.report_date || ""), String(form.days || ""));
            const meta = brandServiceMeta(options.data || [], form.brand);
            return <article key={row.id} className={`batch-check-card ${warn.some(w => w.level === "error") ? "has-error" : ""}`}>
              <div className="batch-card-head">
                <label className="batch-check-pick"><input type="checkbox" checked={Boolean(selected[String(row.id)])} onChange={e => setSelected(v => ({ ...v, [String(row.id)]: e.target.checked }))} /><b>#{index + 1}</b></label>
                <span>{row.report_no || row.id}</span>
                <strong>{form.child_name || "未填孩子"}</strong>
                <em>最后拍摄日：{lastDay || "无法计算"}</em>
              </div>
              <div className="batch-card-grid">
                <label><span>孩子</span><input value={form.child_name || ""} onChange={e => update(row.id, "child_name", e.target.value)} /></label>
                <label><span>品牌</span><select value={form.brand || ""} onChange={e => update(row.id, "brand", e.target.value)}><option value="">品牌</option>{options.data?.filter(o => o.option_type === "brand").map(o => <option key={o.id} value={o.value}>{o.label || o.value}</option>)}</select></label>
                <label><span>报道日期</span>{meta.report_dates.length ? <select value={form.report_date || ""} onChange={e => update(row.id, "report_date", e.target.value)}><option value="">日期</option>{withCurrent(meta.report_dates, form.report_date).map(v => <option key={v} value={v}>{v}</option>)}</select> : <input type="date" value={form.report_date || ""} onChange={e => update(row.id, "report_date", e.target.value)} />}</label>
                <label><span>天数</span>{meta.days.length ? <select value={form.days || ""} onChange={e => update(row.id, "days", e.target.value)}><option value="">天数</option>{withCurrent(meta.days, form.days).map(v => <option key={v} value={v}>{v}</option>)}</select> : <input value={form.days || ""} onChange={e => update(row.id, "days", e.target.value)} />}</label>
                <label><span>校区</span>{meta.campuses.length ? <select value={form.campus || ""} onChange={e => update(row.id, "campus", e.target.value)}><option value="">校区</option>{withCurrent(meta.campuses, form.campus).map(v => <option key={v} value={v}>{v}</option>)}</select> : <input value={form.campus || ""} onChange={e => update(row.id, "campus", e.target.value)} />}</label>
                <label><span>班级</span><input value={form.class_name || ""} onChange={e => update(row.id, "class_name", e.target.value)} /></label>
                <label><span>款式</span><input value={form.package_name || ""} onChange={e => update(row.id, "package_name", e.target.value)} /></label>
                <label><span>金额</span><input value={form.amount || ""} onChange={e => update(row.id, "amount", e.target.value)} /></label>
                <label><span>赠品</span><select value={form.gift || ""} onChange={e => update(row.id, "gift", e.target.value)}><option value="">赠品</option>{giftOptions.map(o => <option key={o.id} value={o.value}>{o.label || o.value}</option>)}</select></label>
                <label className="batch-remark"><span>备注</span><input value={form.remark || ""} onChange={e => update(row.id, "remark", e.target.value)} /></label>
              </div>
              {warn.length > 0 && <div className="batch-card-warn">{warn.map(w => <span key={w.text}>{w.level === "error" ? "⚠" : "提示"} {w.text}</span>)}</div>}
            </article>;
          }) : <Empty text="没有匹配的订单" />}
      </div>
      <div className="modal-actions"><button className="secondary" onClick={close}>关闭</button><button className="primary" disabled={busy} onClick={submit}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}确认批量核对</button></div>
    </div>
  </div>;
}

function BrandNameCheckModal({ filters, options, close }: { filters: Record<string, string>; options: ApiOption[]; close: () => void }) {
  const brands = options.filter(x => x.option_type === "brand");
  const campuses = Array.from(new Set(options.filter(x => ["brand_campus", "campus", "city"].includes(String(x.option_type))).map(x => x.value).filter(Boolean)));
  const [form, setForm] = useState({
    brand: filters.brand || "",
    report_date: filters.report_date || "",
    campus: filters.campus || "",
    status: filters.status || "checked",
    names_text: "",
  });
  const [result, setResult] = useState<BrandNameCheckResult | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (key: keyof typeof form, value: string) => setForm(prev => ({ ...prev, [key]: value }));
  const run = async () => {
    if (!form.brand) return alert("请先选择品牌");
    if (!form.names_text.trim()) return alert("请粘贴品牌名单，一行一个孩子姓名");
    setBusy(true);
    try {
      const data = await api.brandNameCheck(form);
      setResult(data);
    } catch (e) {
      alert((e as Error).message || "核对失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  };
  const copyMissing = () => {
    const text = (result?.missing || []).map(item => item.name).join("\n");
    navigator.clipboard.writeText(text || "").then(() => alert("缺失名单已复制"));
  };
  return (
    <div className="modal-backdrop">
      <div className="modal wide-modal brand-check-modal">
        <div className="modal-head">
          <div><h2>品牌名单核对</h2><p>按品牌、日期和姓名快速核对数量，主要用于检查漏单。</p></div>
          <button onClick={close}><X /></button>
        </div>
        <div className="brand-check-layout">
          <section className="brand-check-input">
            <div className="form-panel compact-form no-border">
              <label><span>品牌</span><select value={form.brand} onChange={e => set("brand", e.target.value)}><option value="">请选择品牌</option>{brands.map(b => <option key={b.id} value={b.value}>{b.label || b.value}</option>)}</select></label>
              <label><span>报道日期</span><input type="date" value={form.report_date} onChange={e => set("report_date", e.target.value)} /></label>
              <label><span>校区</span><select value={form.campus} onChange={e => set("campus", e.target.value)}><option value="">全部校区</option>{campuses.map(c => <option key={c} value={c}>{c}</option>)}</select></label>
              <label><span>系统订单状态</span><select value={form.status} onChange={e => set("status", e.target.value)}><option value="">全部订单</option><option value="checked">已核对</option><option value="pending_check">待核对</option><option value="need_more_info">需补充</option><option value="voided">已作废</option></select></label>
              <label className="span2"><span>品牌名单</span><textarea value={form.names_text} onChange={e => set("names_text", e.target.value)} placeholder="一行一个孩子姓名，也可以直接从 Excel 复制一列粘贴进来" /></label>
            </div>
            <div className="modal-actions"><button className="secondary" onClick={close}>关闭</button><button className="primary" disabled={busy} onClick={run}>{busy ? <Loader2 className="spin" /> : <Search size={16} />}开始核对</button></div>
          </section>
          <section className="brand-check-result">
            {!result ? <Empty text="粘贴名单后点击开始核对，这里会显示匹配、缺失和多出的名单。" /> : <>
              <div className="brand-check-stats">
                <div><span>品牌名单</span><b>{result.summary.imported_count}</b><small>{result.summary.imported_unique_count} 个姓名</small></div>
                <div><span>系统订单</span><b>{result.summary.system_count}</b><small>{result.summary.system_unique_count} 个姓名</small></div>
                <div><span>已匹配</span><b>{result.summary.matched_count}</b><small>姓名一致</small></div>
                <div className={result.summary.missing_count ? "warn" : ""}><span>品牌有系统无</span><b>{result.summary.missing_count}</b><small>重点排查</small></div>
                <div><span>系统多出</span><b>{result.summary.extra_count}</b><small>可能多报</small></div>
                <div className={result.summary.duplicate_count ? "warn" : ""}><span>重复姓名</span><b>{result.summary.duplicate_count}</b><small>人工确认</small></div>
              </div>
              <BrandCheckGroup title="品牌有，系统没有" rows={result.missing} danger action={result.missing.length ? <button className="secondary" onClick={copyMissing}>复制缺失名单</button> : null} />
              <BrandCheckGroup title="系统有，品牌没有" rows={result.extra} />
              <BrandCheckGroup title="重复姓名" rows={result.duplicates} />
              <BrandCheckGroup title="已匹配" rows={result.matched} compact />
            </>}
          </section>
        </div>
      </div>
    </div>
  );
}

function BrandCheckGroup({ title, rows, danger, compact, action }: { title: string; rows: BrandNameCheckRow[]; danger?: boolean; compact?: boolean; action?: React.ReactNode }) {
  return (
    <div className={`brand-check-group ${danger ? "danger" : ""}`}>
      <div className="brand-check-group-head"><b>{title}</b><span>{rows.length} 条</span>{action}</div>
      {rows.length ? <div className="brand-check-list">
        {rows.slice(0, compact ? 30 : 120).map((row, index) => <article key={`${title}-${row.name}-${index}`}>
          <b>{row.name}</b>
          <span>名单 {row.imported_count || 0} / 系统 {row.system_count || 0}</span>
          {!!row.orders?.length && <small>{row.orders.map(order => `${order.report_no || order.id} ${order.campus || ""} ${order.class_name || ""} ${order.package_name || ""}`).join("；")}</small>}
        </article>)}
        {rows.length > (compact ? 30 : 120) && <p className="muted">还有 {rows.length - (compact ? 30 : 120)} 条未展开，可缩小筛选范围查看。</p>}
      </div> : <Empty text="暂无" />}
    </div>
  );
}

function splitOptionValues(text?: string) {
  return String(text || "").split(/[，,、\s\n]+/).map(x => x.trim()).filter(Boolean);
}

function brandServiceMeta(options: ApiOption[], brand?: string | number | null) {
  const value = String(brand || "");
  const card = options.find(x => x.option_type === "brand" && x.value === value);
  const childDays = options.filter(x => x.option_type === "brand_day" && x.parent_value === value).map(x => x.value);
  const childCampuses = options.filter(x => x.option_type === "brand_campus" && x.parent_value === value).map(x => x.value);
  const childDates = options.filter(x => x.option_type === "brand_date" && x.parent_value === value).map(x => x.value);
  if (childDays.length || childCampuses.length || childDates.length) return { exists: Boolean(card), days: childDays, campuses: childCampuses, report_dates: childDates };
  try {
    const raw = card?.parent_value ? JSON.parse(card.parent_value) : {};
    return {
      exists: Boolean(card),
      days: Array.isArray(raw.days) ? raw.days : splitOptionValues(raw.days || raw.day_text),
      campuses: Array.isArray(raw.campuses) ? raw.campuses : splitOptionValues(raw.campuses || raw.campus_text),
      report_dates: Array.isArray(raw.report_dates) ? raw.report_dates : splitOptionValues(raw.report_dates || raw.date_text),
    };
  } catch {
    return { exists: Boolean(card), days: [] as string[], campuses: [] as string[], report_dates: [] as string[] };
  }
}

function withCurrent(values: string[], current?: string | number | null) {
  const cur = String(current || "").trim();
  return Array.from(new Set([...(values || []), ...(cur && !values.includes(cur) ? [cur] : [])].filter(Boolean)));
}

function reportCheckWarnings(report: Partial<OrderReport>, options: ApiOption[]) {
  const warnings: Array<{ level: "error" | "warn"; text: string }> = [];
  const meta = brandServiceMeta(options, report.brand);
  const lastDay = calcEndDate(String(report.report_date || ""), String(report.days || ""));
  if (!report.brand) warnings.push({ level: "error", text: "缺少品牌" });
  else if (options.some(o => o.option_type === "brand") && !meta.exists) warnings.push({ level: "warn", text: "品牌不在品牌服务卡中" });
  if (!report.report_date) warnings.push({ level: "error", text: "缺少报道日期" });
  if (!report.days) warnings.push({ level: "error", text: "缺少参营天数" });
  if (!lastDay) warnings.push({ level: "error", text: "无法计算最后拍摄日，请检查报道日期和参营天数格式" });
  if (!report.campus) warnings.push({ level: "error", text: "缺少所在校区" });
  if (meta.days.length && report.days && !meta.days.includes(String(report.days))) warnings.push({ level: "warn", text: "参营天数不在该品牌服务卡中" });
  if (meta.campuses.length && report.campus && !meta.campuses.includes(String(report.campus))) warnings.push({ level: "warn", text: "校区不在该品牌服务卡中" });
  if (meta.report_dates.length && report.report_date && !meta.report_dates.includes(String(report.report_date))) warnings.push({ level: "warn", text: "报道日期不在该品牌服务卡中" });
  return warnings;
}

function copyReport(r: OrderReport) {
  return navigator.clipboard.writeText(reportText(r));
}

function reportText(r: OrderReport) {
  return `夏令营品牌：${r.brand || ""}\n孩子姓名：${r.child_name || ""}\n班级名称：${r.class_name || ""}\n报道日期：${r.report_date || ""}\n参营天数：${r.days || ""}\n所在校区：${r.campus || ""}\n下单款式：${r.package_name || ""}\n下单老师：${r.order_teacher || r.user_name || ""}\n下单金额：${r.amount || ""}\n赠品：${r.gift || ""}\n备注：${r.remark || ""}`;
}

function BrandOptionsPage({ toast }: { toast: (s: string) => void }) {
  const [type, setType] = useState("brand");
  const [value, setValue] = useState("");
  const [giftValue, setGiftValue] = useState("");
  const [packageForm, setPackageForm] = useState({ name: "", album_count: "", negative_count: "", gifts: [] as string[] });
  const [editing, setEditing] = useState<ApiOption | null>(null);
  const state = useAsync(() => api.adminReportOptions(), []);
  const serviceTypes = ["brand", "campus", "days", "brand_day", "brand_campus", "brand_date"];
  const list = (state.data || []).filter(x => type === "all" ? serviceTypes.includes(x.option_type) : x.option_type === type);
  const gifts = (state.data || []).filter(x => x.option_type === "gift" && x.status !== "disabled");
  const packages = (state.data || []).filter(x => x.option_type === "package" && x.status !== "disabled");
  const typeName = (name: string) => ({
    brand: "品牌服务卡",
    campus: "城市/营地/校区",
    days: "参营天数",
    brand_day: "品牌天数",
    brand_campus: "品牌校区",
    brand_date: "品牌报道",
  }[name] || name);
  const splitValues = (text?: string) => String(text || "").split(/[，,、\s\n]+/).map(x => x.trim()).filter(Boolean);
  const metaOf = (item: ApiOption) => {
    const childDays = (state.data || []).filter(x => x.option_type === "brand_day" && x.parent_value === item.value).map(x => x.value);
    const childCampuses = (state.data || []).filter(x => x.option_type === "brand_campus" && x.parent_value === item.value).map(x => x.value);
    const childDates = (state.data || []).filter(x => x.option_type === "brand_date" && x.parent_value === item.value).map(x => x.value);
    if (childDays.length || childCampuses.length || childDates.length) {
      return { days: childDays, campuses: childCampuses, report_dates: childDates };
    }
    try {
      const raw = item.parent_value ? JSON.parse(item.parent_value) : {};
      return {
        days: Array.isArray(raw.days) ? raw.days : splitValues(raw.days || raw.day_text),
        campuses: Array.isArray(raw.campuses) ? raw.campuses : splitValues(raw.campuses || raw.campus_text),
        report_dates: Array.isArray(raw.report_dates) ? raw.report_dates : splitValues(raw.report_dates || raw.report_date || raw.date_text),
      };
    } catch {
      return { days: [] as string[], campuses: [] as string[], report_dates: [] as string[] };
    }
  };
  const addQuick = async () => {
    if (!value.trim()) return;
    try {
      await api.createReportOption({ option_type: type, value, label: value, status: "enabled" });
      setValue("");
      toast("已新增");
      await state.reload();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const addGift = async () => {
    const text = giftValue.trim();
    if (!text) return;
    try {
      await api.createReportOption({ option_type: "gift", value: text, label: text, status: "enabled" });
      setGiftValue("");
      toast("赠品已新增");
      await state.reload();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const packageMeta = (item: ApiOption) => {
    try {
      const raw = item.parent_value ? JSON.parse(item.parent_value) : {};
      return {
        album_count: raw.album_count || "",
        negative_count: raw.negative_count || "",
        gifts: Array.isArray(raw.gifts) ? raw.gifts : splitValues(raw.gift || ""),
      };
    } catch {
      return { album_count: "", negative_count: "", gifts: [] as string[] };
    }
  };
  const setPkg = (key: "name" | "album_count" | "negative_count", value: string) => setPackageForm(v => ({ ...v, [key]: value }));
  const togglePackageGift = (gift: string) => setPackageForm(v => ({
    ...v,
    gifts: v.gifts.includes(gift) ? v.gifts.filter(item => item !== gift) : [...v.gifts, gift],
  }));
  const addPackage = async () => {
    const name = packageForm.name.trim();
    if (!name) return toast("请先填写款式名称");
    try {
      await api.createReportOption({
        option_type: "package",
        value: name,
        label: name,
        parent_value: JSON.stringify({
          album_count: packageForm.album_count.trim(),
          negative_count: packageForm.negative_count.trim(),
          gifts: packageForm.gifts,
          gift: packageForm.gifts.join("、"),
        }),
        status: "enabled",
      });
      setPackageForm({ name: "", album_count: "", negative_count: "", gifts: [] });
      toast("款式已新增");
      await state.reload();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const remove = async (id: string | number) => {
    if (!confirm("确定删除吗？")) return;
    await api.deleteReportOption(id);
    toast("已删除");
    await state.reload();
  };
  return (
    <>
      <PageTitle title="表单管理" desc="维护报单表单里的可选项：品牌服务卡、校区/营地、参营天数、报道日期和赠品；同步给一键报单和 H5。" action={<button className="secondary" onClick={state.reload}><RefreshCw size={16} />刷新</button>} />
      <div className="toolbar">
        <select value={type} onChange={e => setType(e.target.value)}>
          <option value="brand">品牌服务卡</option>
          <option value="campus">城市/营地/校区</option>
          <option value="days">参营天数</option>
          <option value="all">全部</option>
        </select>
        {type === "brand" ? <button className="primary" onClick={() => setEditing({ id: "new", option_type: "brand", value: "", label: "", parent_value: "", status: "enabled" })}><Plus size={16} />新增品牌服务卡</button> : type === "all" ? <span className="hint">切换到具体分类后可新增选项</span> : <>
          <input className="grow" value={value} onChange={e => setValue(e.target.value)} placeholder={`输入要新增的${typeName(type)}名称`} />
          <button className="primary" onClick={addQuick}><Plus size={16} />新增</button>
        </>}
      </div>
      <div className="option-grid">
        {list.map(o => {
          const meta = metaOf(o);
          return <div className="option-chip service-card" key={o.id}>
            <span>{typeName(o.option_type)}</span>
            <b>{o.label || o.value}</b>
            {o.option_type === "brand" && <small>天数：{meta.days.join(" / ") || "未设置"}</small>}
            {o.option_type === "brand" && <small>校区：{meta.campuses.join(" / ") || "未设置"}</small>}
            {o.option_type === "brand" && <small>报道：{meta.report_dates.join(" / ") || "未设置"}</small>}
            <div className="service-card-actions">
              {o.option_type === "brand" && <button onClick={() => setEditing(o)}><Edit3 size={15} />编辑</button>}
              <button onClick={() => remove(o.id)}><Trash2 size={15} />删除</button>
            </div>
          </div>;
        })}
      </div>
      <div className="panel package-manage-panel">
        <div className="panel-head">
          <div><h3>款式管理</h3><p>维护下单款式，并记录入册数量、底片数量和默认赠品；一键报单只同步款式名称作为下拉选项。</p></div>
        </div>
        <div className="package-add-grid">
          <input value={packageForm.name} onChange={e => setPkg("name", e.target.value)} placeholder="款式名称，例如：轻奢款" />
          <input value={packageForm.album_count} onChange={e => setPkg("album_count", e.target.value)} placeholder="入册数量，例如：18张" />
          <input value={packageForm.negative_count} onChange={e => setPkg("negative_count", e.target.value)} placeholder="底片数量，例如：30张" />
          <div className="package-gift-picker">
            {gifts.length ? gifts.map(gift => {
              const value = gift.value;
              const checked = packageForm.gifts.includes(value);
              return <button type="button" className={checked ? "active" : ""} key={gift.id} onClick={() => togglePackageGift(value)}>{gift.label || value}</button>;
            }) : <span>请先新增赠品</span>}
          </div>
          <button className="primary" onClick={addPackage}><Plus size={16} />新增款式</button>
        </div>
        <div className="gift-option-list package-option-list">
          {packages.length ? packages.map(item => {
            const meta = packageMeta(item);
            return <div className="gift-option-card package-option-card" key={item.id}>
              <span>款式</span>
              <b>{item.label || item.value}</b>
              <small>入册 {meta.album_count || "-"} · 底片 {meta.negative_count || "-"} · 赠品 {meta.gifts.join("、") || "-"}</small>
              <button onClick={() => remove(item.id)}><Trash2 size={15} />删除</button>
            </div>;
          }) : <Empty text="暂无款式，请先新增" />}
        </div>
      </div>
      <div className="panel gift-manage-panel">
        <div className="panel-head">
          <div><h3>赠品管理</h3><p>单独维护赠品下拉选项。AI 识别报单时只会匹配这里已有的赠品，识别不到会留空。</p></div>
        </div>
        <div className="gift-add-row">
          <input value={giftValue} onChange={e => setGiftValue(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addGift(); } }} placeholder="输入赠品名称，例如：相框 / 视频 / 精修券" />
          <button className="primary" onClick={addGift}><Plus size={16} />新增赠品</button>
        </div>
        <div className="gift-option-list">
          {gifts.length ? gifts.map(gift => <div className="gift-option-card" key={gift.id}>
            <span>赠品</span>
            <b>{gift.label || gift.value}</b>
            <button onClick={() => remove(gift.id)}><Trash2 size={15} />删除</button>
          </div>) : <Empty text="暂无赠品，请先新增" />}
        </div>
      </div>
      {editing && <BrandServiceCardModal
        item={editing}
        meta={metaOf(editing)}
        allOptions={state.data || []}
        close={() => setEditing(null)}
        done={async msg => { setEditing(null); toast(msg); await state.reload(); }}
      />}
    </>
  );
}

function BrandServiceCardModal({ item, meta, allOptions, close, done }: { item: ApiOption; meta: { days: string[]; campuses: string[]; report_dates: string[] }; allOptions: ApiOption[]; close: () => void; done: (msg: string) => void }) {
  const isNew = item.id === "new";
  const [name, setName] = useState(item.value || "");
  const [days, setDays] = useState<string[]>(meta.days || []);
  const [campuses, setCampuses] = useState<string[]>(meta.campuses || []);
  const [dates, setDates] = useState<string[]>(meta.report_dates || []);
  const [dayInput, setDayInput] = useState("");
  const [campusInput, setCampusInput] = useState("");
  const [dateInput, setDateInput] = useState("");
  const [busy, setBusy] = useState(false);
  const addUnique = (list: string[], value: string) => {
    const text = value.trim();
    return text && !list.includes(text) ? [...list, text] : list;
  };
  const removeValue = (list: string[], value: string) => list.filter(x => x !== value);
  const addDay = () => {
    const n = String(dayInput).match(/\d+/)?.[0];
    if (!n) return;
    setDays(v => addUnique(v, `${n}天`));
    setDayInput("");
  };
  const addCampus = () => {
    setCampuses(v => addUnique(v, campusInput));
    setCampusInput("");
  };
  const addDate = () => {
    if (!dateInput) return;
    setDates(v => addUnique(v, dateInput));
    setDateInput("");
  };
  const endPreview = dates[0] && days[0] ? calcEndDate(dates[0], days[0]) : "";
  const save = async () => {
    if (!name.trim()) return alert("请填写品牌名称");
    if (!days.length) return alert("请至少添加一个参营天数");
    if (!campuses.length) return alert("请至少添加一个校区/营地");
    if (!dates.length) return alert("请至少添加一个报道日期");
    setBusy(true);
    try {
      let brandValue = name.trim();
      if (isNew) {
        await api.createReportOption({ option_type: "brand", value: brandValue, label: brandValue, parent_value: "", status: "enabled" });
      } else {
        const oldValue = item.value;
        await api.updateReportOption(item.id, { value: brandValue, label: brandValue, parent_value: "", status: item.status || "enabled" });
        const oldChildren = allOptions.filter(x => ["brand_day", "brand_campus", "brand_date"].includes(x.option_type) && x.parent_value === oldValue);
        for (const child of oldChildren) await api.deleteReportOption(child.id);
      }
      for (const value of days) await api.createReportOption({ option_type: "brand_day", value, label: value, parent_value: brandValue, status: "enabled" });
      for (const value of campuses) await api.createReportOption({ option_type: "brand_campus", value, label: value, parent_value: brandValue, status: "enabled" });
      for (const value of dates) await api.createReportOption({ option_type: "brand_date", value, label: value, parent_value: brandValue, status: "enabled" });
      done(isNew ? "品牌服务卡已新增" : "品牌服务卡已更新");
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal compact-modal">
        <div className="modal-head"><div><h2>{isNew ? "新增品牌服务卡" : "编辑品牌服务卡"}</h2><p>一个品牌下面维护可选天数、校区/营地、报道日期。</p></div><button onClick={close}><X /></button></div>
        <div className="form-panel compact-form no-border service-card-modal-form">
          <Field label="品牌名称" value={name} onChange={setName} />
          <div className="inline-add">
            <label><span>参营天数</span><input value={dayInput} onChange={e => setDayInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addDay(); } }} placeholder="输入数字，如 14" /></label>
            <button className="secondary" onClick={addDay}>添加天数</button>
          </div>
          <div className="inline-add">
            <label><span>校区/营地</span><input value={campusInput} onChange={e => setCampusInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCampus(); } }} placeholder="例如 厦门校区" /></label>
            <button className="secondary" onClick={addCampus}>添加校区</button>
          </div>
          <div className="inline-add">
            <label><span>报道日期</span><input type="date" value={dateInput} onChange={e => setDateInput(e.target.value)} /></label>
            <button className="secondary" onClick={addDate}>添加日期</button>
          </div>
        </div>
        <div className="service-tag-panel">
          <ServiceTags title="天数" values={days} onRemove={v => setDays(list => removeValue(list, v))} />
          <ServiceTags title="校区/营地" values={campuses} onRemove={v => setCampuses(list => removeValue(list, v))} />
          <ServiceTags title="报道日期" values={dates} onRemove={v => setDates(list => removeValue(list, v))} />
        </div>
        <p className="hint">日期统一用日历选择，天数统一为固定数字天数。{endPreview ? `示例：${dates[0]} + ${days[0]}，预计结营 ${endPreview}` : ""}</p>
        <div className="modal-actions"><button className="secondary" onClick={close}>取消</button><button className="primary" disabled={busy} onClick={save}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}保存</button></div>
      </div>
    </div>
  );
}

function ServiceTags({ title, values, onRemove }: { title: string; values: string[]; onRemove: (v: string) => void }) {
  return <div className="service-tags"><b>{title}</b><div>{values.length ? values.map(v => <span key={v}>{v}<button onClick={() => onRemove(v)}>×</button></span>) : <small>暂无</small>}</div></div>;
}

function calcEndDate(start: string, days: string) {
  const n = Number(String(days).match(/\d+/)?.[0] || 0);
  const d = new Date(start);
  if (!n || Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + Math.max(n - 1, 0));
  return d.toISOString().slice(0, 10);
}

function PhotographerManagementPage({ toast }: { toast: (s: string) => void }) {
  const [keyword, setKeyword] = useState("");
  const overview = useAsync(() => api.photographerOverview({ keyword }), [keyword]);
  const [scopeEditor, setScopeEditor] = useState<{ photographer: ApiUser; scope?: any } | null>(null);
  const [detail, setDetail] = useState<{ photographer: ApiUser; scope?: any; tasks?: ShootingTask[]; uploads?: ShootingUpload[]; stats?: any } | null>(null);

  const photographerRows = overview.data?.items || [];
  const stats = overview.data?.stats || { total: 0, today_pending: 0, today_shot: 0, pending_review: 0, rejected: 0, near_end: 0, tomorrow_plan: "-", no_scope: 0 };
  const reloadAll = async () => {
    await overview.reload();
  };

  return (
    <>
      <PageTitle
        title="摄影师管理"
        desc="统一管理摄影师接单范围、任务数据、上传审图和明日计划。"
        action={<button className="secondary" onClick={reloadAll}><RefreshCw size={16} />刷新</button>}
      />
      <div className="stat-grid photographer-manage-stats">
        <StatCard label="摄影师总数" value={stats.total} />
        <StatCard label="今日待拍" value={stats.today_pending} />
        <StatCard label="今日已拍" value={stats.today_shot} />
        <StatCard label="待审图" value={stats.pending_review} />
        <StatCard label="驳回重拍" value={stats.rejected} />
        <StatCard label="马上结营未拍" value={stats.near_end} />
        <StatCard label="明日计划人数" value={stats.tomorrow_plan || "-"} />
        <StatCard label="未设置负责范围" value={stats.no_scope} />
      </div>
      <div className="toolbar dense photographer-manage-toolbar">
        <input className="grow" value={keyword} onChange={e => setKeyword(e.target.value)} placeholder="搜索摄影师姓名 / 手机号 / 品牌 / 校区" />
      </div>
      <div className="table-card photographer-management-table">
        {overview.loading ? <Loading /> : photographerRows.length ? (
          <table>
            <thead>
              <tr>
                <th>摄影师姓名</th><th>手机号</th><th>是否允许接单</th><th>负责品牌</th><th>负责校区 / 营地</th>
                <th>今日任务</th><th>今日已拍</th><th>待审图</th><th>异常 / 重拍</th><th>明日计划人数</th><th>最近上传时间</th><th>操作</th>
              </tr>
            </thead>
            <tbody>
              {photographerRows.map(p => {
                const scope = p.scope;
                const rs = p.stats || {};
                const canTake = Boolean(p.can_take_shooting_order);
                return (
                  <tr key={p.id}>
                    <td><b>{p.name}</b></td>
                    <td>{p.phone || "-"}</td>
                    <td><Badge text={canTake ? "允许接单" : "未允许"} /></td>
                    <td>{scope?.brands?.length ? scope.brands.join("、") : <span className="muted">未设置</span>}</td>
                    <td>{scope?.campuses?.length ? scope.campuses.join("、") : <span className="muted">未设置</span>}</td>
                    <td><b>{rs.today_tasks || 0}</b></td>
                    <td><b>{rs.today_shot || 0}</b></td>
                    <td><b>{rs.pending_review || 0}</b></td>
                    <td><b>{rs.issue}</b></td>
                    <td><span className="muted">暂未同步</span></td>
                    <td>{rs.latest_upload ? cnDate(rs.latest_upload) : "-"}</td>
                    <td>
                      <div className="row-actions compact-actions">
                        <button className="mini-btn" onClick={() => setDetail({ photographer: p, scope, tasks: p.tasks || [], uploads: p.uploads || [], stats: p.stats || {} })}>查看详情</button>
                        <button className="mini-btn" onClick={() => setScopeEditor({ photographer: p, scope })}>设置范围</button>
                        <button className="mini-btn" onClick={() => setDetail({ photographer: p, scope, tasks: p.tasks || [], uploads: p.uploads || [], stats: p.stats || {} })}>查看明日计划</button>
                        <button className="mini-btn" onClick={() => setDetail({ photographer: p, scope, tasks: p.tasks || [], uploads: p.uploads || [], stats: p.stats || {} })}>查看今日名单</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : <Empty text="暂无摄影师" />}
      </div>
      <p className="hint">说明：当前 H5 明日计划还保存在摄影师手机本地，后台暂不能统计。下一步接入计划同步接口后，这里会显示真实人数和名单。</p>
      {scopeEditor && <PhotographerScopeModal photographer={scopeEditor.photographer} initialScope={scopeEditor.scope} close={() => setScopeEditor(null)} toast={toast} done={reloadAll} />}
      {detail && (
        <PhotographerDetailModal
          photographer={detail.photographer}
          scope={detail.scope}
          tasks={detail.tasks || []}
          uploads={detail.uploads || []}
          stats={detail.stats || {}}
          close={() => setDetail(null)}
          openScope={() => {
            setScopeEditor(detail);
            setDetail(null);
          }}
        />
      )}
    </>
  );
}

function PhotographerDetailModal({ photographer, scope, tasks, uploads, stats, close, openScope }: { photographer: ApiUser; scope?: any; tasks: ShootingTask[]; uploads: ShootingUpload[]; stats?: any; close: () => void; openScope: () => void }) {
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayTasks = tasks.filter(t => String(t.report_date || t.created_at || "").slice(0, 10) === todayKey || !taskAlreadyShot(t));
  const passed = stats?.approved ?? uploads.filter(u => u.review_status === "approved").length;
  const rejected = stats?.rejected ?? uploads.filter(u => u.review_status === "rejected").length;
  const pending = stats?.upload_pending ?? uploads.filter(u => !u.review_status || u.review_status === "pending").length;
  const uploadCount = stats?.upload_count ?? uploads.length;
  return (
    <div className="modal-backdrop">
      <div className="modal wide-modal photographer-detail-modal">
        <div className="modal-head">
          <div><h2>{photographer.name} 的摄影师详情</h2><p>{photographer.phone || "-"} · {roleName[photographer.role] || photographer.role}</p></div>
          <button onClick={close}><X /></button>
        </div>
        <div className="photographer-detail-section">
          <div className="section-head-row">
            <h3>摄影师资料</h3>
            <button className="secondary" onClick={openScope}>修改负责范围</button>
          </div>
          <div className="detail-kv-grid">
            <div><span>姓名</span><b>{photographer.name}</b></div>
            <div><span>手机号</span><b>{photographer.phone || "-"}</b></div>
            <div><span>是否允许接单</span><b>{photographer.can_take_shooting_order ? "允许" : "未允许"}</b></div>
            <div><span>自动匹配</span><b>{scope?.status === "disabled" ? "关闭" : "开启"}</b></div>
            <div><span>负责品牌</span><b>{scope?.brands?.length ? scope.brands.join("、") : "未设置"}</b></div>
            <div><span>负责校区</span><b>{scope?.campuses?.length ? scope.campuses.join("、") : "未设置"}</b></div>
          </div>
        </div>
        <div className="photographer-detail-section">
          <h3>今日拍摄名单</h3>
          <MiniTaskList tasks={todayTasks} empty="今日暂无需要拍摄的订单" />
        </div>
        <div className="photographer-detail-section">
          <h3>明日拍摄计划</h3>
          <div className="empty-state small">当前 H5 明日计划还未同步到服务器。后续补计划同步接口后，这里会显示摄影师自己制定的明日名单。</div>
        </div>
        <div className="photographer-detail-section">
          <h3>上传 / 审图数据</h3>
          <div className="detail-kv-grid">
            <div><span>上传照片</span><b>{uploadCount}</b></div>
            <div><span>通过</span><b>{passed}</b></div>
            <div><span>待审</span><b>{pending}</b></div>
            <div><span>驳回</span><b>{rejected}</b></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniTaskList({ tasks, empty }: { tasks: ShootingTask[]; empty: string }) {
  if (!tasks.length) return <Empty text={empty} />;
  return (
    <div className="mini-task-list">
      {tasks.slice(0, 12).map(t => (
        <div className="mini-task-row" key={t.id}>
          <b>{t.child_name || "未填孩子"} · {t.class_name || "-"}</b>
          <span>{t.brand || "-"} / {t.campus || "-"} / {t.package_name || "-"}</span>
          <span>最后拍摄日：{t.end_date || "-"}</span>
          <span>{t.special_note || t.shooting_requirement || "-"}</span>
        </div>
      ))}
    </div>
  );
}

function DispatchPage({ toast }: { toast: (s: string) => void }) {
  const photographers = useAsync(api.shootingPhotographers, []);
  const scopes = useAsync(api.photographerScopes, []);
  const [scopeEditor, setScopeEditor] = useState<{ photographer: ApiUser; scope?: any } | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [matchRows, setMatchRows] = useState<Array<{ task: ShootingTask; report?: OrderReport; photographer_id: string }>>([]);
  const [balancedOpen, setBalancedOpen] = useState(false);
  const [filters, setFilters] = useState({ brand: "", campus: "", package_name: "", days: "", assign_status: "", start_at: "", end_at: "" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const setFilter = (key: keyof typeof filters, value: string) => {
    setPage(1);
    setFilters(v => ({ ...v, [key]: value }));
    setSelected({});
  };
  const dispatchParams = { ...filters, page, per_page: perPage };
  const reports = useAsync(() => api.dispatchReportsPage(dispatchParams), [JSON.stringify(dispatchParams)]);
  const rows = reports.data?.items || [];
  const brands = reports.data?.options?.brands || [];
  const campuses = reports.data?.options?.campuses || [];
  const packages = reports.data?.options?.packages || [];
  const dayOptions = reports.data?.options?.days || [];
  const taskMap = new Map(rows.map(r => [String(r.id), r.shooting_task]).filter((entry): entry is [string, ShootingTask] => Boolean(entry[1])));
  const scopeMap = new Map((scopes.data || []).map((s: any) => [String(s.photographer_id), s]));
  const isAssigned = (r: OrderReport) => {
    const task = r.shooting_task || taskMap.get(String(r.id));
    return Boolean(task?.photographer_id || task?.shooting_status === "assigned" || task?.shooting_status === "uploaded" || task?.shooting_status === "completed");
  };
  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([id]) => id);
  const selectedReports = rows.filter(r => selected[String(r.id)]);
  const reloadAll = async () => {
    await Promise.all([reports.reload(), photographers.reload(), scopes.reload()]);
  };
  const generateTasks = async (ids: Array<string | number> = selectedIds) => {
    if (!ids.length) {
      toast("请先勾选要生成拍摄任务的订单");
      return null;
    }
    const r = await api.batchGenerateShootingTasks(ids);
    toast(`生成完成：新增 ${r.created ?? r.generated ?? 0}，跳过 ${r.skipped || 0}`);
    return r;
  };
  const pickPhotographer = (item: OrderReport, scopeList: any[]) => {
    const brand = normText(item.brand);
    const campus = normText(item.campus);
    const matched = scopeList.filter(s => {
      if (s.can_take_shooting_order !== true) return false;
      if (s.status === "disabled") return false;
      const brands = Array.isArray(s.brands) ? s.brands.map(normText) : [];
      const campuses = Array.isArray(s.campuses) ? s.campuses.map(normText) : [];
      if (!brands.length || !campuses.length) return false;
      const brandOk = brands.includes(brand);
      const campusOk = campuses.includes(campus);
      return brandOk && campusOk;
    });
    return matched.length === 1 ? String(matched[0].photographer_id) : "";
  };
  const autoMatch = async (single?: OrderReport) => {
    const requestedTargets = single ? [single] : selectedReports;
    const targets = requestedTargets.filter(report => taskCanBeDispatched(report.shooting_task || taskMap.get(String(report.id))));
    if (!targets.length) return toast(requestedTargets.length ? "所选订单已有拍摄、上传或审图进度，不能普通改派" : "请先勾选要派单的订单");
    const ids = targets.map(x => x.id);
    try {
      const generated = await generateTasks(ids);
      if (!generated) return;
      const generatedTasks = Array.isArray(generated.items) ? generated.items as ShootingTask[] : [];
      const generatedMap = new Map<string, ShootingTask>();
      [...generatedTasks]
        .filter(t => ids.some(id => String(id) === String(t.order_report_id)))
        .sort((a, b) => Number(b.id || 0) - Number(a.id || 0))
        .forEach(t => {
          const key = String(t.order_report_id);
          if (!generatedMap.has(key)) generatedMap.set(key, t);
        });
      const next = targets
        .map(report => {
          const task = generatedMap.get(String(report.id)) || report.shooting_task;
          return { report, task, photographer_id: task?.photographer_id ? String(task.photographer_id) : pickPhotographer(report, scopes.data || []) };
        })
        .filter((x): x is { report: OrderReport; task: ShootingTask; photographer_id: string } => Boolean(x.task));
      if (!next.length) return toast("没有找到可派单的拍摄任务，请先确认报单是否已核对");
      setMatchRows(next);
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const selectableRows = rows.filter(r => taskCanBeDispatched(r.shooting_task || taskMap.get(String(r.id))));
  const allChecked = Boolean(selectableRows.length) && selectableRows.every(r => selected[String(r.id)]);
  const setAssignStatus = (value: "" | "assigned" | "unassigned") => setFilter("assign_status", filters.assign_status === value ? "" : value);
  const dispatchStats = reports.data?.stats || { total: reports.data?.total || 0, assigned: 0, unassigned: 0 };
  return (
    <>
      <PageTitle title="摄影师派单" desc="已核对订单先进入这里，按品牌和校区自动匹配摄影师，确认后批量派单。" action={<div className="action-row"><button className="secondary" onClick={() => generateTasks()}><Camera size={16} />生成拍摄任务</button><button className="secondary" onClick={() => setBalancedOpen(true)}><BarChart3 size={16} />智能均衡派单</button><button className="primary" onClick={() => autoMatch()}><Users size={16} />一键匹配摄影师</button></div>} />
      <ErrorBox message={reports.error || photographers.error || scopes.error} />
      <div className="stat-grid four dispatch-stats">
        <StatCard label="当前筛选订单" value={dispatchStats.total} active={!filters.assign_status} onClick={() => setAssignStatus("")} />
        <StatCard label="未派单数量" value={dispatchStats.unassigned} active={filters.assign_status === "unassigned"} onClick={() => setAssignStatus("unassigned")} />
        <StatCard label="已派单数量" value={dispatchStats.assigned} active={filters.assign_status === "assigned"} onClick={() => setAssignStatus("assigned")} />
        <StatCard label="已选订单" value={selectedIds.length} />
      </div>
      <div className="toolbar dense dispatch-filter">
        <select value={filters.brand} onChange={e => setFilter("brand", e.target.value)}><option value="">全部品牌</option>{brands.map(x => <option key={x} value={x}>{x}</option>)}</select>
        <select value={filters.campus} onChange={e => setFilter("campus", e.target.value)}><option value="">全部校区</option>{campuses.map(x => <option key={x} value={x}>{x}</option>)}</select>
        <select value={filters.package_name} onChange={e => setFilter("package_name", e.target.value)}><option value="">全部款式</option>{packages.map(x => <option key={x} value={x}>{x}</option>)}</select>
        <select value={filters.days} onChange={e => setFilter("days", e.target.value)}><option value="">全部天数</option>{dayOptions.map(x => <option key={x} value={x}>{x}</option>)}</select>
        <select value={filters.assign_status} onChange={e => setFilter("assign_status", e.target.value)}><option value="">全部派单状态</option><option value="unassigned">未派单</option><option value="assigned">已派单</option></select>
        <label className="date-label"><span>下单开始</span><input type="date" value={filters.start_at} onChange={e => setFilter("start_at", e.target.value)} /></label>
        <label className="date-label"><span>下单结束</span><input type="date" value={filters.end_at} onChange={e => setFilter("end_at", e.target.value)} /></label>
        <button className="secondary" onClick={() => { setPage(1); setFilters({ brand: "", campus: "", package_name: "", days: "", assign_status: "", start_at: "", end_at: "" }); setSelected({}); }}>重置</button>
      </div>
      <div className="dispatch-grid">
        <div className="panel dispatch-pool">
          <div className="panel-head">
            <div className="dispatch-pool-title">
              <button className="secondary" onClick={() => setSelected(Object.fromEntries(selectableRows.map(r => [String(r.id), !allChecked])))}>{allChecked ? "取消全选" : "全选当前"}</button>
              <div><h3>已核对报单池</h3><p>已选 {selectedIds.length} 条。可单条匹配，也可以全选后批量匹配。</p></div>
            </div>
          </div>
          <DispatchReportTable rows={rows} loading={reports.loading} selected={selected} setSelected={setSelected} onMatch={autoMatch} taskMap={taskMap} />
          <PaginationBar page={reports.data?.page || page} pages={reports.data?.pages || 1} total={reports.data?.total || 0} perPage={perPage} onPage={value => { setPage(value); setSelected({}); }} onPerPage={value => { setPage(1); setPerPage(value); setSelected({}); }} />
        </div>
        <div className="panel photographer-side-panel">
          <div className="panel-head compact">
            <div><h3>摄影师</h3><p>每个摄影师后面可单独设置接单范围。</p></div>
          </div>
          {photographers.loading ? <Loading /> : photographers.data?.length ? photographers.data.map(p => {
            const scope = scopeMap.get(String(p.id));
            const autoEnabled = scope?.status !== "disabled";
            const canTake = Boolean(p.can_take_shooting_order);
            return (
              <div className="person-row photographer-scope-row" key={p.id}>
                <div className="person-main">
                  <b>{p.name}</b>
                  <span>{p.phone}</span>
                  <small>品牌：{scope?.brands?.length ? scope.brands.join("、") : "未设置"}<br />校区：{scope?.campuses?.length ? scope.campuses.join("、") : "未设置"}</small>
                </div>
                <div className="person-actions">
                  <span className={`mini-badge ${canTake ? "" : "off"}`}>{canTake ? "可接单" : "未开接单"}</span>
                  <span className={`mini-badge ${autoEnabled ? "" : "off"}`}>{autoEnabled ? "自动匹配" : "不匹配"}</span>
                  <button className="secondary small-btn" onClick={() => setScopeEditor({ photographer: p, scope })}>设置范围</button>
                </div>
              </div>
            );
          }) : <Empty text="暂无可派单摄影师" />}
        </div>
      </div>
      {scopeEditor && <PhotographerScopeModal photographer={scopeEditor.photographer} initialScope={scopeEditor.scope} close={() => setScopeEditor(null)} toast={toast} done={reloadAll} />}
      {balancedOpen && <BalancedDispatchModal rows={selectedReports} taskMap={taskMap} scopes={scopes.data || []} generateTasks={generateTasks} close={() => setBalancedOpen(false)} done={async msg => { setBalancedOpen(false); setSelected({}); toast(msg); await reloadAll(); }} />}
      {matchRows.length > 0 && <DispatchMatchModal rows={matchRows} photographers={photographers.data || []} setRows={setMatchRows} close={() => setMatchRows([])} done={async msg => { setMatchRows([]); setSelected({}); toast(msg); await reloadAll(); }} />}
    </>
  );
}

function normText(v?: string | number | null) {
  return String(v || "").trim().replace(/\s+/g, "").toLowerCase();
}

function DispatchReportTable({ rows, loading, selected, setSelected, onMatch, taskMap }: { rows: OrderReport[]; loading: boolean; selected: Record<string, boolean>; setSelected: Dispatch<SetStateAction<Record<string, boolean>>>; onMatch: (r: OrderReport) => void; taskMap: Map<string, ShootingTask> }) {
  return (
    <div className="table-card report-table dispatch-table">
      {loading ? <Loading /> : rows.length ? (
        <table>
          <thead><tr><th>选</th><th>品牌</th><th>孩子</th><th>班级</th><th>报道</th><th>天数</th><th>校区</th><th>款式</th><th>老师</th><th>金额</th><th>赠品</th><th>派单</th><th>提交</th><th>操作</th></tr></thead>
          <tbody>{rows.map(r => {
            const task = taskMap.get(String(r.id));
            const assigned = Boolean(task?.photographer_id || task?.shooting_status === "assigned" || task?.shooting_status === "uploaded" || task?.shooting_status === "completed");
            const dispatchable = taskCanBeDispatched(task);
            const partialRefund = partialRefundText(r);
            return <tr key={r.id}>
              <td><input type="checkbox" disabled={!dispatchable} checked={Boolean(selected[String(r.id)])} onChange={e => setSelected(v => ({ ...v, [String(r.id)]: e.target.checked }))} /></td>
              <td>{r.brand || "-"}</td><td>{r.child_name || "-"}</td><td>{r.class_name || "-"}</td><td>{r.report_date || "-"}</td><td>{r.days || "-"}</td><td>{r.campus || "-"}</td><td>{r.package_name || "-"}</td><td>{r.order_teacher || r.user_name || "-"}</td><td><b>{r.amount || "-"}</b>{partialRefund && <small className="dispatch-refund-note">{partialRefund}</small>}</td><td>{r.gift || "-"}</td><td><Badge text={assigned ? "已派单" : "未派单"} /></td><td>{cnDate(r.created_at)}</td>
              <td><button className="mini-btn" disabled={!dispatchable} onClick={() => onMatch(r)}>{dispatchable ? (assigned ? "改派" : "匹配") : "已有进度"}</button></td>
            </tr>;
          })}</tbody>
        </table>
      ) : <Empty text="暂无已核对报单" />}
    </div>
  );
}

function BalancedDispatchModal({
  rows,
  taskMap,
  scopes,
  generateTasks,
  close,
  done,
}: {
  rows: OrderReport[];
  taskMap: Map<string, ShootingTask>;
  scopes: any[];
  generateTasks: (ids?: Array<string | number>) => Promise<any>;
  close: () => void;
  done: (msg: string) => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const targets = useMemo(
    () => rows.filter(report => taskCanBeDispatched(report.shooting_task || taskMap.get(String(report.id)))),
    [rows, taskMap],
  );
  const candidates = useMemo(() => {
    if (!targets.length) return [];
    return (scopes || []).filter(scope => {
      if (scope?.can_take_shooting_order !== true) return false;
      if (scope?.status === "disabled") return false;
      const brands = Array.isArray(scope.brands) ? scope.brands.map(normText) : [];
      const campuses = Array.isArray(scope.campuses) ? scope.campuses.map(normText) : [];
      if (!brands.length || !campuses.length) return false;
      return targets.every(report => brands.includes(normText(report.brand)) && campuses.includes(normText(report.campus)));
    });
  }, [scopes, targets]);
  const totalAssigned = Object.values(counts).reduce((sum, value) => sum + Number(value || 0), 0);
  const totalOrders = targets.length;
  const setCount = (id: string | number, value: number) => {
    const key = String(id);
    setCounts(prev => ({ ...prev, [key]: Math.max(0, Math.min(totalOrders, Number.isFinite(value) ? Math.floor(value) : 0)) }));
  };
  const average = () => {
    if (!candidates.length || !totalOrders) return;
    const base = Math.floor(totalOrders / candidates.length);
    let rest = totalOrders % candidates.length;
    const next: Record<string, number> = {};
    candidates.forEach(scope => {
      const key = String(scope.photographer_id);
      next[key] = base + (rest > 0 ? 1 : 0);
      rest -= 1;
    });
    setCounts(next);
  };
  useEffect(() => {
    if (!Object.keys(counts).length && candidates.length && totalOrders) average();
  }, [candidates.length, totalOrders]);
  const submit = async () => {
    if (!totalOrders) return alert("请先勾选可以派单的订单");
    if (!candidates.length) return alert("没有找到同时符合这些订单品牌和校区范围的摄影师，请调整筛选或摄影师范围");
    if (totalAssigned !== totalOrders) return alert(`分配数量需要等于订单数量：当前 ${totalOrders} 单，已分配 ${totalAssigned} 单`);
    const assignments = Object.entries(counts)
      .map(([photographer_id, count]) => ({ photographer_id, count: Number(count || 0) }))
      .filter(item => item.count > 0);
    setBusy(true);
    try {
      const generated = await generateTasks(targets.map(item => item.id));
      const generatedTasks = Array.isArray(generated?.items) ? generated.items as ShootingTask[] : [];
      const generatedMap = new Map<string, ShootingTask>();
      generatedTasks.forEach(task => {
        if (task?.order_report_id) generatedMap.set(String(task.order_report_id), task);
      });
      const taskIds = targets
        .map(report => generatedMap.get(String(report.id)) || report.shooting_task || taskMap.get(String(report.id)))
        .map(task => task?.id)
        .filter(Boolean) as Array<string | number>;
      if (taskIds.length !== totalOrders) return alert("有订单还没有生成拍摄任务，请刷新后重试");
      const result = await api.balancedAssignShootingTasks(taskIds, assignments, "智能均衡派单");
      const detail = (result.summary || [])
        .filter(item => Number(item.count || 0) > 0)
        .map(item => `${item.photographer_name} ${item.count} 单`)
        .join("，");
      const skippedText = result.skipped ? `，跳过 ${result.skipped} 单` : "";
      await done(`智能均衡派单完成：成功 ${result.updated} 单${skippedText}${detail ? `；${detail}` : ""}`);
    } catch (e) {
      alert((e as Error).message || "智能均衡派单失败，请刷新后重试");
    } finally {
      setBusy(false);
    }
  };
  return <div className="modal-backdrop">
    <div className="modal wide-modal balanced-dispatch-modal">
      <div className="modal-head">
        <div><h2>智能均衡派单</h2><p>为同一批订单设置每位摄影师的数量，也可以一键平均。</p></div>
        <button onClick={close}><X /></button>
      </div>
      <div className="balanced-summary">
        <div><span>可派订单</span><b>{totalOrders}</b></div>
        <div><span>可用摄影师</span><b>{candidates.length}</b></div>
        <div className={totalAssigned === totalOrders ? "ok" : "warn"}><span>已分配</span><b>{totalAssigned}</b></div>
      </div>
      {!rows.length ? <Empty text="请先在左侧勾选要派单的订单" /> : !targets.length ? <Empty text="所选订单已有拍摄、上传或审图进度，不能普通改派" /> : !candidates.length ? <Empty text="没有找到同时符合这些订单品牌和校区范围的摄影师" /> : (
        <>
          <div className="balanced-toolbar">
            <button className="secondary" onClick={average}><BarChart3 size={16} />平均分配</button>
            <span>数量合计必须等于 {totalOrders} 单，确认后会按轮流方式分配。</span>
          </div>
          <div className="balanced-photographer-list">
            {candidates.map(scope => {
              const id = String(scope.photographer_id);
              return <div className="balanced-photographer-row" key={id}>
                <div>
                  <b>{scope.name || scope.photographer_name || `摄影师 ${id}`}</b>
                  <small>品牌：{scope.brands?.join("、") || "-"}　校区：{scope.campuses?.join("、") || "-"}</small>
                </div>
                <label><span>本次分配</span><input type="number" min={0} max={totalOrders} value={counts[id] ?? 0} onChange={e => setCount(id, Number(e.target.value))} /><em>单</em></label>
              </div>;
            })}
          </div>
        </>
      )}
      <div className="modal-actions"><button className="secondary" onClick={close}>取消</button><button className="primary" disabled={busy || !totalOrders || !candidates.length} onClick={submit}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}确认均衡派单</button></div>
    </div>
  </div>;
}

function DispatchMatchModal({ rows, photographers, setRows, close, done }: { rows: Array<{ task: ShootingTask; report?: OrderReport; photographer_id: string }>; photographers: ApiUser[]; setRows: Dispatch<SetStateAction<Array<{ task: ShootingTask; report?: OrderReport; photographer_id: string }>>>; close: () => void; done: (msg: string) => void }) {
  const [busy, setBusy] = useState(false);
  const setPid = (taskId: string | number, pid: string) => setRows(list => list.map(x => String(x.task.id) === String(taskId) ? { ...x, photographer_id: pid } : x));
  const submit = async () => {
    const valid = rows.filter(x => x.photographer_id);
    if (!valid.length) return alert("请至少选择一个摄影师");
    setBusy(true);
    try {
      const result = await api.batchAssignShootingTasks(valid.map(row => ({ task_id: row.task.id, photographer_id: row.photographer_id, note: "批量确认派单" })));
      const savedMap = new Map(result.items.map(task => [String(task.id), task]));
      setRows(list => list.map(item => {
        const saved = savedMap.get(String(item.task.id));
        return saved ? { ...item, task: saved, photographer_id: String(saved.photographer_id || item.photographer_id) } : item;
      }));
      done(`已处理 ${valid.length} 条，实际新派或改派 ${result.updated} 条${valid.length < rows.length ? `，${rows.length - valid.length} 条未选择摄影师已跳过` : ""}`);
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    } finally {
      setBusy(false);
    }
  };
  return <div className="modal-backdrop">
    <div className="modal wide-modal dispatch-confirm-modal">
      <div className="modal-head"><div><h2>确认派单</h2><p>品牌和校区只有唯一符合人选时才会自动预选；未匹配或多人符合时请手动选择。</p></div><button onClick={close}><X /></button></div>
      <div className="table-card">
        <table>
          <thead><tr><th>孩子</th><th>品牌</th><th>校区</th><th>班级</th><th>报道/天数</th><th>款式</th><th>匹配摄影师</th></tr></thead>
          <tbody>{rows.map(row => <tr key={row.task.id}>
            <td>{row.task.child_name || row.report?.child_name || "-"}</td>
            <td>{row.task.brand || row.report?.brand || "-"}</td>
            <td>{row.task.campus || row.report?.campus || "-"}</td>
            <td>{row.task.class_name || row.report?.class_name || "-"}</td>
            <td>{row.task.report_date || row.report?.report_date || "-"} / {row.task.days || row.report?.days || "-"}</td>
            <td>{row.task.package_name || row.report?.package_name || "-"}</td>
            <td><select value={row.photographer_id} onChange={e => setPid(row.task.id, e.target.value)}><option value="">未匹配，请选择</option>{photographers.filter(p => p.can_take_shooting_order === true).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></td>
          </tr>)}</tbody>
        </table>
      </div>
      <div className="modal-actions"><button className="secondary" onClick={close}>取消</button><button className="primary" disabled={busy} onClick={submit}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}确认并派单</button></div>
    </div>
  </div>;
}

function PhotographerScopeModal({ photographer, initialScope, close, toast, done }: { photographer: ApiUser; initialScope?: any; close: () => void; toast: (s: string) => void; done?: () => void | Promise<void> }) {
  const options = useAsync(() => api.adminReportOptions(), []);
  const [editing, setEditing] = useState<any>(() => ({
    photographer_id: photographer.id,
    name: photographer.name,
    brands: Array.isArray(initialScope?.brands) ? initialScope.brands : [],
    campuses: Array.isArray(initialScope?.campuses) ? initialScope.campuses : [],
    status: initialScope?.status || "enabled",
  }));
  const [canTake, setCanTake] = useState(Boolean(photographer.can_take_shooting_order));
  const [autoMatch, setAutoMatch] = useState((initialScope?.status || "enabled") !== "disabled");
  const [saving, setSaving] = useState(false);
  const splitValues = (text?: string) => String(text || "").split(/[，,、\s\n]+/).map(x => x.trim()).filter(Boolean);
  const uniqueTexts = (values: unknown[]) => Array.from(new Set(values.map(x => String(x || "").trim()).filter(Boolean)));
  const metaOf = (item: ApiOption) => {
    const childCampuses = (options.data || []).filter(x => x.option_type === "brand_campus" && x.parent_value === item.value).map(x => x.value);
    if (childCampuses.length) return { campuses: childCampuses };
    try {
      const raw = item.parent_value ? JSON.parse(item.parent_value) : {};
      return {
        campuses: Array.isArray(raw.campuses) ? raw.campuses : splitValues(raw.campuses || raw.campus_text),
      };
    } catch {
      return { campuses: [] as string[] };
    }
  };
  const brandCards = (options.data || []).filter(x => x.option_type === "brand");
  const configuredBrands = uniqueTexts(brandCards.map(x => x.value));
  const savedBrands = uniqueTexts(Array.isArray(editing?.brands) ? editing.brands : []);
  const brands = uniqueTexts([...configuredBrands, ...savedBrands]);
  const selectedBrands = savedBrands.length ? savedBrands : configuredBrands;
  const configuredCampuses = uniqueTexts(
    brandCards
      .filter(x => selectedBrands.includes(x.value))
      .flatMap(x => metaOf(x).campuses)
      .filter(Boolean)
  );
  const savedCampuses = uniqueTexts(Array.isArray(editing?.campuses) ? editing.campuses : []);
  const campuses = uniqueTexts([...configuredCampuses, ...savedCampuses]);
  const brandConfigured = (value: string) => configuredBrands.includes(value);
  const campusConfigured = (value: string) => configuredCampuses.includes(value);
  const toggle = (key: "brands" | "campuses", value: string) => {
    setEditing((v: any) => {
      const arr = Array.isArray(v?.[key]) ? v[key] : [];
      const next = arr.includes(value) ? arr.filter((x: string) => x !== value) : [...arr, value];
      return { ...v, [key]: next };
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.savePhotographerDispatchSettings(photographer.id, { can_take_shooting_order: canTake, brands: editing.brands || [], campuses: editing.campuses || [], cities: [], status: autoMatch ? "enabled" : "disabled" });
      toast("摄影师范围已保存");
      await done?.();
      close();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal wide-modal scope-single-modal">
        <div className="modal-head">
          <div><h2>设置摄影师范围</h2><p>{photographer.name} · {photographer.phone}</p></div>
          <button onClick={close}><X /></button>
        </div>
        <div className="scope-switch-grid">
          <label className="switch-card">
            <input type="checkbox" checked={canTake} onChange={e => setCanTake(e.target.checked)} />
            <span><b>是否允许接单</b><small>开启后，这个账号可以接收摄影派单。</small></span>
          </label>
          <label className="switch-card">
            <input type="checkbox" checked={autoMatch} onChange={e => setAutoMatch(e.target.checked)} />
            <span><b>是否参与自动匹配</b><small>开启后，一键匹配摄影师会按下面范围匹配。</small></span>
          </label>
        </div>
        <div className="scope-editor">
          <h3>负责品牌</h3>
          <p>多选，来自品牌服务卡；历史已选项会保留。品牌或校区任一未选择时不会自动匹配，只能手动派单。</p>
          <div className="check-grid">{brands.length ? brands.map(x => <label key={x}><input type="checkbox" checked={(editing.brands || []).includes(x)} onChange={() => toggle("brands", x)} />{x}{!brandConfigured(x) ? <small>历史已选</small> : null}</label>) : <div className="hint">请先在表单管理里新增品牌服务卡</div>}</div>
          <h3>负责校区 / 营地</h3>
          <p>多选，会跟随上面已选品牌显示；已保存但当前品牌未配置的校区会标为历史已选。</p>
          <div className="check-grid">{campuses.length ? campuses.map(x => <label key={x}><input type="checkbox" checked={(editing.campuses || []).includes(x)} onChange={() => toggle("campuses", x)} />{x}{!campusConfigured(x) ? <small>历史已选</small> : null}</label>) : <div className="hint">请先在品牌服务卡里配置校区/营地</div>}</div>
          <div className="modal-actions">
            <button className="secondary" onClick={close}>取消</button>
            <button className="primary" onClick={save} disabled={saving}>{saving ? "保存中..." : "保存"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

type ShootingTaskHubSection = "tasks" | "weather" | "nameCompare";

function ShootingTasksPage({ user, toast, initialSection = "tasks" }: { user: ApiUser; toast: (s: string) => void; initialSection?: ShootingTaskHubSection }) {
  const canViewTaskList = user.role === "admin" || can(user, "shooting.task.all.view");
  const canViewWeather = user.role === "admin" || can(user, "shooting.weather.view");
  const canCompareNames = user.role === "admin" || can(user, "shooting.task.assign") || can(user, "shooting.task.all.view") || can(user, "shooting.task.own.view");
  const allowedInitialSection: ShootingTaskHubSection = initialSection === "weather" && canViewWeather
    ? "weather"
    : initialSection === "nameCompare" && canCompareNames
      ? "nameCompare"
      : canViewTaskList
        ? "tasks"
        : canViewWeather
          ? "weather"
          : "nameCompare";
  const [hubSection, setHubSection] = useState<ShootingTaskHubSection>(allowedInitialSection);
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [brand, setBrand] = useState("");
  const [campus, setCampus] = useState("");
  const [packageName, setPackageName] = useState("");
  const [days, setDays] = useState("");
  const [reportDate, setReportDate] = useState("");
  const [nearEndOnly, setNearEndOnly] = useState(false);
  const [remindedOnly, setRemindedOnly] = useState(false);
  const [reviewedOnly, setReviewedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const canEditPhotographer = user.role === "admin" || can(user, "shooting.task.photographer.edit");
  const taskParams = {
    status,
    keyword,
    brand,
    campus,
    package_name: packageName,
    days,
    report_date: reportDate,
    near_end_only: nearEndOnly ? "true" : "",
    reminded_only: remindedOnly ? "true" : "",
    reviewed_only: reviewedOnly ? "true" : "",
    assigned_only: "true",
    page,
    per_page: perPage,
  };
  const tasks = useAsync(() => canViewTaskList
    ? api.shootingTasksPage(taskParams)
    : Promise.resolve({ items: [] as ShootingTask[], total: 0, page: 1, per_page: perPage, pages: 1 }), [canViewTaskList, JSON.stringify(taskParams)]);
  const stats = useAsync(() => canViewTaskList ? api.shootingStats() : Promise.resolve({}), [canViewTaskList]);
  const photographers = useAsync(() => canEditPhotographer ? api.shootingPhotographers() : Promise.resolve([] as ApiUser[]), [canEditPhotographer]);
  const isShot = (t: ShootingTask) => ["shot", "uploaded", "completed"].includes(String(t.shooting_status || "")) || ["pending_review", "approved"].includes(String(t.review_status || ""));
  const isReviewed = (t: ShootingTask) => ["approved", "rejected"].includes(String(t.review_status || ""));
  const brandOptions = (stats.data?.brands || []) as string[];
  const campusOptions = (stats.data?.campuses || []) as string[];
  const packageOptions = (stats.data?.packages || []) as string[];
  const dayOptions = (stats.data?.days || []) as string[];
  const reportDateOptions = (stats.data?.report_dates || []) as string[];
  const daysLeft = (value?: string) => {
    if (!value) return null;
    const target = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (Number.isNaN(target.getTime())) return null;
    return Math.ceil((target.getTime() - today.getTime()) / 86400000);
  };
  const visibleRows = (tasks.data?.items || []).filter(t => Boolean(t.photographer_id));
  const resetQuickFilters = () => {
    setPage(1);
    setNearEndOnly(false);
    setRemindedOnly(false);
    setReviewedOnly(false);
    setStatus("");
  };
  const resetAllFilters = () => {
    resetQuickFilters();
    setBrand("");
    setCampus("");
    setPackageName("");
    setDays("");
    setReportDate("");
    setKeyword("");
  };
  const assign = async (task: ShootingTask, pid: string) => {
    if (!pid) return;
    if (!canEditPhotographer) {
      toast("当前账号没有修改摄影师权限");
      return;
    }
    try {
      await api.assignShootingTask(task.id, pid);
      toast("已分配摄影师");
      await tasks.reload();
      await stats.reload();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const remindTask = async (task: ShootingTask) => {
    const left = daysLeft(task.end_date);
    const defaultNote = left !== null && left >= 0 && left <= 2 && !isShot(task)
      ? `订单${left === 0 ? "今天" : `${left}天后`}离营且未拍，请尽快拍摄并上传。`
      : "请尽快跟进这个拍摄任务。";
    const note = prompt("提醒摄影师内容", defaultNote);
    if (!note) return;
    try {
      await api.remindShootingTask(task.id, note);
      toast("已提醒摄影师，摄影师打开 H5 会弹窗看到");
      await tasks.reload();
      await stats.reload();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const hubTabs = <div className="shooting-task-hub-tabs">
    <div className="shooting-task-hub-heading">
      <span className="shooting-task-hub-icon"><Camera size={18} /></span>
      <div><b>拍摄任务中心</b><span>任务、天气和名单工具集中在一个页面</span></div>
    </div>
    <nav aria-label="拍摄任务功能切换">
      {canViewTaskList && <button className={hubSection === "tasks" ? "active" : ""} onClick={() => setHubSection("tasks")}><Camera size={16} /><span>任务列表</span></button>}
      {canViewWeather && <button className={hubSection === "weather" ? "active" : ""} onClick={() => setHubSection("weather")}><CloudSun size={16} /><span>拍摄天气</span></button>}
      {canCompareNames && <button className={hubSection === "nameCompare" ? "active" : ""} onClick={() => setHubSection("nameCompare")}><ClipboardCheck size={16} /><span>名单核对</span></button>}
    </nav>
  </div>;
  if (hubSection === "weather") return <>{hubTabs}<ShootingWeatherPage /></>;
  if (hubSection === "nameCompare") return <>{hubTabs}<NameComparePage toast={toast} /></>;
  return (
    <>
      {hubTabs}
      <PageTitle title="拍摄任务" desc="这里只显示已派单订单，用于跟进摄影师拍摄、上传和审图进度。" action={<button className="secondary" onClick={async () => { await tasks.reload(); await stats.reload(); }}><RefreshCw size={16} />刷新</button>} />
      <div className="stat-grid shooting-dashboard">
        <StatCard label="未拍摄订单" value={stats.data?.not_shot ?? "-"} active={status === "assigned"} onClick={() => { resetQuickFilters(); setStatus(status === "assigned" ? "" : "assigned"); }} />
        <StatCard label="已拍摄订单" value={stats.data?.shot ?? "-"} active={status === "shot"} onClick={() => { resetQuickFilters(); setStatus(status === "shot" ? "" : "shot"); }} />
        <StatCard label="未上传审图" value={stats.data?.shot_not_uploaded ?? "-"} active={status === "shot"} onClick={() => { resetQuickFilters(); setStatus("shot"); }} />
        <StatCard label="已审图" value={stats.data?.reviewed ?? "-"} active={reviewedOnly} onClick={() => { resetQuickFilters(); setReviewedOnly(v => !v); }} />
        <StatCard label="加急订单" value={stats.data?.reminded ?? "-"} active={remindedOnly} onClick={() => { resetQuickFilters(); setRemindedOnly(v => !v); }} />
        <button className={`stat-card urgent-stat ${nearEndOnly ? "active" : ""}`} onClick={() => { resetQuickFilters(); setNearEndOnly(v => !v); }}>
          <span>马上结营订单</span>
          <strong>{stats.data?.near_end ?? "-"}</strong>
        </button>
        <StatCard label="已完成" value={stats.data?.completed ?? "-"} active={status === "completed"} onClick={() => { resetQuickFilters(); setStatus(status === "completed" ? "" : "completed"); }} />
        <StatCard label="异常订单" value={stats.data?.issue ?? "-"} active={status === "issue"} onClick={() => { resetQuickFilters(); setStatus(status === "issue" ? "" : "issue"); }} />
      </div>
      <div className="toolbar dense">
        <select value={brand} onChange={e => { setPage(1); setBrand(e.target.value); }}><option value="">全部品牌</option>{brandOptions.map(v => <option key={v} value={v}>{v}</option>)}</select>
        <select value={campus} onChange={e => { setPage(1); setCampus(e.target.value); }}><option value="">全部校区</option>{campusOptions.map(v => <option key={v} value={v}>{v}</option>)}</select>
        <select value={packageName} onChange={e => { setPage(1); setPackageName(e.target.value); }}><option value="">全部款式</option>{packageOptions.map(v => <option key={v} value={v}>{v}</option>)}</select>
        <select value={days} onChange={e => { setPage(1); setDays(e.target.value); }}><option value="">全部天数</option>{dayOptions.map(v => <option key={v} value={v}>{v}</option>)}</select>
        <select value={reportDate} onChange={e => { setPage(1); setReportDate(e.target.value); }}><option value="">全部报道日期</option>{reportDateOptions.map(v => <option key={v} value={v}>{v}</option>)}</select>
        <input className="grow" value={keyword} onChange={e => { setPage(1); setKeyword(e.target.value); }} placeholder="搜索孩子/班级/任务号/摄影师" />
        {(nearEndOnly || remindedOnly || reviewedOnly || status || brand || campus || packageName || days || reportDate || keyword) && <button className="secondary" onClick={resetAllFilters}>重置筛选</button>}
      </div>
      <div className="table-card shooting-progress-table">
        {tasks.loading ? <Loading /> : visibleRows.length ? (
          <table><thead><tr><th>任务号</th><th>孩子</th><th>品牌</th><th>校区</th><th>班级</th><th>最后拍摄日</th><th>离营</th><th>摄影师</th><th>派单</th><th>拍摄</th><th>审图</th><th>备注</th><th>操作</th></tr></thead><tbody>
            {visibleRows.map(t => {
              const left = daysLeft(t.end_date);
              const assigned = Boolean(t.photographer_id);
              const shot = isShot(t);
              const reviewed = isReviewed(t);
              const urgent = left !== null && left >= 0 && left <= 2 && !shot;
              return <tr key={t.id} className={urgent ? "urgent-row" : ""}><td>{t.task_no}</td><td>{t.child_name}</td><td>{t.brand}</td><td>{t.campus}</td><td>{t.class_name}</td><td><b>{t.end_date || "-"}</b></td><td>{left === null ? "-" : left < 0 ? `超期 ${Math.abs(left)}天` : `${left}天`}</td><td>{canEditPhotographer ? <select value={t.photographer_id || ""} onChange={e => assign(t, e.target.value)}><option value="">选择摄影师</option>{photographers.data?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select> : <span>{t.photographer_name || "-"}</span>}</td><td><Badge text={assigned ? "已分配" : "未分配"} /></td><td><Badge text={shot ? "已拍摄" : "未拍摄"} /></td><td><Badge text={reviewed ? "已审图" : "未审图"} /></td><td><span>{t.special_note || "-"}</span>{urgent && <Badge text="马上结营未拍" />}{t.shooting_requirement?.startsWith("【提醒】") && <Badge text="已提醒" />}</td><td><button className="secondary" onClick={() => remindTask(t)}>提醒</button></td></tr>;
            })}
          </tbody></table>
        ) : <Empty text="暂无已派单拍摄任务。可以先去摄影师派单页生成并分配。" />}
      </div>
      <PaginationBar page={tasks.data?.page || page} pages={tasks.data?.pages || 1} total={tasks.data?.total || 0} perPage={perPage} onPage={setPage} onPerPage={value => { setPage(1); setPerPage(value); }} />
    </>
  );
}

function RefundFields({ form, set }: { form: Partial<OrderReport>; set: (key: keyof OrderReport, value: any) => void }) {
  const required = Boolean(form.refund_required);
  const amount = Number(form.amount || 0);
  const setRequired = (checked: boolean) => {
    set("refund_required", checked);
    if (!checked) {
      set("refund_type", "none");
      set("refund_amount", "");
      set("refund_paid", false);
    } else {
      set("refund_type", form.refund_type && form.refund_type !== "none" ? form.refund_type : "partial");
    }
  };
  const setType = (value: string) => {
    set("refund_type", value);
    if (value === "full") set("refund_amount", amount || "");
  };
  return (
    <div className="refund-panel span2">
      <div className="refund-panel-head">
        <div><b>退款信息</b><span>{required ? refundText(form) : "这个订单不需要退款"}</span></div>
        <label className="switch-line"><input type="checkbox" checked={required} onChange={e => setRequired(e.target.checked)} />需要退款</label>
      </div>
      {required && <div className="refund-grid">
        <label><span>退款类型</span><select value={form.refund_type || "partial"} onChange={e => setType(e.target.value)}><option value="partial">部分退款</option><option value="full">全额退款</option></select></label>
        <label><span>退款金额</span><input value={form.refund_amount ?? ""} disabled={form.refund_type === "full"} onChange={e => set("refund_amount", e.target.value)} placeholder="请输入退款金额" /></label>
        <label className="switch-line refund-paid"><input type="checkbox" checked={Boolean(form.refund_paid)} onChange={e => set("refund_paid", e.target.checked)} />已完成退款</label>
        <label><span>退款备注</span><input value={form.refund_note || ""} onChange={e => set("refund_note", e.target.value)} placeholder="可填写退款原因或流水备注" /></label>
      </div>}
    </div>
  );
}

function NameComparePage({ toast }: { toast: (s: string) => void }) {
  const [sentText, setSentText] = useState("");
  const [currentText, setCurrentText] = useState("");
  const cleanName = (value: string) => {
    const firstCell = String(value || "").split(/\t|,|，|;|；|\|/)[0] || "";
    return firstCell
      .replace(/^[\s　]*(?:\d+|[一二三四五六七八九十百千]+)[\.、)）．。:：-]?\s*/u, "")
      .replace(/[（(【\[].*?[】\])）]/g, "")
      .replace(/\s+/g, "")
      .trim();
  };
  const parseRows = (text: string) => text.split(/\r?\n/).map(cleanName).filter(Boolean);
  const countMap = (rows: string[]) => rows.reduce<Record<string, number>>((acc, name) => {
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const sentRows = useMemo(() => parseRows(sentText), [sentText]);
  const currentRows = useMemo(() => parseRows(currentText), [currentText]);
  const result = useMemo(() => {
    const sent = countMap(sentRows);
    const current = countMap(currentRows);
    const sentNames = Object.keys(sent);
    const currentNames = Object.keys(current);
    const sortCn = (a: string, b: string) => a.localeCompare(b, "zh-CN");
    return {
      sent,
      current,
      matched: sentNames.filter(name => current[name]).sort(sortCn),
      sentOnly: sentNames.filter(name => !current[name]).sort(sortCn),
      currentOnly: currentNames.filter(name => !sent[name]).sort(sortCn),
      duplicateSent: sentNames.filter(name => sent[name] > 1).sort(sortCn),
      duplicateCurrent: currentNames.filter(name => current[name] > 1).sort(sortCn),
    };
  }, [sentRows, currentRows]);
  const resultText = () => [
    "名单核对结果",
    `已发名单：${sentRows.length} 行，去重 ${Object.keys(result.sent).length} 人`,
    `现有名单：${currentRows.length} 行，去重 ${Object.keys(result.current).length} 人`,
    `两边都有：${result.matched.length} 人`,
    "",
    `已发有，现有没有：${result.sentOnly.length} 人`,
    result.sentOnly.length ? result.sentOnly.join("\n") : "无",
    "",
    `现有有，已发没有：${result.currentOnly.length} 人`,
    result.currentOnly.length ? result.currentOnly.join("\n") : "无",
    "",
    `已发重复：${result.duplicateSent.length} 人`,
    result.duplicateSent.length ? result.duplicateSent.map(name => `${name} x${result.sent[name]}`).join("\n") : "无",
    "",
    `现有重复：${result.duplicateCurrent.length} 人`,
    result.duplicateCurrent.length ? result.duplicateCurrent.map(name => `${name} x${result.current[name]}`).join("\n") : "无",
  ].join("\n");
  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(resultText());
      toast("核对结果已复制");
    } catch {
      toast("复制失败，请手动选择结果复制");
    }
  };
  const clearAll = () => {
    setSentText("");
    setCurrentText("");
    toast("名单已清空");
  };
  const renderList = (title: string, rows: string[], tone: string, map?: Record<string, number>) => (
    <section className={`name-compare-result ${tone}`}>
      <div><b>{title}</b><span>{rows.length} 项</span></div>
      {rows.length ? <ul>{rows.map(name => <li key={name}><span>{name}</span>{map && map[name] > 1 && <em>x{map[name]}</em>}</li>)}</ul> : <Empty text="暂无差异" />}
    </section>
  );
  return (
    <>
      <PageTitle title="名单核对" desc="粘贴已发名单和现有名单，快速找出漏发、多发和重复姓名。" action={<div className="action-row"><button className="secondary" onClick={clearAll}><Trash2 size={16} />清空</button><button className="primary" onClick={copyResult}><Copy size={16} />复制结果</button></div>} />
      <div className="name-compare-page">
        <div className="name-compare-inputs">
          <label>
            <span>已发名单</span>
            <textarea value={sentText} onChange={e => setSentText(e.target.value)} placeholder={"一行一个名字，例如：\n1. 张三\n2. 李四\n王五"} />
          </label>
          <label>
            <span>现有名单</span>
            <textarea value={currentText} onChange={e => setCurrentText(e.target.value)} placeholder={"粘贴系统现有名单或表格第一列，例如：\n张三\n李四\n赵六"} />
          </label>
        </div>
        <div className="name-compare-summary">
          <StatCard label="已发名单" value={sentRows.length} />
          <StatCard label="现有名单" value={currentRows.length} />
          <StatCard label="两边都有" value={result.matched.length} />
          <StatCard label="已发有现有没有" value={result.sentOnly.length} />
          <StatCard label="现有有已发没有" value={result.currentOnly.length} />
          <StatCard label="重复姓名" value={result.duplicateSent.length + result.duplicateCurrent.length} />
        </div>
        <div className="name-compare-results">
          {renderList("已发有，现有没有", result.sentOnly, "danger")}
          {renderList("现有有，已发没有", result.currentOnly, "warn")}
          {renderList("已发重复", result.duplicateSent, "neutral", result.sent)}
          {renderList("现有重复", result.duplicateCurrent, "neutral", result.current)}
        </div>
      </div>
    </>
  );
}

function ShootingWeatherPage() {
  const [days, setDays] = useState(7);
  const [cityFilter, setCityFilter] = useState("");
  const weather = useAsync(() => api.shootingWeather({ days, city: cityFilter }), [days, cityFilter]);
  const data = weather.data || {};
  const summary = data.summary || {};
  const cities = (data.cities || []) as any[];
  const riskRows = cities
    .flatMap(city => (city.risks || []).map((risk: any) => ({ ...risk, city: city.city })))
    .sort((a: any, b: any) => {
      const rank = (item: any) => item.level === "urgent" ? 0 : item.level === "warning" ? 1 : 2;
      return rank(a) - rank(b) || Number(a.days_left ?? 99) - Number(b.days_left ?? 99);
    });
  const cityLevelClass = (level?: string) => level === "urgent" ? "danger" : level === "bad" ? "danger" : level === "caution" ? "warn" : "good";
  const riskText = (level?: string) => level === "urgent" ? "紧急" : level === "warning" ? "预警" : "正常";
  const cityOptions = cities.map(item => String(item.city || "")).filter(Boolean);
  const topCities = [...cities]
    .sort((a, b) => Number(b.urgent_count || 0) - Number(a.urgent_count || 0) || Number(b.risk_count || 0) - Number(a.risk_count || 0))
    .slice(0, 4);
  const bestCities = cities.filter(city => city.level === "good").slice(0, 6).map(city => city.city).join("、") || "暂无";
  const strongestSignal = Number(summary.urgent_count || 0) > 0
    ? `${summary.urgent_count} 个订单需要优先确认`
    : Number(summary.risk_count || 0) > 0
      ? `${summary.risk_count} 个订单建议提前安排`
      : "当前天气风险较低";
  const weatherText = (item: any) => String(item?.text_day || item?.text_night || "待判断");
  const tempText = (item: any) => `${item?.temp_min || "-"}~${item?.temp_max || "-"}℃`;
  const riskPercent = Math.min(100, Math.round((Number(summary.risk_count || 0) / Math.max(Number(summary.pending_count || 0), 1)) * 100));
  const urgentRows = riskRows.slice(0, 6);
  const generatedAt = data.generated_at ? new Date(data.generated_at).toLocaleString("zh-CN", { hour12: false }) : "实时同步";
  const highRiskCities = cities.filter(city => ["danger", "warn"].includes(cityLevelClass(city.level))).length;
  const trendPoints = (forecast: any[] = [], field: "max" | "min" = "max") => {
    const rows = forecast.slice(0, 7);
    const nums = rows.map(item => Number(field === "max" ? item.temp_max : item.temp_min)).filter(num => Number.isFinite(num));
    if (!rows.length || !nums.length) return "";
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    const span = Math.max(max - min, 1);
    return rows.map((item, index) => {
      const raw = Number(field === "max" ? item.temp_max : item.temp_min);
      const value = Number.isFinite(raw) ? raw : min;
      const x = 8 + index * 18;
      const y = 34 - ((value - min) / span) * 22;
      return `${x},${y}`;
    }).join(" ");
  };
  const kpiTrend = (tone: string) => (
    <svg viewBox="0 0 132 42" aria-hidden="true" className={`weather-kpi-spark ${tone}`}>
      <polyline points="4,30 18,28 32,20 46,24 60,18 74,21 88,12 102,25 118,17 128,21" />
    </svg>
  );
  return (
    <>
      <section className="weather-workspace">
        <div className="weather-page-head">
          <div className="weather-page-brand"><Camera size={26} /></div>
          <div className="weather-page-title">
            <h1>拍摄天气工作台</h1>
            <p>结合天气与订单状态，智能辅助拍摄排期与营地运营决策</p>
          </div>
          <div className="weather-page-actions">
            <button className="weather-refresh light" onClick={weather.reload}><RefreshCw size={16} />刷新</button>
            <div className="weather-update-time">
              <b>{String(generatedAt).slice(0, 10)}</b>
              <span>更新于 {String(generatedAt).slice(11) || "实时"}</span>
            </div>
          </div>
        </div>
        <div className="weather-filter-bar">
          <label><span>时间范围</span><select value={days} onChange={e => setDays(Number(e.target.value) || 7)}><option value={7}>未来 7 天</option><option value={15}>未来 15 天</option></select></label>
          <label><span>城市范围</span><select value={cityFilter} onChange={e => setCityFilter(e.target.value)}><option value="">全部城市</option>{cityOptions.map(city => <option key={city} value={city}>{city}</option>)}</select></label>
          <label><span>订单状态</span><select disabled><option>全部状态</option></select></label>
          <label><span>营地类型</span><select disabled><option>全部类型</option></select></label>
          <button className="weather-advanced"><Search size={15} />高级筛选</button>
        </div>
        <div className="weather-overview-grid">
          <div className="weather-metric primary"><div className="weather-metric-icon"><ClipboardCheck size={22} /></div><span>未拍订单</span><strong>{summary.pending_count ?? "-"}</strong><small>需排期或确认完成状态</small>{kpiTrend("green")}</div>
          <div className="weather-metric danger"><div className="weather-metric-icon"><Bell size={22} /></div><span>紧急处理</span><strong>{summary.urgent_count ?? "-"}</strong><small>结营临近，优先联系摄影师</small>{kpiTrend("red")}</div>
          <div className="weather-metric warn"><div className="weather-metric-icon"><ShieldCheck size={22} /></div><span>风险城市</span><strong>{highRiskCities}</strong><small>天气风险较高，需关注调整</small>{kpiTrend("orange")}</div>
          <div className="weather-metric good"><div className="weather-metric-icon"><Camera size={22} /></div><span>适合拍摄</span><strong>{summary.good_city_count ?? "-"}</strong><small>{bestCities}</small>{kpiTrend("green")}</div>
          <div className="weather-metric primary"><div className="weather-metric-icon"><FileText size={22} /></div><span>今日优先确认</span><strong>{summary.urgent_count ?? "-"}</strong><small>更新于 {String(generatedAt).slice(11) || "实时"}</small><div className="weather-progress"><i style={{ width: `${Math.min(100, riskPercent)}%` }}></i></div></div>
        </div>
      </section>
      {data.provider_status !== "configured" && (
        <div className="warn-box weather-config-warning">
          天气服务还没有配置，当前只能展示待拍订单风险。配置 QWEATHER_API_KEY 后会显示真实 7/15 天天气。
        </div>
      )}
      {data.provider_status === "configured" && data.provider_message && (
        <div className="warn-box weather-config-warning">{data.provider_message}</div>
      )}
      {weather.loading ? <Loading /> : (
        <>
          <section className="weather-priority-panel weather-intel-panel weather-intel-pro">
            <div className="weather-intel-column">
              <div className="weather-section-head compact">
                <div>
                  <h2>城市风险榜</h2>
                  <p>按紧急程度和风险订单排序。</p>
                </div>
                <a>查看全部 <ChevronRight size={14} /></a>
              </div>
              <div className="weather-city-rank-list">
                {topCities.map((city, index) => (
                  <div className={`weather-city-rank-item ${cityLevelClass(city.level)}`} key={`priority-${city.city}`}>
                    <span className="weather-rank">{index + 1}</span>
                    <div>
                      <b>{city.city}</b>
                      <small>{city.pending_count || 0} 个未拍，{city.risk_count || 0} 个风险</small>
                    </div>
                    <Badge text={city.label || "待判断"} />
                  </div>
                ))}
                {!topCities.length && <Empty text="暂无优先城市" />}
              </div>
            </div>
            <div className="weather-intel-column">
              <div className="weather-section-head compact">
                <div>
                  <h2>临近结营队列</h2>
                  <p>先处理红色和橙色提醒。</p>
                </div>
                <a>查看全部 <ChevronRight size={14} /></a>
              </div>
              <div className="weather-urgent-list">
                {urgentRows.map((risk: any) => (
                  <div className={`weather-urgent-item ${risk.level}`} key={`urgent-${risk.city}-${risk.task_id}`}>
                    <span>{riskText(risk.level)}</span>
                    <div>
                      <b>{risk.child_name || "-"} · {risk.city || "-"}</b>
                      <small>{risk.campus || "-"} / {risk.end_date || "-"} / {risk.reason}</small>
                    </div>
                  </div>
                ))}
                {!urgentRows.length && <Empty text="暂无临近结营提醒" />}
              </div>
            </div>
          </section>
          <section className="weather-city-overview">
            <div className="weather-section-head compact">
              <div>
                <h2>城市天气概览</h2>
                <p>结营前存在降雨或天气不稳定的城市已标注，建议提前安排。</p>
              </div>
              <div className="weather-tabs"><button className="active">未来 7 天</button><button>未来 15 天</button><select value={cityFilter} onChange={e => setCityFilter(e.target.value)}><option value="">全部城市</option>{cityOptions.map(city => <option key={city} value={city}>{city}</option>)}</select></div>
            </div>
            <div className="weather-city-grid">
            {cities.map(city => {
              const todayWeather = (city.forecast || [])[0] || {};
              const badDays = (city.forecast || []).filter((item: any) => item.level === "bad").length;
              const forecast = (city.forecast || []).slice(0, 7);
              const cityTone = badDays > 0 ? "warn" : cityLevelClass(city.level) === "good" ? "good" : "neutral";
              return (
              <article className={`weather-city-card ${cityTone}`} key={city.city}>
                <div className="weather-city-head">
                  <div>
                    <h3>{city.city}</h3>
                    <p>{weatherText(todayWeather)} · {tempText(todayWeather)}</p>
                  </div>
                  <div className="weather-city-score">
                    <Badge text={city.label || "待判断"} />
                    <small>{city.pending_count || 0} 未拍 / {badDays} 天风险天气</small>
                  </div>
                </div>
                <div className="weather-days-strip">
                  {forecast.map((item: any) => (
                    <div className={`weather-day ${cityLevelClass(item.level)}`} key={`${city.city}-${item.date}`}>
                      <b>{String(item.date || "").slice(5) || "-"}</b>
                      <span>{item.text_day || "-"}</span>
                      <small>{item.temp_min || "-"}~{item.temp_max || "-"}℃</small>
                    </div>
                  ))}
                </div>
                <svg viewBox="0 0 132 42" className={`weather-city-trend ${cityLevelClass(city.level)}`} aria-hidden="true">
                  <polyline points={trendPoints(forecast, "max")} />
                </svg>
                <div className="weather-risk-mini">
                  {(city.risks || []).slice(0, 3).map((risk: any) => (
                    <div key={`${city.city}-${risk.task_id}`}>
                      <span className={`risk-dot ${risk.level}`}></span>
                      <b>{risk.child_name || "-"} · {risk.campus || "-"}</b>
                      <small>{risk.reason}</small>
                    </div>
                  ))}
                  {!(city.risks || []).length && <p className="muted">暂无明显拍摄风险</p>}
                </div>
              </article>
            );})}
            {!cities.length && <Empty text="暂无未拍摄订单或城市数据" />}
          </div>
          </section>
          <div className="table-card weather-risk-table">
            <div className="panel-head weather-section-head">
              <div>
                <h3>优先拍摄提醒</h3>
                <p>按紧急程度和结营时间排序。</p>
              </div>
            </div>
            {riskRows.length ? (
              <table>
                <thead><tr><th>风险</th><th>城市</th><th>订单</th><th>校区/班级</th><th>结营</th><th>建议</th></tr></thead>
                <tbody>{riskRows.slice(0, 80).map((risk: any) => (
                  <tr key={`${risk.city}-${risk.task_id}`}>
                    <td><Badge text={riskText(risk.level)} /></td>
                    <td>{risk.city || "-"}</td>
                    <td><b>{risk.child_name || "-"}</b><span>{risk.brand || "-"} / {risk.package_name || "-"}</span></td>
                    <td>{risk.campus || "-"} / {risk.class_name || "-"}</td>
                    <td>{risk.end_date || "-"}<br /><small>{Number(risk.days_left) >= 0 ? `${risk.days_left} 天后` : `已超 ${Math.abs(Number(risk.days_left || 0))} 天`}</small></td>
                    <td>{risk.reason}{risk.suggested_date && <small>建议 {risk.suggested_date} 前安排</small>}</td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <Empty text="暂无优先拍摄提醒" />}
          </div>
        </>
      )}
    </>
  );
}

function ReviewCenterPage({ toast }: { toast: (s: string) => void }) {
  const [status, setStatus] = useState("pending_review");
  const [reviewViewMode, setReviewViewMode] = useState<"grid" | "list">("grid");
  const [nearEndOnly, setNearEndOnly] = useState(false);
  const [issueOnly, setIssueOnly] = useState(false);
  const [brandFilter, setBrandFilter] = useState("");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [activeGroup, setActiveGroup] = useState<{ key: string; task?: ShootingTask; uploads: ShootingUpload[] } | null>(null);
  const uploadParams = useMemo(() => ({
    by_task: "true",
    group_status: !nearEndOnly && !issueOnly ? status : "",
    brand: !nearEndOnly && !issueOnly ? brandFilter : "",
    keyword: !nearEndOnly && !issueOnly ? keyword.trim() : "",
    page,
    per_page: perPage,
  }), [brandFilter, issueOnly, keyword, nearEndOnly, page, perPage, status]);
  const taskParams = useMemo(() => ({
    status: issueOnly ? "issue" : "",
    near_end_only: nearEndOnly ? "true" : "",
    brand: issueOnly || nearEndOnly ? brandFilter : "",
    keyword: issueOnly || nearEndOnly ? keyword.trim() : "",
    page,
    per_page: perPage,
  }), [brandFilter, issueOnly, keyword, nearEndOnly, page, perPage]);
  const emptyTaskPage = { items: [] as ShootingTask[], total: 0, page: 1, per_page: perPage, pages: 1 };
  const uploads = useAsync(() => api.reviewerUploadsPage(uploadParams), [JSON.stringify(uploadParams)]);
  const tasks = useAsync(() => (issueOnly || nearEndOnly ? api.shootingTasksPage(taskParams) : Promise.resolve(emptyTaskPage)).catch(() => emptyTaskPage), [JSON.stringify(taskParams), issueOnly, nearEndOnly, perPage]);
  const issueCount = useAsync(() => api.shootingTasksPage({ status: "issue", page: 1, per_page: 1 }).catch(() => emptyTaskPage), []);
  const nearEndCount = useAsync(() => api.shootingTasksPage({ near_end_only: "true", page: 1, per_page: 1 }).catch(() => emptyTaskPage), []);
  const stats = useAsync(api.reviewerStats, []);

  const daysLeft = (value?: string) => {
    if (!value) return null;
    const target = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (Number.isNaN(target.getTime())) return null;
    return Math.ceil((target.getTime() - today.getTime()) / 86400000);
  };

  const allGroups = useMemo(() => {
    const map = new Map<string, { key: string; task?: ShootingTask; uploads: ShootingUpload[] }>();
    for (const item of uploads.data?.items || []) {
      const key = String(item.task_id || item.task?.id || item.id);
      const old = map.get(key);
      if (old) old.uploads.push(item);
      else map.set(key, { key, task: item.task, uploads: [item] });
    }
    return Array.from(map.values());
  }, [uploads.data]);

  const groupStatus = (group: { uploads: ShootingUpload[] }) => {
    if (group.uploads.some(u => u.review_status === "pending_review")) return "pending_review";
    if (group.uploads.some(u => u.review_status === "rejected")) return "rejected";
    if (group.uploads.length && group.uploads.every(u => u.review_status === "approved")) return "approved";
    return "pending_review";
  };

  const nearEndNoUploadTasks = useMemo(() => nearEndOnly ? (tasks.data?.items || []) : [], [nearEndOnly, tasks.data]);
  const issueTasks = useMemo(() => issueOnly ? (tasks.data?.items || []) : [], [issueOnly, tasks.data]);

  const orderStats = useMemo(() => ({
    pending_review: stats.data?.pending_review ?? allGroups.filter(group => groupStatus(group) === "pending_review").length,
    approved: stats.data?.approved ?? allGroups.filter(group => groupStatus(group) === "approved").length,
    rejected: stats.data?.rejected ?? allGroups.filter(group => groupStatus(group) === "rejected").length,
    total: stats.data?.total ?? uploads.data?.total ?? allGroups.length,
    near_end_no_upload: nearEndCount.data?.total ?? nearEndNoUploadTasks.length,
    issue: issueCount.data?.total ?? issueTasks.length,
  }), [allGroups, issueCount.data, issueTasks, nearEndCount.data, nearEndNoUploadTasks, stats.data, uploads.data]);

  const baseGroups = useMemo(() => {
    if (issueOnly) {
      return issueTasks.map(task => ({ key: `issue-${task.id}`, task, uploads: [] as ShootingUpload[] }));
    }
    if (nearEndOnly) {
      return nearEndNoUploadTasks.map(task => ({ key: `task-${task.id}`, task, uploads: [] as ShootingUpload[] }));
    }
    return allGroups;
  }, [allGroups, issueOnly, issueTasks, nearEndNoUploadTasks, nearEndOnly, status]);

  const brandOptions = useMemo(() => {
    return Array.from(new Set([...(stats.data?.brands || []), ...allGroups.map(group => group.task?.brand)].filter(Boolean).map(String))).sort((a, b) => a.localeCompare(b, "zh-CN"));
  }, [allGroups, stats.data]);

  const groups = useMemo(() => {
    return baseGroups;
  }, [baseGroups]);

  const resetReviewFilters = () => {
    setPage(1);
    setNearEndOnly(false);
    setIssueOnly(false);
    setStatus("");
    setBrandFilter("");
    setKeyword("");
  };

  const approveGroup = async (group: { uploads: ShootingUpload[] }) => {
    try {
      const rows = group.uploads.filter(u => u.review_status !== "approved");
      if (!rows.length) {
        toast("这个订单还没有可审核图片");
        return;
      }
      for (const item of rows) await api.approveUpload(item.id);
      toast(`整单已通过，共 ${rows.length} 张图片`);
      await uploads.reload();
      await tasks.reload();
      await stats.reload();
      setActiveGroup(null);
    } catch (e) {
      toast(`整单通过失败：${(e as Error).message}`);
    }
  };
  const rejectGroup = async (group: { uploads: ShootingUpload[] }) => {
    try {
      const note = prompt("请输入驳回整改意见");
      if (!note) return;
      const rows = group.uploads.filter(u => u.review_status !== "rejected");
      if (!rows.length) {
        toast("这个订单还没有可驳回图片");
        return;
      }
      for (const item of rows) await api.rejectUpload(item.id, note);
      toast(`整单已驳回，共 ${rows.length} 张图片`);
      await uploads.reload();
      await tasks.reload();
      await stats.reload();
      setActiveGroup(null);
    } catch (e) {
      toast(`整单驳回失败：${(e as Error).message}`);
    }
  };
  const remindTask = async (task?: ShootingTask) => {
    if (!task?.id) {
      toast("这个订单没有关联拍摄任务，不能提醒");
      return;
    }
    const left = daysLeft(task.end_date);
    const defaultNote = left !== null && left >= 0 && left <= 2 && !taskAlreadyShot(task)
      ? `订单${left === 0 ? "今天" : `${left}天后`}离营且未拍/未传，请尽快处理。`
      : "请尽快跟进这个拍摄任务。";
    const note = prompt("提醒摄影师内容", defaultNote);
    if (!note) return;
    try {
      await api.remindShootingTask(task.id, note);
      toast("已提醒摄影师，摄影师打开 H5 会弹窗看到");
      await tasks.reload();
      await uploads.reload();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const pageData = issueOnly || nearEndOnly ? tasks.data : uploads.data;
  return (
    <>
      <PageTitle title="审图中心" desc="查看摄影师上传图片，通过或驳回整改。" action={<button className="secondary" onClick={async () => { await uploads.reload(); await tasks.reload(); await stats.reload(); }}><RefreshCw size={16} />刷新</button>} />
      <div className="stat-grid review-dashboard">
        <StatCard label="全部审图订单" value={orderStats.total || stats.data?.total || "-"} active={!nearEndOnly && !issueOnly && status === ""} onClick={() => { setPage(1); setNearEndOnly(false); setIssueOnly(false); setStatus(""); }} />
        <StatCard label="待审订单" value={orderStats.pending_review} active={!nearEndOnly && !issueOnly && status === "pending_review"} onClick={() => { setPage(1); setNearEndOnly(false); setIssueOnly(false); setStatus("pending_review"); }} />
        <StatCard label="已通过订单" value={orderStats.approved} active={!nearEndOnly && !issueOnly && status === "approved"} onClick={() => { setPage(1); setNearEndOnly(false); setIssueOnly(false); setStatus("approved"); }} />
        <StatCard label="已驳回订单" value={orderStats.rejected} active={!nearEndOnly && !issueOnly && status === "rejected"} onClick={() => { setPage(1); setNearEndOnly(false); setIssueOnly(false); setStatus("rejected"); }} />
        <StatCard label="异常拍摄" value={orderStats.issue} active={issueOnly} onClick={() => { setPage(1); setIssueOnly(true); setNearEndOnly(false); setStatus(""); }} />
        <StatCard label="即将离营未传" value={orderStats.near_end_no_upload} active={nearEndOnly} onClick={() => { setPage(1); setNearEndOnly(true); setIssueOnly(false); setStatus(""); }} />
      </div>
      <div className="review-filter-bar">
        <div className="review-filter-main">
          <select value={status} disabled={nearEndOnly || issueOnly} onChange={e => { setPage(1); setNearEndOnly(false); setIssueOnly(false); setStatus(e.target.value); }}>
            <option value="">全部审图订单</option>
            <option value="pending_review">未审图</option>
            <option value="approved">已审图</option>
            <option value="rejected">驳回重拍</option>
          </select>
          <select value={brandFilter} onChange={e => { setPage(1); setBrandFilter(e.target.value); }}>
            <option value="">全部品牌</option>
            {brandOptions.map(brand => <option key={brand} value={brand}>{brand}</option>)}
          </select>
          <div className="search-box review-search">
            <Search size={16} />
            <input value={keyword} onChange={e => { setPage(1); setKeyword(e.target.value); }} placeholder="关键词搜索，空格可叠加" />
          </div>
          <button className="secondary" onClick={resetReviewFilters}>重置</button>
        </div>
        <div className="review-view-toggle" aria-label="审图订单显示方式">
          <button className={reviewViewMode === "grid" ? "active" : ""} onClick={() => setReviewViewMode("grid")} title="横排卡片">
            <Grid2X2 size={16} />横排
          </button>
          <button className={reviewViewMode === "list" ? "active" : ""} onClick={() => setReviewViewMode("list")} title="列表">
            <List size={16} />列表
          </button>
        </div>
      </div>
      {(uploads.loading || tasks.loading) ? <Loading /> : reviewViewMode === "grid" ? (
        <div className="review-card-grid">
          {groups.map(group => {
            const task = group.task;
            const first = group.uploads[0];
            const pending = group.uploads.filter(u => u.review_status === "pending_review").length;
            const approved = group.uploads.filter(u => u.review_status === "approved").length;
            const rejected = group.uploads.filter(u => u.review_status === "rejected").length;
            const left = daysLeft(task?.end_date);
            return <article className="review-order-card" key={group.key}>
              <div className="review-card-cover">
                {group.uploads[0] ? <CachedPreviewImage upload={group.uploads[0]} /> : <div className="review-card-empty">未上传</div>}
                <span>{group.uploads.length ? `${group.uploads.length}张` : "未传"}</span>
              </div>
              <div className="review-card-body">
                <b>{task?.child_name || first?.file_name || "未填孩子"}</b>
                <small>{task?.class_name || "未填班级"} · {task?.task_no || `#${task?.id || first?.task_id || "-"}`}</small>
                <p>{task?.brand || "-"} / {task?.campus || "-"} / {task?.package_name || "-"}</p>
                <div className="review-card-line">
                  <span>摄影师</span><strong>{first?.photographer_name || task?.photographer_name || "-"}</strong>
                </div>
                <div className="review-card-line">
                  <span>最后拍摄</span><strong>{task?.end_date || "-"}</strong>
                </div>
                <div className="review-card-badges">
                  {group.uploads.length ? <>
                    <Badge text={`待审 ${pending}`} />
                    <Badge text={`通过 ${approved}`} />
                    <Badge text={`驳回 ${rejected}`} />
                  </> : <Badge text={`剩余 ${left ?? "-"} 天`} />}
                  {task && left !== null && left <= 2 && !taskAlreadyShot(task) && <Badge text="马上结营未拍" />}
                  {task?.shooting_requirement?.startsWith("【提醒】") && <Badge text="已提醒" />}
                </div>
                <p className="review-card-note">{task?.special_note || first?.upload_note || "无备注"}</p>
              </div>
              <div className="review-card-actions">
                <button className="secondary" onClick={() => remindTask(task)}>提醒</button>
                <button className="primary" disabled={!group.uploads.length} onClick={() => setActiveGroup(group)}>{group.uploads.length ? "审图" : "待上传"}</button>
              </div>
            </article>;
          })}
          {!groups.length && <Empty text="暂无审图订单" />}
        </div>
      ) : (
        <div className="review-order-list">
          <div className="review-order-head">
            <span>照片</span>
            <span>订单信息</span>
            <span>拍摄信息</span>
            <span>审图状态</span>
            <span>备注</span>
            <span>操作</span>
          </div>
          {groups.map(group => {
            const task = group.task;
            const first = group.uploads[0];
            const pending = group.uploads.filter(u => u.review_status === "pending_review").length;
            const approved = group.uploads.filter(u => u.review_status === "approved").length;
            const rejected = group.uploads.filter(u => u.review_status === "rejected").length;
            return <article className="review-order-row" key={group.key}>
              <div className="review-thumb-stack">
                {group.uploads.slice(0, 3).map((u, idx) => <CachedPreviewImage key={u.id} upload={u} style={{ transform: `translateX(${idx * 16}px)` }} />)}
                <span>{group.uploads.length ? `${group.uploads.length}张` : "未传"}</span>
              </div>
              <div className="review-order-main">
                <b>{task?.child_name || first?.file_name || "未填孩子"} · {task?.class_name || "未填班级"}</b>
                <span>{task?.brand || "-"} / {task?.campus || "-"} / {task?.package_name || "-"}</span>
                <small>{task?.task_no || `#${task?.id || first?.task_id || "-"}`}</small>
              </div>
              <div className="review-shoot-info">
                <span>摄影师：{first?.photographer_name || task?.photographer_name || "-"}</span>
                <span>最后拍摄：{task?.end_date || "-"}</span>
                <span>上传：{group.uploads.length || 0} 张</span>
              </div>
              <div className="review-order-meta">
                {group.uploads.length ? <>
                  <Badge text={`待审 ${pending}`} />
                  <Badge text={`通过 ${approved}`} />
                  <Badge text={`驳回 ${rejected}`} />
                </> : <Badge text={`剩余 ${daysLeft(task?.end_date) ?? "-"} 天`} />}
                {task && daysLeft(task.end_date) !== null && Number(daysLeft(task.end_date)) <= 2 && !taskAlreadyShot(task) && <Badge text="马上结营未拍" />}
                {task?.shooting_requirement?.startsWith("【提醒】") && <Badge text="已提醒" />}
              </div>
              <div className="review-note-cell">
                <p>{task?.special_note || first?.upload_note || "无备注"}</p>
                {task?.shooting_requirement && <small>{task.shooting_requirement}</small>}
              </div>
              <div className="review-row-actions">
                <button className="secondary" onClick={() => remindTask(task)}>提醒</button>
                <button className="primary" disabled={!group.uploads.length} onClick={() => setActiveGroup(group)}>{group.uploads.length ? "审图" : "待上传"}</button>
              </div>
            </article>;
          })}
          {!groups.length && <Empty text="暂无审图订单" />}
        </div>
      )}
      <PaginationBar page={pageData?.page || page} pages={pageData?.pages || 1} total={pageData?.total || 0} perPage={perPage} onPage={setPage} onPerPage={value => { setPage(1); setPerPage(value); }} />
      {activeGroup && <ReviewOrderModal group={activeGroup} close={() => setActiveGroup(null)} approve={() => approveGroup(activeGroup)} reject={() => rejectGroup(activeGroup)} toast={toast} reload={async () => { await uploads.reload(); await tasks.reload(); await stats.reload(); }} />}
    </>
  );
}

function DownloadCenterModal({ jobs, loading, reload, close, toast }: { jobs: ReviewDownloadJob[]; loading: boolean; reload: () => Promise<void>; close: () => void; toast: (s: string) => void }) {
  const jobTypeText = (job: ReviewDownloadJob) => job.job_type === "review_originals_approved" ? "审图通过原图包" : "全部原图包";
  const retryDownloadJob = async (job: ReviewDownloadJob) => {
    try {
      await api.retryReviewDownloadJob(job.id);
      await reload();
      toast("已重新开始生成");
    } catch (e) {
      toast((e as Error).message || "重试失败，请稍后再试");
    }
  };
  const openJobFile = async (job: ReviewDownloadJob) => {
    try {
      const info = await api.reviewDownloadJobUrl(job.id);
      await api.openDownloadUrl(info.url, info.filename || job.file_name || "修图原图.zip");
      await reload();
    } catch (e) {
      toast((e as Error).message || "下载链接生成失败，请刷新后重试");
    }
  };
  const expiredJob = (job: ReviewDownloadJob) => {
    if (job.expired || job.status === "expired") return true;
    if (!job.expires_at) return false;
    return new Date(job.expires_at).getTime() <= Date.now();
  };
  const jobStatusText = (job: ReviewDownloadJob) => {
    if (expiredJob(job)) return "已过期";
    if (job.status === "queued") return "排队中";
    if (job.status === "running") return "生成中";
    if (job.status === "completed") return "已完成";
    if (job.status === "failed") return "失败";
    return String(job.status || "-");
  };
  const retryExpiredJob = async (job: ReviewDownloadJob) => {
    try {
      await api.retryReviewDownloadJob(job.id);
      await reload();
      toast("已重新生成下载包");
    } catch (e) {
      toast((e as Error).message || "重新生成失败，请稍后再试");
    }
  };
  const downloadMetaText = (job: ReviewDownloadJob) => {
    const parts = [jobTypeText(job), `${job.finished_count || 0}/${job.total_count || 0} 个订单`, cnDate(job.created_at)];
    if (job.status === "completed" && job.file_size) parts.push(fileSizeText(job.file_size));
    if (job.expires_at && !expiredJob(job)) parts.push(`有效期至 ${cnDate(job.expires_at)}`);
    if (job.download_count) parts.push(`已下载 ${job.download_count} 次`);
    return parts.join(" · ");
  };
  return (
    <div className="modal-backdrop">
      <section className="modal download-center-modal">
        <div className="panel-head compact">
          <div>
            <h3>下载中心</h3>
            <p>原图包会在后台生成，点击刷新进度查看最新状态，完成后再点击下载文件。</p>
          </div>
          <div className="download-center-head-actions">
            <button className="secondary" onClick={reload}><RefreshCw size={16} />刷新进度</button>
            <button className="icon-btn" onClick={close}><X size={18} /></button>
          </div>
        </div>
        <div className="download-job-list">
          {loading ? <Loading /> : jobs.length ? jobs.map(job => {
            const progress = Math.max(0, Math.min(100, Number(job.progress || 0)));
            const isActiveJob = ["queued", "running"].includes(String(job.status));
            return (
              <article className={`download-job ${job.status} ${isActiveJob ? "active" : ""}`} key={job.id}>
              <div className="download-job-main">
                <b>{job.title || job.file_name || `下载任务 #${job.id}`}</b>
                <span>{downloadMetaText(job)}</span>
                {job.error_message && <small>{job.error_message}</small>}
              </div>
              <div className="download-progress">
                <i style={{ width: `${progress}%` }} />
              </div>
              <span className="download-progress-text">{progress}%</span>
              <Badge text={jobStatusText(job)} />
              <div className="download-job-actions">
                {job.status === "completed" && !expiredJob(job) ? <button className="primary" onClick={() => openJobFile(job)}><Download size={15} />下载文件</button> : null}
                {expiredJob(job) ? <button className="secondary" onClick={() => retryExpiredJob(job)}>重新生成</button> : null}
                {job.status === "failed" ? <button className="secondary" onClick={() => retryDownloadJob(job)}>重试</button> : null}
              </div>
              </article>
            );
          }) : <Empty text="暂无下载任务，点击单个原图或批量下载后会出现在这里" />}
        </div>
      </section>
    </div>
  );
}

function RetouchCenterPage({ toast }: { toast: (s: string) => void }) {
  const [brandFilter, setBrandFilter] = useState("");
  const [downloadStatus, setDownloadStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [retouchViewMode, setRetouchViewMode] = useState<"grid" | "list">("grid");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [selectedGroups, setSelectedGroups] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [showDownloadCenter, setShowDownloadCenter] = useState(false);
  const [activeGroup, setActiveGroup] = useState<{ key: string; task?: ShootingTask; uploads: ShootingUpload[] } | null>(null);
  const uploadParams = useMemo(() => ({
    review_status: "approved",
    by_task: "true",
    brand: brandFilter,
    download_status: downloadStatus,
    keyword: keyword.trim(),
    page,
    per_page: perPage,
  }), [brandFilter, downloadStatus, keyword, page, perPage]);
  const uploads = useAsync(() => api.reviewerUploadsPage(uploadParams), [JSON.stringify(uploadParams)]);
  const stats = useAsync(api.reviewerStats, []);
  const downloadJobs = useAsync(() => api.reviewDownloadJobs().catch(() => [] as ReviewDownloadJob[]), []);

  const allGroups = useMemo(() => {
    const map = new Map<string, { key: string; task?: ShootingTask; uploads: ShootingUpload[] }>();
    for (const item of uploads.data?.items || []) {
      const key = String(item.task_id || item.task?.id || item.id);
      const old = map.get(key);
      if (old) old.uploads.push(item);
      else map.set(key, { key, task: item.task, uploads: [item] });
    }
    return Array.from(map.values());
  }, [uploads.data]);
  const brandOptions = useMemo(() => Array.from(new Set([...(stats.data?.brands || []), ...allGroups.map(group => group.task?.brand)].filter(Boolean).map(String))).sort((a, b) => a.localeCompare(b, "zh-CN")), [allGroups, stats.data]);
  const groups = useMemo(() => {
    return allGroups;
  }, [allGroups]);
  const selectedDownloadGroups = groups.filter(group => group.task?.id && group.uploads.length && selectedGroups[group.key]);
  useEffect(() => {
    setSelectedGroups(current => {
      const keys = new Set(groups.map(group => group.key));
      const next = Object.fromEntries(Object.entries(current).filter(([key]) => keys.has(key)));
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [groups]);
  const createDownload = async (rows: { key: string; task?: ShootingTask; uploads: ShootingUpload[] }[], scope: "all" | "approved") => {
    const ids = rows.map(group => group.task?.id).filter(Boolean) as Array<string | number>;
    if (!ids.length) {
      toast("请先选择有图片的已通过订单");
      return;
    }
    const scopeName = scope === "approved" ? "审图通过原图" : "全部原图";
    setBusy(true);
    try {
      await api.createReviewDownloadJob(ids, ids.length === 1 ? `${rows[0].task?.child_name || "订单"} ${scopeName}下载` : `${scopeName}批量下载 ${ids.length} 个订单`, scope);
      setShowDownloadCenter(true);
      await downloadJobs.reload();
      toast("已加入下载中心");
    } catch (e) {
      toast((e as Error).message || "创建下载任务失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  };
  const selectCurrent = (checked: boolean) => {
    setSelectedGroups(current => {
      const next = { ...current };
      groups.forEach(group => {
        if (!group.task?.id || !group.uploads.length) return;
        if (checked) next[group.key] = true;
        else delete next[group.key];
      });
      return next;
    });
  };
  return (
    <>
      <PageTitle title="修图中心" desc="查看已通过审图的订单，预览图片并打包下载原图。" action={<button className="secondary" onClick={async () => { await uploads.reload(); await stats.reload(); }}><RefreshCw size={16} />刷新</button>} />
      <div className="stat-grid review-dashboard">
        <StatCard label="已通过订单" value={uploads.data?.total ?? allGroups.length} active />
        <StatCard label="当前筛选" value={groups.length} />
        <StatCard label="已选订单" value={selectedDownloadGroups.length} />
      </div>
      <div className="review-filter-bar">
        <div className="review-filter-main">
          <select value={brandFilter} onChange={e => { setPage(1); setBrandFilter(e.target.value); }}>
            <option value="">全部品牌</option>
            {brandOptions.map(brand => <option key={brand} value={brand}>{brand}</option>)}
          </select>
          <select value={downloadStatus} onChange={e => { setPage(1); setDownloadStatus(e.target.value); }}>
            <option value="">全部下载状态</option>
            <option value="downloaded">已下载</option>
            <option value="not_downloaded">未下载</option>
          </select>
          <div className="search-box review-search">
            <Search size={16} />
            <input value={keyword} onChange={e => { setPage(1); setKeyword(e.target.value); }} placeholder="搜索姓名、班级、校区、款式，空格可叠加" />
          </div>
          <button className="secondary" onClick={() => { setPage(1); setBrandFilter(""); setDownloadStatus(""); setKeyword(""); }}>重置</button>
        </div>
        <div className="review-bulk-actions">
          <div className="review-view-toggle" aria-label="修图订单显示方式">
            <button className={retouchViewMode === "grid" ? "active" : ""} onClick={() => setRetouchViewMode("grid")} title="横排卡片">
              <Grid2X2 size={16} />横排
            </button>
            <button className={retouchViewMode === "list" ? "active" : ""} onClick={() => setRetouchViewMode("list")} title="列表">
              <List size={16} />列表
            </button>
          </div>
          <label><input type="checkbox" checked={groups.length > 0 && groups.every(group => selectedGroups[group.key] || !group.task?.id || !group.uploads.length)} onChange={e => selectCurrent(e.target.checked)} /> 全选当前</label>
          <span>已选 {selectedDownloadGroups.length} 个订单</span>
          <button className="secondary" onClick={() => { setShowDownloadCenter(true); void downloadJobs.reload(); }}><Download size={16} />下载中心</button>
          <button className="secondary" disabled={busy || !selectedDownloadGroups.length} onClick={() => createDownload(selectedDownloadGroups, "all")}><Download size={16} />批量全部原图</button>
          <button className="primary" disabled={busy || !selectedDownloadGroups.length} onClick={() => createDownload(selectedDownloadGroups, "approved")}><Download size={16} />批量通过原图</button>
        </div>
      </div>
      {uploads.loading ? <Loading /> : retouchViewMode === "grid" ? (
        <div className="review-card-grid retouch-card-grid">
          {groups.map(group => {
            const task = group.task;
            const first = group.uploads[0];
            const checked = Boolean(selectedGroups[group.key]);
            return <article className={`review-order-card retouch-order-card${checked ? " selected" : ""}`} key={group.key}>
              <label className="retouch-card-check">
                <input type="checkbox" disabled={!task?.id || !group.uploads.length} checked={checked} onChange={e => setSelectedGroups(v => ({ ...v, [group.key]: e.target.checked }))} />
                选择
              </label>
              <div className="review-card-cover">
                {group.uploads[0] ? <CachedPreviewImage upload={group.uploads[0]} /> : <div className="review-card-empty">未上传</div>}
                <span>{group.uploads.length}张</span>
              </div>
              <div className="review-card-body">
                <b>{task?.child_name || first?.file_name || "未填孩子"}</b>
                <small>{task?.class_name || "未填班级"} · {task?.task_no || `#${task?.id || first?.task_id || "-"}`}</small>
                <p>{task?.brand || "-"} / {task?.campus || "-"} / {task?.package_name || "-"}</p>
                <div className="review-card-line">
                  <span>摄影师</span><strong>{first?.photographer_name || task?.photographer_name || "-"}</strong>
                </div>
                <div className="review-card-line">
                  <span>通过图片</span><strong>{group.uploads.length} 张</strong>
                </div>
                <div className="review-card-line">
                  <span>最后拍摄</span><strong>{task?.end_date || "-"}</strong>
                </div>
                <p className="review-card-note">{task?.special_note || first?.upload_note || "无备注"}</p>
              </div>
              <div className="review-card-actions">
                <button className="secondary" disabled={!group.uploads.length} onClick={() => setActiveGroup(group)}>查看</button>
                <button className="secondary" disabled={busy || !task?.id || !group.uploads.length} onClick={() => createDownload([group], "all")}><Download size={15} />全部原图</button>
                <button className="primary" disabled={busy || !task?.id || !group.uploads.length} onClick={() => createDownload([group], "approved")}><Download size={15} />通过原图</button>
              </div>
            </article>;
          })}
          {!groups.length && <Empty text="暂无已通过订单" />}
        </div>
      ) : (
        <div className="review-order-list">
          <div className="retouch-order-head">
            <span>选择</span>
            <span>照片</span>
            <span>订单信息</span>
            <span>拍摄信息</span>
            <span>备注</span>
            <span>操作</span>
          </div>
          {groups.map(group => {
            const task = group.task;
            const first = group.uploads[0];
            return <article className="retouch-order-row" key={group.key}>
              <label className="review-row-check">
                <input type="checkbox" disabled={!task?.id || !group.uploads.length} checked={Boolean(selectedGroups[group.key])} onChange={e => setSelectedGroups(v => ({ ...v, [group.key]: e.target.checked }))} />
              </label>
              <div className="review-thumb-stack">
                {group.uploads.slice(0, 3).map((u, idx) => <CachedPreviewImage key={u.id} upload={u} style={{ transform: `translateX(${idx * 16}px)` }} />)}
                <span>{group.uploads.length}张</span>
              </div>
              <div className="review-order-main">
                <b>{task?.child_name || first?.file_name || "未填孩子"} · {task?.class_name || "未填班级"}</b>
                <span>{task?.brand || "-"} / {task?.campus || "-"} / {task?.package_name || "-"}</span>
                <small>{task?.task_no || `#${task?.id || first?.task_id || "-"}`}</small>
              </div>
              <div className="review-shoot-info">
                <span>摄影师：{first?.photographer_name || task?.photographer_name || "-"}</span>
                <span>通过图片：{group.uploads.length} 张</span>
                <span>最后拍摄：{task?.end_date || "-"}</span>
              </div>
              <div className="review-note-cell">
                <p>{task?.special_note || first?.upload_note || "无备注"}</p>
              </div>
              <div className="review-row-actions">
                <button className="secondary" disabled={!group.uploads.length} onClick={() => setActiveGroup(group)}>查看</button>
                <button className="secondary" disabled={busy || !task?.id || !group.uploads.length} onClick={() => createDownload([group], "all")}><Download size={15} />全部原图</button>
                <button className="primary" disabled={busy || !task?.id || !group.uploads.length} onClick={() => createDownload([group], "approved")}><Download size={15} />通过原图</button>
              </div>
            </article>;
          })}
          {!groups.length && <Empty text="暂无已通过订单" />}
        </div>
      )}
      <PaginationBar page={uploads.data?.page || page} pages={uploads.data?.pages || 1} total={uploads.data?.total || 0} perPage={perPage} onPage={setPage} onPerPage={value => { setPage(1); setPerPage(value); }} />
      {activeGroup && <RetouchPreviewModal group={activeGroup} close={() => setActiveGroup(null)} />}
      {showDownloadCenter && <DownloadCenterModal jobs={downloadJobs.data || []} loading={downloadJobs.loading} reload={downloadJobs.reload} close={() => setShowDownloadCenter(false)} toast={toast} />}
    </>
  );
}

function RetouchPreviewModal({ group, close }: { group: { task?: ShootingTask; uploads: ShootingUpload[] }; close: () => void }) {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const task = group.task;
  return (
    <div className="modal-backdrop">
      <div className="modal review-order-modal">
        <div className="modal-head">
          <div>
            <h2>修图预览</h2>
            <p>{task?.child_name || "未填孩子"} · {task?.class_name || "未填班级"} · {task?.brand || "-"} / {task?.campus || "-"}</p>
          </div>
          <button className="icon-btn" onClick={close}><X size={18} /></button>
        </div>
        <div className="review-photo-grid">
          {group.uploads.map((item, index) => <article key={item.id}>
            <CachedPreviewImage upload={item} onClick={() => setPreviewIndex(index)} />
            <div><Badge text="已通过" /><span>{item.upload_note || item.file_name || "无备注"}</span></div>
          </article>)}
        </div>
      </div>
      {previewIndex !== null && <EnhancedImagePreview uploads={group.uploads} index={previewIndex} setIndex={setPreviewIndex} close={() => setPreviewIndex(null)} />}
    </div>
  );
}

function ReviewOrderModal({ group, close, approve, reject, toast, reload }: { group: { task?: ShootingTask; uploads: ShootingUpload[] }; close: () => void; approve: () => void | Promise<void>; reject: () => void | Promise<void>; toast: (s: string) => void; reload: () => Promise<void> }) {
  const task = group.task;
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [localUploads, setLocalUploads] = useState<ShootingUpload[]>(group.uploads);
  useEffect(() => {
    setLocalUploads(group.uploads);
    setSelected({});
  }, [group.uploads]);
  const updateUploads = (rows: ShootingUpload[]) => {
    const map = new Map(rows.map(row => [String(row.id), row]));
    setLocalUploads(list => list.map(item => map.get(String(item.id)) || item));
    setSelected(v => {
      const next = { ...v };
      rows.forEach(row => { delete next[String(row.id)]; });
      return next;
    });
  };
  const selectedUploads = localUploads.filter(item => selected[String(item.id)]);
  const pendingUploads = localUploads.filter(item => item.review_status !== "approved");
  const pendingCount = localUploads.filter(item => !["approved", "rejected"].includes(String(item.review_status || ""))).length;
  const approvedCount = localUploads.filter(item => item.review_status === "approved").length;
  const rejectedCount = localUploads.filter(item => item.review_status === "rejected").length;
  const toggleAll = (checked: boolean) => setSelected(Object.fromEntries(localUploads.map(item => [String(item.id), checked])));
  const run = async (action: () => void | Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };
  const rejectItems = async (rows: ShootingUpload[]) => {
    if (!rows.length) {
      toast("请先选择要驳回的照片");
      return;
    }
    const note = prompt("请输入这几张照片的驳回/补拍原因");
    if (!note) return;
    const saved: ShootingUpload[] = [];
    for (const item of rows) saved.push(await api.rejectUpload(item.id, note));
    updateUploads(saved);
    toast(`已驳回 ${rows.length} 张，摄影师可查看原因并补拍`);
    await reload();
  };
  const approveItems = async (rows: ShootingUpload[]) => {
    if (!rows.length) {
      toast("请先选择要通过的照片");
      return;
    }
    const saved: ShootingUpload[] = [];
    for (const item of rows) saved.push(await api.approveUpload(item.id));
    updateUploads(saved);
    toast(`已通过 ${rows.length} 张`);
    await reload();
  };
  return (
    <div className="modal-backdrop">
      <div className="modal review-order-modal">
        <div className="modal-head">
          <div>
            <h2>订单审图</h2>
            <p>{task?.child_name || "未填孩子"} · {task?.class_name || "未填班级"} · {task?.brand || "-"} / {task?.campus || "-"}</p>
          </div>
          <button className="icon-btn" onClick={close}><X size={18} /></button>
        </div>
        <div className="review-order-info">
          <div><span>款式</span><b>{task?.package_name || "-"}</b></div>
          <div><span>最后拍摄日</span><b>{task?.end_date || "-"}</b></div>
          <div><span>摄影师</span><b>{group.uploads[0]?.photographer_name || task?.photographer_name || "-"}</b></div>
          <div><span>任务号</span><b>{task?.task_no || "-"}</b></div>
          <p>订单备注：{task?.special_note || "无"}</p>
        </div>
        <div className="review-select-bar">
          <label><input type="checkbox" checked={localUploads.length > 0 && localUploads.every(item => selected[String(item.id)])} onChange={e => toggleAll(e.target.checked)} /> 全选</label>
          <span>已选 {selectedUploads.length} 张 / 共 {localUploads.length} 张</span>
          <Badge text={`待审 ${pendingCount}`} />
          <Badge text={`通过 ${approvedCount}`} />
          <Badge text={`驳回 ${rejectedCount}`} />
          <button className="secondary" onClick={() => approveItems(selectedUploads)}>通过选中</button>
          <button className="danger" onClick={() => rejectItems(selectedUploads)}>驳回选中补拍</button>
        </div>
        <div className="review-photo-grid">
          {localUploads.map((item, index) => <article key={item.id}>
            <label className="review-photo-check"><input type="checkbox" checked={Boolean(selected[String(item.id)])} onChange={e => setSelected(v => ({ ...v, [String(item.id)]: e.target.checked }))} />选择</label>
            <CachedPreviewImage upload={item} onClick={() => setPreviewIndex(index)} />
            <div><Badge text={reviewStatus[item.review_status || ""] || item.review_status || "待审"} /><span>{item.upload_note || "无上传备注"}</span></div>
            {item.review_status === "rejected" && <p className="review-reject-note">驳回原因：{item.review_comment || "审图员未填写原因"}</p>}
            <div className="review-photo-actions">
              <button className="secondary" disabled={item.review_status === "approved"} onClick={() => approveItems([item])}>{item.review_status === "approved" ? "已通过" : "单张通过"}</button>
              <button className="danger" disabled={item.review_status === "rejected"} onClick={() => rejectItems([item])}>{item.review_status === "rejected" ? "已驳回" : "单张驳回"}</button>
            </div>
          </article>)}
        </div>
        <div className="modal-actions sticky-actions">
          <button className="secondary" disabled={busy} onClick={close}>关闭</button>
          <button className="danger" disabled={busy} onClick={() => run(reject)}>整单驳回补拍</button>
          <button className="primary" disabled={busy || !pendingUploads.length} onClick={() => run(approve)}>{busy ? "处理中..." : `整单通过${pendingUploads.length ? `(${pendingUploads.length})` : ""}`}</button>
        </div>
      </div>
      {previewIndex !== null && <EnhancedImagePreview uploads={localUploads} index={previewIndex} setIndex={setPreviewIndex} close={() => setPreviewIndex(null)} />}
    </div>
  );
}

function EnhancedImagePreview({ uploads, index, setIndex, close }: { uploads: ShootingUpload[]; index: number; setIndex: (index: number | null) => void; close: () => void }) {
  const [rotation, setRotation] = useState(0);
  const current = uploads[index];
  const canPrev = index > 0;
  const canNext = index < uploads.length - 1;
  const goPrev = () => {
    if (!canPrev) return;
    setRotation(0);
    setIndex(index - 1);
  };
  const goNext = () => {
    if (!canNext) return;
    setRotation(0);
    setIndex(index + 1);
  };
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrev();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
      }
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [index, canPrev, canNext]);
  if (!current) return null;
  return (
    <div className="image-preview-backdrop enhanced-preview" onClick={close}>
      <div className="preview-toolbar" onClick={e => e.stopPropagation()}>
        <button className="secondary" disabled={!canPrev} onClick={goPrev}>上一张</button>
        <span>{index + 1} / {uploads.length}</span>
        <button className="secondary" disabled={!canNext} onClick={goNext}>下一张</button>
        <button className="secondary" onClick={() => setRotation(value => (value - 90 + 360) % 360)}><RotateCcw size={16} />左旋转</button>
        <button className="secondary" onClick={() => setRotation(value => (value + 90) % 360)}><RotateCw size={16} />右旋转</button>
        <button className="icon-btn" onClick={close}><X size={18} /></button>
      </div>
      <button className="preview-nav preview-prev" disabled={!canPrev} onClick={e => { e.stopPropagation(); goPrev(); }}>‹</button>
      <div className="preview-image-stage" onClick={e => e.stopPropagation()}>
        <CachedPreviewImage upload={current} style={{ transform: `rotate(${rotation}deg)` }} />
      </div>
      <button className="preview-nav preview-next" disabled={!canNext} onClick={e => { e.stopPropagation(); goNext(); }}>›</button>
      <div className="preview-caption" onClick={e => e.stopPropagation()}>
        <b>{current.file_name || `照片 ${current.id}`}</b>
        <span>{reviewStatus[current.review_status || ""] || current.review_status || "待审"}</span>
      </div>
    </div>
  );
}

function SalesTestPage() {
  const exams = useAsync(api.exams, []);
  const attempts = useAsync(api.examAttempts, []);
  const [active, setActive] = useState<Exam | null>(null);
  const [attempt, setAttempt] = useState<any | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [result, setResult] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const optionEntries = (q: any) => {
    const raw = q.options_json || q.options || [];
    const textOf = (value: any) => {
      if (value == null) return "";
      if (typeof value === "string" || typeof value === "number") return String(value);
      if (typeof value === "object") return String(value.text ?? value.label ?? value.content ?? value.value ?? value.name ?? JSON.stringify(value));
      return String(value);
    };
    if (Array.isArray(raw)) return raw.map((x: any, i: number) => [String(x?.key || x?.code || String.fromCharCode(65 + i)), textOf(x)]);
    return Object.entries(raw).map(([k, v]) => [k, textOf(v)]);
  };
  const start = async (exam: Exam) => {
    setBusy(true);
    try {
      const detail = await api.examDetail(exam.id);
      const started = await api.startExam(exam.id);
      setActive(detail);
      setAttempt(started);
      setAnswers({});
      setResult(null);
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    } finally {
      setBusy(false);
    }
  };
  const setAnswer = (qid: string | number, value: any) => setAnswers(v => ({ ...v, [String(qid)]: value }));
  const qKind = (type?: string) => {
    const t = String(type || "").toLowerCase();
    if (t.includes("judge") || t.includes("判断")) return "judge";
    if (t.includes("multi") || t.includes("多")) return "multiple";
    if (t.includes("single") || t.includes("choice") || t.includes("单")) return "single";
    return "subjective";
  };
  const toggleMulti = (qid: string | number, key: string) => {
    const id = String(qid);
    const arr = Array.isArray(answers[id]) ? answers[id] : [];
    setAnswer(id, arr.includes(key) ? arr.filter((x: string) => x !== key) : [...arr, key]);
  };
  const submit = async () => {
    if (!attempt?.id) return;
    setBusy(true);
    try {
      const payload = (active?.questions || []).map(q => ({ question_id: Number(q.id), answer: answers[String(q.id)] ?? "" }));
      const res = await api.submitExam(attempt.id, payload);
      setResult(res);
      await attempts.reload();
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    } finally {
      setBusy(false);
    }
  };
  const questionCount = active?.questions?.length || 0;
  const answeredCount = active ? (active.questions || []).filter(q => {
    const value = answers[String(q.id)];
    return Array.isArray(value) ? value.length > 0 : Boolean(String(value || "").trim());
  }).length : 0;
  if (active) {
    return (
      <>
        <PageTitle title={active.title} desc={`${active.level || ""} · 满分 ${active.total_score || 100} · 已答 ${answeredCount}/${questionCount}`} action={<button className="secondary" onClick={() => setActive(null)}>返回试卷</button>} />
        <div className="exam-layout">
          <aside className="exam-side">
            <div className="exam-score-card">
              <span>答题进度</span>
              <b>{answeredCount}/{questionCount}</b>
              <div className="progress"><i style={{ width: `${questionCount ? (answeredCount / questionCount) * 100 : 0}%` }} /></div>
            </div>
            <div className="question-nav">
              {(active.questions || []).map((q, i) => {
                const value = answers[String(q.id)];
                const done = Array.isArray(value) ? value.length > 0 : Boolean(String(value || "").trim());
                return <a key={q.id} href={`#q-${q.id}`} className={done ? "done" : ""}>{i + 1}</a>;
              })}
            </div>
            <p className="exam-hint">单选、判断、多选自动评分；问答题由 AI 按标准评分。</p>
          </aside>
          <div className="exam-paper">
            {(active.questions || []).map((q: any, index) => {
              const type = qKind(q.question_type || q.type);
              const opts = type === "judge" ? [["对", "对"], ["错", "错"]] : optionEntries(q);
              const typeName = type === "single" ? "单选题" : type === "multiple" ? "多选题" : type === "judge" ? "判断题" : "问答题";
              return (
                <div className="question-card" id={`q-${q.id}`} key={q.id}>
                  <div className="question-head">
                    <h3>{index + 1}. {q.title || q.content}</h3>
                    <span>{typeName} · {q.score || 0} 分</span>
                  </div>
                  {q.content && q.title !== q.content && <p>{q.content}</p>}
                  {(type === "single" || type === "judge") && opts.map(([k, v]) => <label className="option-line" key={String(k)}><input type="radio" name={`q-${q.id}`} checked={answers[String(q.id)] === k} onChange={() => setAnswer(q.id, k)} />{type === "judge" ? "" : `${k}. `}{String(v)}</label>)}
                  {type === "multiple" && opts.map(([k, v]) => <label className="option-line" key={String(k)}><input type="checkbox" checked={(answers[String(q.id)] || []).includes(k)} onChange={() => toggleMulti(q.id, String(k))} />{k}. {String(v)}</label>)}
                  {type === "subjective" && <textarea rows={5} value={answers[String(q.id)] || ""} onChange={e => setAnswer(q.id, e.target.value)} placeholder="请输入你的回答，建议写出判断、原因和更好的销售话术" />}
                </div>
              );
            })}
          </div>
        </div>
        {result && <div className="exam-result-card"><b>提交成功</b><span>总分 {result.total_score ?? "-"} · 客观题 {result.objective_score ?? "-"} · 主观题 {result.subjective_score ?? "-"}</span><p>{result.ai_summary || "已完成评分。"}</p></div>}
        <div className="footer-actions sticky-actions"><button className="primary" disabled={busy} onClick={submit}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}提交试卷</button></div>
      </>
    );
  }
  return (
    <>
      <PageTitle eyebrow="SALES EXAM" title="销售测试" desc="三套试卷按新人、上岗、高级实战分级，客观题自动评分，主观题 AI 按标准评分。" action={<button className="secondary" onClick={() => { exams.reload(); attempts.reload(); }}><RefreshCw size={16} />刷新</button>} />
      <div className="exam-card-grid">
        {exams.loading ? <Loading /> : exams.data?.map((e, i) => <button className={`exam-select-card level-${String(e.level || i).toLowerCase()}`} key={e.id} onClick={() => start(e)} disabled={busy}>
          <div><span>{e.level || ["A", "B", "C"][i] || "EXAM"}</span><small>{e.questions?.length || 0} 题 · 满分 {e.total_score || 100}</small></div>
          <h3>{e.title}</h3>
          <p>{e.description}</p>
          <b>开始考试 <ChevronRight size={18} /></b>
        </button>)}
      </div>
      <div className="table-card exam-history"><h3>我的考试记录</h3><table><thead><tr><th>试卷</th><th>状态</th><th>总分</th><th>客观题</th><th>主观题</th><th>时间</th></tr></thead><tbody>{(attempts.data || []).slice(0, 8).map((a: any) => <tr key={a.id}><td>{a.exam?.title || a.exam_name || a.exam_id}</td><td><Badge text={a.status} /></td><td><b>{a.total_score ?? "-"}</b></td><td>{a.objective_score ?? "-"}</td><td>{a.subjective_score ?? "-"}</td><td>{cnDate(a.created_at)}</td></tr>)}</tbody></table></div>
    </>
  );
}

function ExamAdminPage() {
  const [userId, setUserId] = useState("");
  const [status, setStatus] = useState("");
  const [detail, setDetail] = useState<any | null>(null);
  const users = useAsync(() => api.users({ status: "approved" }), []);
  const attempts = useAsync(() => api.adminExamAttempts({ user_id: userId }), [userId]);
  const userNameMap = useMemo(() => new Map((users.data || []).map(u => [String(u.id), u.name || u.phone])), [users.data]);
  const rows = (attempts.data || []).filter((item: any) => !status || item.status === status);
  const scoreRows = rows.filter((item: any) => item.total_score !== null && item.total_score !== undefined && item.total_score !== "");
  const avg = scoreRows.length ? Math.round(scoreRows.reduce((sum: number, item: any) => sum + Number(item.total_score || 0), 0) / scoreRows.length) : "-";
  const statusText = (value?: string) => {
    const map: Record<string, string> = { in_progress: "进行中", submitted: "待评分", grading: "评分中", graded: "已评分", failed: "评分失败" };
    return map[value || ""] || value || "-";
  };
  const passText = (value?: string | null) => {
    const map: Record<string, string> = { passed: "通过", failed: "未通过", pending: "待定" };
    return map[value || ""] || value || "-";
  };
  const examTitle = (item: any) => item.exam?.title || item.exam_name || item.exam_title || `试卷 ${item.exam_id || "-"}`;
  const answerText = (value: any): string => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (Array.isArray(value)) return value.map(answerText).join("、");
    if (typeof value === "object") return value.text || value.content || value.answer || JSON.stringify(value);
    return String(value);
  };
  const answersOf = (item: any) => {
    if (Array.isArray(item.answers)) return item.answers;
    if (Array.isArray(item.answer_json)) return item.answer_json;
    try {
      return typeof item.answer_json === "string" ? JSON.parse(item.answer_json) : [];
    } catch {
      return [];
    }
  };
  return (
    <>
      <PageTitle title="考试成绩" desc="按销售下拉筛选考试成绩，查看得分、AI 评分意见和答题详情。" action={<button className="secondary" onClick={attempts.reload}><RefreshCw size={16} />刷新</button>} />
      <div className="stat-grid five compact-stats">
        <StatCard label="考试记录" value={rows.length} />
        <StatCard label="已评分" value={rows.filter((item: any) => item.status === "graded").length} />
        <StatCard label="进行中" value={rows.filter((item: any) => item.status === "in_progress").length} />
        <StatCard label="平均分" value={avg} />
        <StatCard label="通过人数" value={rows.filter((item: any) => item.pass_status === "passed").length} />
      </div>
      <div className="toolbar exam-toolbar">
        <select value={userId} onChange={e => setUserId(e.target.value)}>
          <option value="">全部销售</option>
          {users.data?.map(u => <option key={u.id} value={u.id}>{u.name} · {cnRole(u.role)}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">全部状态</option>
          <option value="in_progress">进行中</option>
          <option value="submitted">待评分</option>
          <option value="graded">已评分</option>
          <option value="failed">评分失败</option>
        </select>
      </div>
      <div className="table-card exam-admin-table">
        {attempts.loading ? <Loading /> : rows.length ? (
          <table>
            <thead><tr><th>销售</th><th>试卷</th><th>状态</th><th>总分</th><th>客观题</th><th>主观题</th><th>结果</th><th>提交时间</th><th>操作</th></tr></thead>
            <tbody>{rows.map((item: any) => (
              <tr key={item.id}>
                <td>{item.user_name || userNameMap.get(String(item.user_id)) || `用户 ${item.user_id || "-"}`}</td>
                <td><b>{examTitle(item)}</b><small>{item.exam?.level || item.level || ""}</small></td>
                <td><Badge text={statusText(item.status)} /></td>
                <td><b>{item.total_score ?? "-"}</b>{item.exam?.total_score ? <small> / {item.exam.total_score}</small> : null}</td>
                <td>{item.objective_score ?? "-"}</td>
                <td>{item.subjective_score ?? "-"}</td>
                <td>{passText(item.pass_status)}</td>
                <td>{cnDate(item.submitted_at || item.created_at)}</td>
                <td><button className="small-btn" onClick={() => setDetail(item)}>查看详情</button></td>
              </tr>
            ))}</tbody>
          </table>
        ) : <Empty text="暂无考试成绩" />}
      </div>
      {detail && (
        <div className="modal-backdrop" onClick={() => setDetail(null)}>
          <div className="modal exam-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>{examTitle(detail)}</h2>
                <p>{detail.user_name || userNameMap.get(String(detail.user_id)) || `用户 ${detail.user_id || "-"}`} · {statusText(detail.status)} · {cnDate(detail.submitted_at || detail.created_at)}</p>
              </div>
              <button className="icon-btn" onClick={() => setDetail(null)}><X size={20} /></button>
            </div>
            <div className="exam-score-grid">
              <StatCard label="总分" value={detail.total_score ?? "-"} />
              <StatCard label="客观题" value={detail.objective_score ?? "-"} />
              <StatCard label="主观题" value={detail.subjective_score ?? "-"} />
              <StatCard label="结果" value={passText(detail.pass_status)} />
            </div>
            <div className="exam-comment-grid">
              <section><h3>AI 总评</h3><p>{detail.ai_summary || detail.ai_comment || "暂无总评"}</p></section>
              <section><h3>优势</h3><p>{answerText(detail.ai_strengths || detail.strengths)}</p></section>
              <section><h3>短板</h3><p>{answerText(detail.ai_weaknesses || detail.weaknesses)}</p></section>
              <section><h3>建议</h3><p>{answerText(detail.ai_suggestions || detail.recommended_training || detail.suggestions)}</p></section>
            </div>
            <div className="table-card answer-table">
              <table>
                <thead><tr><th>题目</th><th>答案</th><th>得分</th><th>AI 说明</th></tr></thead>
                <tbody>{answersOf(detail).map((answer: any, index: number) => (
                  <tr key={index}>
                    <td>{answer.question_title || answer.title || answer.question?.title || answer.question?.content || `第 ${index + 1} 题`}</td>
                    <td>{answerText(answer.answer ?? answer.user_answer ?? answer.value)}</td>
                    <td>{answer.score ?? answer.ai_score ?? "-"}</td>
                    <td>{answer.ai_comment || answer.comment || answer.reason || "-"}</td>
                  </tr>
                ))}</tbody>
              </table>
              {!answersOf(detail).length && <Empty text="暂无答题明细" />}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const auditModules = ["系统", "报单", "拍摄", "审图", "训练", "考试"];

function auditDetailText(detail?: Record<string, unknown> | null) {
  if (!detail) return "-";
  const changed = detail.changed_fields;
  if (changed && typeof changed === "object" && !Array.isArray(changed)) {
    const rows = Object.entries(changed as Record<string, { before?: unknown; after?: unknown }>).slice(0, 8);
    if (!rows.length) return "-";
    return rows.map(([field, value]) => `${field}: ${auditValue(value?.before)} -> ${auditValue(value?.after)}`).join("\n");
  }
  const parts = Object.entries(detail).filter(([, value]) => value !== null && value !== undefined && value !== "");
  return parts.length ? parts.map(([key, value]) => `${key}: ${auditValue(value)}`).join("\n") : "-";
}

function AuditLogsPage() {
  const [filters, setFilters] = useState({ module: "", keyword: "", start_at: "", end_at: "", limit: "200" });
  const [active, setActive] = useState<AuditLog | null>(null);
  const state = useAsync(() => api.auditLogs(filters), [JSON.stringify(filters)]);
  const rows = state.data || [];
  const counts = auditModules.reduce<Record<string, number>>((acc, name) => {
    acc[name] = rows.filter(item => item.module === name).length;
    return acc;
  }, {});
  const set = (key: keyof typeof filters, value: string) => setFilters(v => ({ ...v, [key]: value }));
  return (
    <>
      <PageTitle
        eyebrow="AUDIT LOGS"
        title="操作日志"
        desc="管理员查看系统、报单、拍摄、上传和审图的关键操作记录。"
        action={<button className="secondary" onClick={state.reload}><RefreshCw size={16} />刷新</button>}
      />
      <div className="stat-grid audit-stats">
        <StatCard label="全部记录" value={rows.length} active={!filters.module} onClick={() => set("module", "")} />
        {auditModules.map(name => <StatCard key={name} label={name} value={counts[name] || 0} active={filters.module === name} onClick={() => set("module", name)} />)}
      </div>
      <div className="toolbar dense audit-toolbar">
        <select value={filters.module} onChange={e => set("module", e.target.value)}>
          <option value="">全部类型</option>
          {auditModules.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
        <label><span>开始</span><input type="date" value={filters.start_at} onChange={e => set("start_at", e.target.value)} /></label>
        <label><span>结束</span><input type="date" value={filters.end_at} onChange={e => set("end_at", e.target.value)} /></label>
        <select value={filters.limit} onChange={e => set("limit", e.target.value)}>
          <option value="100">最近 100 条</option>
          <option value="200">最近 200 条</option>
          <option value="500">最近 500 条</option>
        </select>
        <div className="search audit-search"><Search size={16} /><input value={filters.keyword} onChange={e => set("keyword", e.target.value)} placeholder="搜索操作人、孩子、订单号、动作、备注" /></div>
        <button className="secondary" onClick={() => setFilters({ module: "", keyword: "", start_at: "", end_at: "", limit: "200" })}>重置</button>
      </div>
      <div className="audit-layout">
        <div className="audit-timeline">
          {state.loading ? <Loading /> : rows.length ? rows.map(item => (
            <button className={`audit-row ${active?.id === item.id ? "active" : ""}`} key={item.id} onClick={() => setActive(item)}>
              <span className={`audit-dot module-${item.module}`} />
              <div className="audit-main">
                <div className="audit-row-head">
                  <Badge text={item.module} />
                  <b>{item.title}</b>
                  <time>{cnDate(item.created_at)}</time>
                </div>
                <p>{item.target_label || item.target_type || "-"}</p>
                <small>{item.operator_name || "系统"}{item.operator_role ? ` / ${cnRole(item.operator_role)}` : ""}{item.note ? ` · ${item.note}` : ""}</small>
              </div>
            </button>
          )) : <Empty text="暂无操作日志" />}
        </div>
        <aside className="audit-detail">
          {active ? (
            <>
              <div className="audit-detail-head">
                <Badge text={active.module} />
                <h3>{active.title}</h3>
                <p>{cnDate(active.created_at)}</p>
              </div>
              <div className="detail-kv-grid audit-kv">
                <div><span>操作人</span><b>{active.operator_name || "系统"}</b></div>
                <div><span>角色</span><b>{cnRole(active.operator_role || "")}</b></div>
                <div><span>对象</span><b>{active.target_label || "-"}</b></div>
                <div><span>来源</span><b>{active.source}</b></div>
              </div>
              {active.note && <section className="audit-note"><span>备注</span><p>{active.note}</p></section>}
              <section className="audit-note">
                <span>明细</span>
                <pre>{auditDetailText(active.detail)}</pre>
              </section>
              {active.ip_address && <section className="audit-note"><span>IP</span><p>{active.ip_address}</p></section>}
            </>
          ) : (
            <Empty text="请选择左侧一条日志查看详情" />
          )}
        </aside>
      </div>
    </>
  );
}

function SettingsPage({ user }: { user: ApiUser }) {
  const [editing, setEditing] = useState<Partial<AISetting> | null>(null);
  const [cacheStats, setCacheStats] = useState<ImageCacheStats | null>(null);
  const isAdmin = user.role === "admin";
  const settings = useAsync(() => user.role === "admin" ? api.aiSettings() : Promise.resolve([]), [user.role]);
  const configuredModels = settings.data || [];
  const hasAiService = configuredModels.some(item => item.status === "enabled" && (item.api_key_masked || item.model_name));
  const hasStorageService = true;
  useEffect(() => {
    void imageCacheStats().then(setCacheStats);
  }, []);
  const clearLocalCache = async () => {
    localStorage.removeItem("sales_training_app_logs");
    const stats = await clearImageCache();
    setCacheStats(stats);
    alert("缓存已清理");
  };
  const test = async (item: AISetting) => {
    try {
      const res = await api.testAISetting(item.id);
      const data = res?.data || res;
      alert(data?.connected === false ? "连接失败，请检查配置" : "连接正常");
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    }
  };
  const toggle = async (item: AISetting) => {
    try {
      if (item.status === "enabled") await api.disableAISetting(item.id);
      else await api.enableAISetting(item.id);
      await settings.reload();
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    }
  };
  const remove = async (item: AISetting) => {
    if (!window.confirm(`确认删除模型配置「${item.provider} / ${item.model_name}」？`)) return;
    try {
      await api.deleteAISetting(item.id);
      await settings.reload();
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    }
  };
  return (
    <>
      <PageTitle title="设置" desc={isAdmin ? "账号信息、安全状态和服务配置。" : "账号信息、安全设置和本地缓存。"} />
      <div className="panel settings-panel">
        <p><b>当前账号：</b>{user.name || user.phone} / {cnRole(user.role)}</p>
        <p><b>系统状态：</b>正常</p>
        <p><b>版本信息：</b>影战邦效率系统</p>
        <p><b>账号安全：</b>如需修改密码，请联系管理员处理。</p>
        <p><b>本地缓存：</b>{imageCacheSummary(cacheStats)} <button className="secondary small-btn" onClick={clearLocalCache}>清除缓存</button></p>
        {!isAdmin && <p><b>安全说明：</b>普通账号不会显示接口、权限和底层配置。</p>}
        {isAdmin && (
          <>
            <p><b>接口状态：</b>已连接</p>
            <p><b>存储服务：</b>{hasStorageService ? "已配置" : "未配置"}</p>
            <p><b>AI 服务：</b>{hasAiService ? "已配置" : "未配置"}</p>
            <p><b>安全说明：</b>密钥和数据库信息不会在前端明文展示。</p>
          </>
        )}
      </div>
      {isAdmin && (
        <div className="panel ai-settings-panel">
          <div className="panel-head">
            <div>
              <h3>大模型设置</h3>
              <p>API Key 仅加密保存在服务器，客户端不会保存或展示完整密钥。</p>
            </div>
            <button className="primary" onClick={() => setEditing({ provider: "deepseek", api_base_url: "", model_name: "deepseek-chat", temperature: 0.7, status: "enabled" })}><Plus size={16} />新增模型</button>
          </div>
          <div className="table-card model-table">
            {settings.loading ? <Loading /> : (settings.data || []).length ? <table>
              <thead><tr><th>服务商</th><th>接口状态</th><th>模型</th><th>温度</th><th>Key</th><th>状态</th><th>操作</th></tr></thead>
              <tbody>{(settings.data || []).map(item => <tr key={item.id}>
                <td>{item.provider}</td>
                <td>{item.api_base_url ? "已配置" : "未配置"}</td>
                <td>{item.model_name}</td>
                <td>{item.temperature}</td>
                <td>{item.api_key_masked || "未配置"}</td>
                <td><Badge text={item.status === "enabled" ? "启用中" : "已停用"} /></td>
                <td className="row-actions">
                  <button onClick={() => setEditing(item)}><Edit3 size={15} />编辑</button>
                  <button onClick={() => toggle(item)}>{item.status === "enabled" ? "停用" : "启用"}</button>
                  <button onClick={() => test(item)}>测试</button>
                  <button className="danger" onClick={() => remove(item)}><Trash2 size={15} /></button>
                </td>
              </tr>)}</tbody>
            </table> : <Empty text="暂无大模型配置，请新增一个模型。" />}
          </div>
        </div>
      )}
      {editing && <AISettingModal item={editing} close={() => setEditing(null)} done={async () => { setEditing(null); await settings.reload(); }} />}
    </>
  );
}

function AISettingModal({ item, close, done }: { item: Partial<AISetting>; close: () => void; done: () => void }) {
  const isNew = !item.id;
  const [form, setForm] = useState<Partial<AISetting>>({ provider: "", api_base_url: "", api_key: "", model_name: "", temperature: 0.7, status: "enabled", ...item });
  const [busy, setBusy] = useState(false);
  const set = (key: keyof AISetting, value: any) => setForm(v => ({ ...v, [key]: value }));
  const save = async () => {
    if (!form.provider || !form.api_base_url || !form.model_name) return alert("请完善模型服务配置");
    if (isNew && !form.api_key) return alert("新增模型必须填写 API Key");
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.api_key) delete payload.api_key;
      if (isNew) await api.createAISetting(payload);
      else await api.updateAISetting(item.id!, payload);
      done();
    } catch (e) {
      alert((e as Error).message || "\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5");
    } finally {
      setBusy(false);
    }
  };
  return <div className="modal-backdrop">
    <div className="modal compact-modal">
      <div className="modal-head"><h2>{isNew ? "新增大模型配置" : "编辑大模型配置"}</h2><button onClick={close}><X /></button></div>
      <div className="form-panel no-border one-col">
        <Field label="服务商 provider" value={form.provider} onChange={v => set("provider", v)} />
        <Field label="模型接口地址（仅管理员可配置）" value={form.api_base_url} onChange={v => set("api_base_url", v)} />
        <Field label="模型名称 model" value={form.model_name} onChange={v => set("model_name", v)} />
        <Field label="temperature" value={form.temperature} onChange={v => set("temperature", Number(v || 0.7))} />
        <label><span>状态</span><select value={form.status || "enabled"} onChange={e => set("status", e.target.value)}><option value="enabled">启用</option><option value="disabled">停用</option></select></label>
        <Field label={isNew ? "API Key" : `API Key（当前：${form.api_key_masked || "已脱敏"}，不改就留空）`} value={form.api_key || ""} onChange={v => set("api_key", v)} />
        <div className="hint">密钥提交到后端后会加密保存；普通用户和客户端都看不到完整 Key。</div>
      </div>
      <div className="modal-actions"><button className="secondary" onClick={close}>取消</button><button className="primary" disabled={busy} onClick={save}>{busy ? <Loader2 className="spin" /> : <Check size={16} />}保存配置</button></div>
    </div>
  </div>;
}

function Badge({ text }: { text: string }) {
  return <span className="badge">{text}</span>;
}

export default App;
